
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  ClipboardCheck,
  User as UserIcon,
  Target,
  TrendingUp, 
  DollarSign, 
  FileText, 
  ChevronRight, 
  ChevronLeft,
  Trash2,
  CheckCircle2,
  Building2,
  ExternalLink,
  ShieldCheck,
  Settings2,
  Stethoscope,
  Printer,
  Download,
  UploadCloud,
  Send,
  FileCheck,
  Loader2,
  MailCheck,
  AlertCircle,
  FileBadge,
  BookOpen,
  Lock,
  UserPlus,
  LogOut,
  Users,
  X,
  Database,
  Terminal,
  BarChart3,
  Info,
  ArrowUp,
  AlertTriangle,
  Crown,
  ChevronDown,
  Key,
  HelpCircle,
  Search,
  Eye,
  EyeOff,
  Landmark,
  Paperclip,
  Calendar,
  Clock,
  Filter,
  Activity,
  Award,
  Zap,
  RefreshCw,
  TrendingDown,
  MapPin,
  Hash,
  FileBarChart2,
  PieChart,
  Layers,
  CheckSquare,
  XCircle,
  Bell,
  Globe,
  Pencil,
  Ban,
  Menu
} from 'lucide-react';
import { FormState, User, AcaoServico, NaturezaDespesa } from './types';

import {
  DIRETRIZES,
  PROGRAMAS,
  ACOES_SERVICOS_POR_PROGRAMA,
  PROGRAMA_EMENDA_COLETIVA_10352_CUSTEIO,
  GRUPOS_ACAO_EMENDA_COLETIVA_10352_CUSTEIO,
  METAS_QUALITATIVAS_OPTIONS,
  METAS_QUANTITATIVAS_OPTIONS,
  NATUREZAS_DESPESA,
  NATUREZAS_POR_PROGRAMA
} from './constants';
import { AdminTreeEditor } from './components/AdminTreeEditor';
import { CnesCadastroAdmin } from './components/CnesCadastroAdmin';
import { FaqPanel, FaqItem } from './components/FaqPanel';
import { FaqAdmin } from './components/FaqAdmin';
import { avisar, confirmar, pedirSenha, EscParaFechar } from './components/Feedback';
import { supabase } from './supabase';
import { InputField } from './components/InputField';
import { Section } from './components/Section';
import { StepperProgress } from './components/StepperProgress';
import { Button, Select, TextArea } from './components/FormElements';
import { 
  maskCPF, 
  validateCPF, 
  maskCNPJ, 
  validateCNPJ, 
  maskCurrency, 
  maskPhone,
  maskCNES,
  validateEmail,
  maskPercentage,
  formatPercentageDisplay
} from './utils/masks';
import { initializeSecurity } from './utils/security';
import { CRITERIOS_SAES, SAES_LABELS_SHORT, parseJustCriterios, serializeJustCriterios, justDisplayText } from './utils/justificativa';
import { MenuHamburguerUserActions } from './components/UserActionsMenu';
import { LoginScreen } from './components/LoginScreen';
import { PlanoDocumento } from './components/PlanoDocumento';
import { AppHeader } from './components/AppHeader';
import { helpContent } from './data/ajudaSecoes';
import { VisualizarPlanoModal } from './components/VisualizarPlanoModal';


type PlanilhaBaseRow = {
  deputado: string;
  senador: string;
  cnes: string;
  entidade: string;
  cnpj: string;
};

type EmendaDisponibilizadaForm = {
  cnes: string;
  entidade: string;
  cnpj: string;
  parlamentar: string;
  tipoParlamentar: 'deputado' | 'senador' | '';
  valor: string;
  programa: string;
  numeroEmenda: string;
};

// ─── ComboboxField: input livre + sugestões abrindo abaixo ─────────────────
interface ComboboxOption { label: string; value: string; }
interface ComboboxFieldProps {
  label: string;
  value: string;
  options: ComboboxOption[];
  placeholder?: string;
  onChange: (text: string) => void;
  onSelect: (value: string) => void;
}
const ComboboxField: React.FC<ComboboxFieldProps> = ({ label, value, options, placeholder, onChange, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setQuery(value); }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase())).slice(0, 300)
    : options.slice(0, 300);

  return (
    <div className="space-y-2" ref={containerRef}>
      <label className="text-sm font-bold text-gray-900">{label}</label>
      <div className="relative">
        <input
          type="text"
          className="w-full px-4 py-3 rounded-xl border border-blue-300 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
          placeholder={placeholder}
          value={query}
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {open && filtered.length > 0 && (
          <ul className="absolute z-[200] left-0 right-0 top-full mt-1 bg-white border border-blue-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
            {filtered.map((opt) => (
              <li
                key={opt.value}
                className="px-4 py-2.5 text-sm cursor-pointer hover:bg-blue-50 text-gray-800 border-b border-gray-100 last:border-0"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setQuery(opt.label);
                  onChange(opt.value);
                  onSelect(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const App: React.FC = () => {
  // Authentication & Session State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [loginInput, setLoginInput] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  
  // View Management
  const [currentView, setCurrentView] = useState<'new' | 'list' | 'dashboard'>('new'); // Toggle entre novo plano e listagem
  
  // Admin & User Management
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showAdminTreeEditor, setShowAdminTreeEditor] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDisponibilizarEmendaModal, setShowDisponibilizarEmendaModal] = useState(false);

  // Dynamic lists loaded from DB (fall back to constants when DB is empty)
  const [dynamicProgramas, setDynamicProgramas] = useState<string[]>(PROGRAMAS);
  const [dynamicAcoes, setDynamicAcoes] = useState<Record<string, AcaoServico[]>>(ACOES_SERVICOS_POR_PROGRAMA);
  const [dynamicMetasQuant, setDynamicMetasQuant] = useState<Record<string, string[]>>(METAS_QUANTITATIVAS_OPTIONS);
  const [dynamicNaturezas, setDynamicNaturezas] = useState<NaturezaDespesa[]>(NATUREZAS_DESPESA);
  // Per-program naturezas: keyed by program name. Empty = show all naturezas.
  const [dynamicProgramaNaturezas, setDynamicProgramaNaturezas] = useState<Record<string, NaturezaDespesa[]>>({});
  // Per-program metas quantitativas: keyed by program name. Empty = programa não tem config própria.
  const [dynamicProgramaMetas, setDynamicProgramaMetas] = useState<Record<string, { categoria: string; itens: string[] }[]>>({});
  // True assim que a tentativa de carregar a árvore dinâmica termina (com sucesso OU falha).
  const [treeDataLoaded, setTreeDataLoaded] = useState(false);
  // True se a tentativa terminou em falha definitiva (após as retentativas) — nesse caso
  // a whitelist estática por programa (NATUREZAS_POR_PROGRAMA) continua valendo como rede
  // de segurança, em vez de liberar todas as naturezas para todos os programas.
  const [treeDataError, setTreeDataError] = useState(false);
  const [treeDataErrorDetail, setTreeDataErrorDetail] = useState<string | null>(null);
  const [usersList, setUsersList] = useState<any[]>([]);
  const [inactiveUsersList, setInactiveUsersList] = useState<any[]>([]);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'user' as 'user' | 'admin' | 'intermediate', cnes: '' });
  // Controle do mínimo de caracteres da justificativa
  const [minJustificativa, setMinJustificativa] = useState<number>(() => {
    try { return Number(localStorage.getItem('min_justificativa') ?? 0); } catch { return 0; }
  });
  // Estado dos 5 critérios SAES da justificativa
  const [justCriterios, setJustCriterios] = useState<string[]>(['', '', '', '', '']);
  const justFromUserRef = useRef(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any>({ id: '', email: '', name: '', cnes: '', password: '' });
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResults, setCsvResults] = useState<any>(null);
  const [selectedCnes, setSelectedCnes] = useState<string>('');
  // Cadastro do CNES (tabela cnes_cadastro): site de transparência por CNES
  const [cnesCadastroCache, setCnesCadastroCache] = useState<Record<string, string>>({});
  const [showSiteTransparenciaModal, setShowSiteTransparenciaModal] = useState(false);
  const [siteTransparenciaObrigatorio, setSiteTransparenciaObrigatorio] = useState(false);
  const [siteTransparenciaInput, setSiteTransparenciaInput] = useState('');
  const [siteTransparenciaErro, setSiteTransparenciaErro] = useState('');
  const [isSavingSiteTransparencia, setIsSavingSiteTransparencia] = useState(false);
  const [showCnesCadastroAdmin, setShowCnesCadastroAdmin] = useState(false);
  // Central de Dúvidas (tabela faq_itens + app_settings.faq_painel_ativo)
  const [faqItens, setFaqItens] = useState<FaqItem[]>([]);
  const [faqPainelAtivo, setFaqPainelAtivo] = useState(false);
  const [showFaqPanel, setShowFaqPanel] = useState(false);
  const [faqBoasVindas, setFaqBoasVindas] = useState(false);
  const [showFaqAdmin, setShowFaqAdmin] = useState(false);
  const [sheetBaseRows, setSheetBaseRows] = useState<PlanilhaBaseRow[]>([]);
  const [selectedOfertaEmendaId, setSelectedOfertaEmendaId] = useState<string | null>(null);
  const [emendasDisponiveisUsuario, setEmendasDisponiveisUsuario] = useState<any[]>([]);
  const [emendasDisponibilizadasAdmin, setEmendasDisponibilizadasAdmin] = useState<any[]>([]);
  const [isSavingDisponibilizacao, setIsSavingDisponibilizacao] = useState(false);
  const [isLoadingEmendasDisponiveis, setIsLoadingEmendasDisponiveis] = useState(false);
  const [showEditEmendaModal, setShowEditEmendaModal] = useState(false);
  const [editingEmenda, setEditingEmenda] = useState<any>(null);
  const [editingEmendaForm, setEditingEmendaForm] = useState<EmendaDisponibilizadaForm>({
    cnes: '', entidade: '', cnpj: '', parlamentar: '', tipoParlamentar: '', valor: '', programa: '', numeroEmenda: ''
  });
  const [isSavingEditEmenda, setIsSavingEditEmenda] = useState(false);
  const [filtroStatusEmendas, setFiltroStatusEmendas] = useState<'todas' | 'disponibilizada' | 'utilizada' | 'cancelada'>('todas');
  const [emendaNotifCount, setEmendaNotifCount] = useState(0);
  const [newEmendaDisponibilizada, setNewEmendaDisponibilizada] = useState<EmendaDisponibilizadaForm>({
    cnes: '',
    entidade: '',
    cnpj: '',
    parlamentar: '',
    tipoParlamentar: '',
    valor: '',
    programa: '',
    numeroEmenda: ''
  });
  const [emendaBannerDismissed, setEmendaBannerDismissed] = useState(false);

  // Plan List & Edit Management
  const [planosList, setPlanosList] = useState<any[]>([]);
  const [isLoadingPlanos, setIsLoadingPlanos] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [selectedPlanos, setSelectedPlanos] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showSelectPlanModal, setShowSelectPlanModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Filtros de Planos
  const [filtros, setFiltros] = useState({
    cnes: '',
    parlamentar: '',
    emenda: '',
    cnpj: ''
  });

  // Visualizar Plano - Modal Read-Only
  const [showViewPlanModal, setShowViewPlanModal] = useState(false);
  const [viewingPlanData, setViewingPlanData] = useState<any>(null);
  const [isLoadingViewPlan, setIsLoadingViewPlan] = useState(false);

  // Dashboard Filtros Avançados
  const [dashboardFiltros, setDashboardFiltros] = useState({
    programa: '',
    cnes: '',
    periodo: ''
  });

  // Form Progress State
  const [activeSection, setActiveSection] = useState('info-emenda'); 
  const [showDocument, setShowDocument] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailsDestino, setEmailsDestino] = useState('');
  const [planoSalvoId, setPlanoSalvoId] = useState<string | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState<Date | null>(null);
  const [formHasChanges, setFormHasChanges] = useState(false);
  const [lastSavedFormData, setLastSavedFormData] = useState<FormState | null>(null);
  const [helpSectionId, setHelpSectionId] = useState<string | null>(null);
  
  // Section completion tracking
  const [sectionStatus, setSectionStatus] = useState<{[key: string]: boolean}>({
    'info-emenda': false,
    'beneficiario': false,
    'alinhamento': false,
    'metas-quantitativas': false,
    'metas-qualitativas': false,
    'execucao-financeira': false,
    'finalizacao': false
  });

  const [formData, setFormData] = useState<FormState>({
    emenda: { parlamentar: '', numero: '', valor: '', valorExtenso: '', programa: 'EMENDA INDIVIDUAL - CUSTEIO MAC - 2E90' },
    beneficiario: { nome: '', cnes: '', cnpj: '', email: '', telefone: '' },
    planejamento: { diretrizId: '', objetivoId: '', metaIds: [] },
    acoesServicos: [],
    metasQualitativas: [],
    naturezasDespesa: [],
    justificativa: '',
    responsavelAssinatura: ''
  });

  // Helper: parse multi-CNES string into array
  const parseCnesList = (cnes: string | undefined): string[] => {
    if (!cnes) return [];
    return cnes.split(',').map(c => c.trim()).filter(Boolean);
  };

  // Lista de CNES do usuário atual
  const userCnesList = useMemo(() => parseCnesList(currentUser?.cnes), [currentUser?.cnes]);

  const emendasDisponiveisFiltradas = useMemo(() => {
    if (!selectedCnes) return emendasDisponiveisUsuario;
    return emendasDisponiveisUsuario.filter((item: any) => String(item.cnes || '') === selectedCnes);
  }, [emendasDisponiveisUsuario, selectedCnes]);

  const entidadesPorCnes = useMemo(() => {
    const mapa: Record<string, string> = {};
    for (const row of sheetBaseRows) {
      if (row.entidade && !mapa[row.cnes]) mapa[row.cnes] = row.entidade;
    }
    return mapa;
  }, [sheetBaseRows]);

  const sheetRowsForSelectedCnes = useMemo(() => {
    if (!newEmendaDisponibilizada.cnes) return [];
    return sheetBaseRows.filter(row => row.cnes === newEmendaDisponibilizada.cnes);
  }, [sheetBaseRows, newEmendaDisponibilizada.cnes]);

  const parlamentaresOptionsForSelectedCnes = useMemo(() => {
    const options: Array<{ label: string; value: string; tipo: 'deputado' | 'senador' }> = [];
    const seen = new Set<string>();

    for (const row of sheetRowsForSelectedCnes) {
      if (row.deputado && !seen.has(`deputado:${row.deputado}`)) {
        options.push({ label: `Deputado: ${row.deputado}`, value: row.deputado, tipo: 'deputado' });
        seen.add(`deputado:${row.deputado}`);
      }
      if (row.senador && !seen.has(`senador:${row.senador}`)) {
        options.push({ label: `Senador: ${row.senador}`, value: row.senador, tipo: 'senador' });
        seen.add(`senador:${row.senador}`);
      }
    }

    return options;
  }, [sheetRowsForSelectedCnes]);

  const entidadesOptionsForSelectedCnes = useMemo(() => {
    const seen = new Set<string>();
    const options: Array<{ label: string; value: string }> = [];
    for (const row of sheetRowsForSelectedCnes) {
      const value = row.entidade || '';
      if (value && !seen.has(value)) {
        options.push({ label: value, value });
        seen.add(value);
      }
    }
    return options;
  }, [sheetRowsForSelectedCnes]);

  const cnpjsOptionsForSelectedCnes = useMemo(() => {
    const seen = new Set<string>();
    const options: Array<{ label: string; value: string }> = [];
    for (const row of sheetRowsForSelectedCnes) {
      const value = row.cnpj || '';
      if (value && !seen.has(value)) {
        options.push({ label: value, value });
        seen.add(value);
      }
    }
    return options;
  }, [sheetRowsForSelectedCnes]);

  const loadSheetBaseRows = async () => {
    try {
      const response = await fetch('/data/deputados_planoTrabalho.json', { cache: 'no-store' });
      if (!response.ok) throw new Error('Não foi possível carregar a base de Deputado/Senador por CNES.');
      const data = await response.json();
      if (Array.isArray(data)) {
        setSheetBaseRows(
          data
            .map((row: any) => ({
              deputado: String(row.deputado || '').trim(),
              senador: String(row.senador || '').trim(),
              cnes: String(row.cnes || '').trim(),
              entidade: String(row.entidade || '').trim(),
              cnpj: String(row.cnpj || '').trim()
            }))
            .filter((row: PlanilhaBaseRow) => row.cnes)
        );
      }
    } catch (error) {
      console.warn('⚠️ Falha ao carregar base da planilha local:', error);
      setSheetBaseRows([]);
    }
  };

  const loadEmendasDisponibilizadasAdmin = async () => {
    if (currentUser?.role !== 'admin') return;
    try {
      const { data, error } = await supabase
        .from('emendas_disponibilizadas')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setEmendasDisponibilizadasAdmin(data || []);
    } catch (error) {
      console.warn('⚠️ Não foi possível carregar emendas disponibilizadas (admin):', error);
      setEmendasDisponibilizadasAdmin([]);
    }
  };

  const loadEmendasDisponiveisUsuario = async () => {
    if (!currentUser || currentUser.role !== 'user') return;
    const cnesList = parseCnesList(currentUser.cnes);
    if (cnesList.length === 0) {
      setEmendasDisponiveisUsuario([]);
      return;
    }

    setIsLoadingEmendasDisponiveis(true);
    try {
      const { data, error } = await supabase
        .from('emendas_disponibilizadas')
        .select('*')
        .eq('status', 'disponibilizada')
        .in('cnes', cnesList)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEmendasDisponiveisUsuario(data || []);
    } catch (error) {
      console.warn('⚠️ Não foi possível carregar emendas disponíveis para o usuário:', error);
      setEmendasDisponiveisUsuario([]);
    } finally {
      setIsLoadingEmendasDisponiveis(false);
    }
  };

  const aplicarEmendaDisponibilizadaNoFormulario = (oferta: any) => {
    if (!oferta) return;

    const valorMasked = maskCurrency(String(Math.round(parseFloat(String(oferta.valor || 0)) * 100)));
    const cnes = String(oferta.cnes || '').trim();

    if (cnes) {
      setSelectedCnes(cnes);
    }

    setSelectedOfertaEmendaId(oferta.id);
    setEmendaBannerDismissed(true);

    updateFormData('emenda', {
      ...formData.emenda,
      parlamentar: String(oferta.parlamentar || ''),
      numero: String(oferta.numero_emenda || ''),
      valor: valorMasked,
      valorExtenso: '',
      programa: String(oferta.programa || '')
    });

    updateFormData('beneficiario', {
      ...formData.beneficiario,
      nome: String(oferta.entidade || ''),
      cnes,
      cnpj: String(oferta.cnpj || '')
    });
  };

  // Função para obter formData inicial
  const getInitialFormData = (): FormState => {
    // Auto-fill CNES for regular users (non-admin) - usa o CNES selecionado
    const userCnes = currentUser?.role === 'user' ? (selectedCnes || '') : '';
    
    console.log('📝 getInitialFormData() chamado:', {
      currentUser_role: currentUser?.role,
      currentUser_cnes: currentUser?.cnes,
      userCnes_resultado: userCnes,
      ifrUserStandard: currentUser?.role === 'user'
    });
    
    return {
      emenda: { parlamentar: '', numero: '', valor: '', valorExtenso: '', programa: '' },
      beneficiario: { nome: '', cnes: userCnes, cnpj: '', email: '', telefone: '', contaBancaria: '', extratoUrl: '', extratoFilename: '' },
      planejamento: { diretrizId: '', objetivoId: '', metaIds: [] },
      acoesServicos: [],
      metasQualitativas: [],
      naturezasDespesa: [],
      justificativa: '',
      responsavelAssinatura: ''
    };
  };

  const [currentSelection, setCurrentSelection] = useState<{
    categoria: string;
    item: string;
    metas: string[];
  }>({ categoria: '', item: '', metas: [''] });

  const [currentMetaQualitativa, setCurrentMetaQualitativa] = useState({ meta: '', valor: '' });

  const [currentNatureza, setCurrentNatureza] = useState({ codigo: '', valor: '' });

  // ======== UPLOAD EXTRATO BANCÁRIO ========
  const [isUploadingExtrato, setIsUploadingExtrato] = useState(false);
  const [extratoPreviewUrl, setExtratoPreviewUrl] = useState<string | null>(null);

  // Data da alteração da justificativa (para exibir no PDF)
  const [justificativaAlteradaEm, setJustificativaAlteradaEm] = useState<string | null>(null);
  const extratoInputRef = useRef<HTMLInputElement>(null);

  // ======== VALIDAÇÃO ADMIN ========
  const [validandoPlanoId, setValidandoPlanoId] = useState<string | null>(null);

  // ======== IMPRESSÃO DE PLANO DA LISTA ========
  const pendingPrintPlanoIdRef = useRef<string | null>(null);

  // ======== INLINE EDIÇÃO CONTA/EXTRATO EM MEUS PLANOS ========
  const [inlineEditContaId, setInlineEditContaId] = useState<string | null>(null);
  const [inlineContaValue, setInlineContaValue] = useState('');
  const [isUploadingInlineExtrato, setIsUploadingInlineExtrato] = useState(false);
  const [inlineExtratoUploadId, setInlineExtratoUploadId] = useState<string | null>(null);
  const inlineExtratoInputRef = useRef<HTMLInputElement>(null);

  // ======== MENU HAMBÚRGUER PARA BOTÕES ========
  const [openMenuPlanoId, setOpenMenuPlanoId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fechar menu hambúrguer ao clicar fora
  useEffect(() => {
    if (!openMenuPlanoId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuPlanoId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuPlanoId]);

  const LOGO_URL_COLORIDA = "/img/brasao-1.png";  // Versão oficial colorida
  const LOGO_URL_BRANCA = "/img/brasao-3-texto-branco.png";      // Versão oficial branca para header

  // CNES isentos de conta bancária e extrato bancário obrigatório
  const CNES_ISENTO_CONTA_EXTRATO = ['2090236', '7066376', '2071568', '2079798'];

  // ======== DYNAMIC TREE DATA FROM DB ========
  // Tenta carregar os dados dinâmicos do banco até 3 vezes (falhas de rede/timing
  // são transitórias). Se mesmo assim falhar definitivamente, treeDataError fica
  // true e a UI usa a whitelist estática por programa (NATUREZAS_POR_PROGRAMA)
  // como rede de segurança — garante que cada programa continue restrito às suas
  // próprias naturezas mesmo se o banco estiver indisponível, em vez de liberar
  // tudo para todos os programas. Detalhe: console.error/log são desabilitados em
  // produção por utils/security.ts, então o erro também é guardado em estado
  // (treeDataErrorDetail) para poder ser exibido na tela para o admin.
  // Executa uma query isolando qualquer exceção síncrona/assíncrona (ex.: "X is
  // not a constructor" lançado dentro da própria lib) num objeto {data,error}
  // com o nome da query e o stack — assim dá pra saber exatamente qual das 5
  // consultas quebrou e onde, sem precisar do DevTools do usuário.
  const safeQuery = async (
    label: string,
    fn: () => PromiseLike<{ data: any; error: any }>
  ): Promise<{ data: any; error: any; label: string }> => {
    try {
      const res = await fn();
      return { data: res.data, error: res.error, label };
    } catch (err: any) {
      return {
        data: null,
        error: {
          message: `[${label}] ${err?.name || 'Error'}: ${err?.message || String(err)}`,
          stack: err?.stack,
        },
        label,
      };
    }
  };

  const loadTreeDataFromDB = async (attempt = 1): Promise<void> => {
    const MAX_ATTEMPTS = 3;
    try {
      const [p, a, m, n, pn, pm] = await Promise.all([
        safeQuery('programas_orcamentarios', () => supabase.from('programas_orcamentarios').select('id,nome,ordem').eq('ativo', true).order('ordem').order('nome')),
        safeQuery('acoes_servicos_catalogo', () => supabase.from('acoes_servicos_catalogo').select('programa_id,categoria,item,ordem').eq('ativo', true).order('ordem').order('item')),
        safeQuery('metas_quantitativas_catalogo', () => supabase.from('metas_quantitativas_catalogo').select('categoria,item,ordem').eq('ativo', true).order('ordem')),
        safeQuery('naturezas_despesa', () => supabase.from('naturezas_despesa').select('codigo,descricao,ordem').eq('ativo', true).order('ordem').order('codigo')),
        safeQuery('programa_naturezas_catalogo', () => supabase.from('programa_naturezas_catalogo').select('programa_id,codigo,descricao,ordem').eq('ativo', true).order('ordem')),
        safeQuery('programa_metas_catalogo', () => supabase.from('programa_metas_catalogo').select('programa_id,categoria,item,ordem').eq('ativo', true).order('ordem')),
      ]);

      // pm (programa_metas_catalogo) não entra no firstError: é uma tabela opcional/nova
      // e uma falha nela (ex.: ainda não criada) não deve travar o resto da árvore dinâmica.
      const firstError = p.error || a.error || m.error || n.error || pn.error;
      if (firstError && attempt < MAX_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, attempt * 800));
        return loadTreeDataFromDB(attempt + 1);
      }
      if (firstError) {
        setTreeDataError(true);
        setTreeDataErrorDetail(
          `${firstError.message || 'Erro desconhecido ao consultar o banco'}${firstError.stack ? `\n${firstError.stack}` : ''}`
        );
        setTreeDataLoaded(true);
        return;
      }

      if (!p.error && p.data && p.data.length > 0) {
        setDynamicProgramas(p.data.map((x: any) => x.nome));
        if (!a.error && a.data && a.data.length > 0) {
          // Objeto simples em vez de Map: o ícone "Map" do lucide-react era
          // importado neste arquivo e sombreava o construtor global Map,
          // fazendo "new Map(...)" tentar instanciar o componente de ícone
          // (não é construtível) e lançar "Map is not a constructor". O
          // import do ícone foi removido; usar objeto simples aqui evita
          // reintroduzir o mesmo risco se alguém importar o ícone de novo.
          const idToNome: Record<string, string> = {};
          (p.data as any[]).forEach((x: any) => { idToNome[x.id] = x.nome; });
          const grouped: Record<string, AcaoServico[]> = {};
          for (const ac of a.data as any[]) {
            const nomeProg = idToNome[ac.programa_id];
            if (!nomeProg) continue;
            if (!grouped[nomeProg]) grouped[nomeProg] = [];
            let cat = grouped[nomeProg].find((c: AcaoServico) => c.categoria === ac.categoria);
            if (!cat) { cat = { categoria: ac.categoria, itens: [] }; grouped[nomeProg].push(cat); }
            cat.itens.push(ac.item);
          }
          setDynamicAcoes(grouped);
        }
      }
      if (!m.error && m.data && m.data.length > 0) {
        const grp: Record<string, string[]> = {};
        for (const meta of m.data as any[]) {
          if (!grp[meta.categoria]) grp[meta.categoria] = [];
          grp[meta.categoria].push(meta.item);
        }
        setDynamicMetasQuant(grp);
      }
      if (!n.error && n.data && n.data.length > 0) {
        setDynamicNaturezas((n.data as any[]).map(x => ({ codigo: x.codigo, descricao: x.descricao })));
      }
      if (!pn.error && pn.data && pn.data.length > 0) {
        // Use programs data from the already-fetched p, or re-fetch if unavailable
        const progData = (p.data && p.data.length > 0)
          ? (p.data as any[])
          : ((await supabase.from('programas_orcamentarios').select('id,nome').eq('ativo', true)).data ?? []);
        const idToNome: Record<string, string> = {};
        progData.forEach((x: any) => { idToNome[x.id] = x.nome; });
        const grpNat: Record<string, NaturezaDespesa[]> = {};
        for (const pnRow of pn.data as any[]) {
          const nomeProg = idToNome[pnRow.programa_id];
          if (!nomeProg) continue;
          if (!grpNat[nomeProg]) grpNat[nomeProg] = [];
          grpNat[nomeProg].push({ codigo: pnRow.codigo, descricao: pnRow.descricao });
        }
        setDynamicProgramaNaturezas(grpNat);
      }
      if (!pm.error && pm.data && pm.data.length > 0) {
        const progData = (p.data && p.data.length > 0)
          ? (p.data as any[])
          : ((await supabase.from('programas_orcamentarios').select('id,nome').eq('ativo', true)).data ?? []);
        const idToNome: Record<string, string> = {};
        progData.forEach((x: any) => { idToNome[x.id] = x.nome; });
        const grpMetas: Record<string, { categoria: string; itens: string[] }[]> = {};
        for (const pmRow of pm.data as any[]) {
          const nomeProg = idToNome[pmRow.programa_id];
          if (!nomeProg) continue;
          if (!grpMetas[nomeProg]) grpMetas[nomeProg] = [];
          let cat = grpMetas[nomeProg].find(c => c.categoria === pmRow.categoria);
          if (!cat) { cat = { categoria: pmRow.categoria, itens: [] }; grpMetas[nomeProg].push(cat); }
          cat.itens.push(pmRow.item);
        }
        setDynamicProgramaMetas(grpMetas);
      }
      // Sucesso confirmado: dados do banco são a fonte de verdade a partir daqui.
      setTreeDataError(false);
      setTreeDataErrorDetail(null);
      setTreeDataLoaded(true);
    } catch (err: any) {
      if (attempt < MAX_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, attempt * 800));
        return loadTreeDataFromDB(attempt + 1);
      }
      // Falha definitiva após todas as tentativas. console.error não ajuda aqui —
      // utils/security.ts o transforma em no-op em produção — por isso o erro
      // fica em estado (treeDataErrorDetail) para ser mostrado na tela.
      setTreeDataError(true);
      setTreeDataErrorDetail(
        `[fora das queries] ${err?.name || 'Error'}: ${err?.message || String(err)}${err?.stack ? `\n${err.stack}` : ''}`
      );
      setTreeDataLoaded(true);
    }
  };

  // ======== RBAC - CONTROLE DE ACESSO ========
  const isAdmin = (): boolean => currentUser?.role === 'admin';
  
  const canEditPlan = (planCreatedBy: string, planValidado?: boolean): boolean => {
    if (!currentUser) return false;
    // Planos validados não podem ser editados por ninguém
    if (planValidado) return false;
    // Only admin e owner (user) podem editar
    // Intermediate users NÃO podem editar
    return isAdmin() || (currentUser.role === 'user' && planCreatedBy === currentUser.id);
  };
  
  const canViewPlan = (planCreatedBy: string): boolean => {
    if (!currentUser) return false;
    // Admin, intermediate e owner (user) podem visualizar
    if (isAdmin()) return true; // Admin vê tudo
    if (currentUser.role === 'intermediate') return true; // Intermediate vê todos os planos
    return planCreatedBy === currentUser.id; // User vê só seus planos
  };

  // Função para obter o tipo de portaria baseado no programa orçamentário
  const getPortariaForPrograma = (programa: string): string => {
    if (programa.startsWith('EMENDA INDIVIDUAL')) {
      return 'PORTARIA GM/MS Nº 10.297';
    } else if (programa.includes('PORTARIA 10.169')) {
      return 'Portaria GM/MS nº 10.169';
    } else if (programa.includes('10.352')) {
      return 'Portaria GM/MS Nº 10.352';
    }
    return 'PORTARIA GM/MS Nº 10.297'; // Padrão
  };

  // Help Content for each section
  
  const openHelpModal = (sectionId: string) => {
    setHelpSectionId(sectionId);
    setShowHelpModal(true);
  };

  // Check session on mount
  useEffect(() => {
    // ⚠️ INICIALIZAR SEGURANÇA - Desabilitar console e DevTools
    initializeSecurity();

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          try {
            // Query SIMPLIFICADA - apenas colunas que existem com certeza
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('id, full_name, email, cnes')
              .eq('id', session.user.id)
              .maybeSingle();
            
            if (!profileError && profile) {
              try {
                // Pegar role de user_roles (fonte de verdade)
                const { data: userRole } = await supabase
                  .from('user_roles')
                  .select('role')
                  .eq('user_id', session.user.id)
                  .maybeSingle();
                
                const userData = {
                  id: session.user.id,
                  username: session.user.email || '',
                  name: profile.full_name || profile.email,
                  role: userRole?.role || 'user',
                  cnes: profile.cnes || ''
                };
                console.log('🔐 checkSession - Usuário carregado:', userData);
                setCurrentUser(userData);
                // Selecionar primeiro CNES se houver múltiplos
                const cnesList = (profile.cnes || '').split(',').map((c: string) => c.trim()).filter(Boolean);
                setSelectedCnes(cnesList[0] || '');
                setIsAuthenticated(true);
              } catch (roleError) {
                // Se não conseguir pegar role, usa 'user' como padrão
                const userData = {
                  id: session.user.id,
                  username: session.user.email || '',
                  name: profile.full_name || profile.email,
                  role: 'user',
                  cnes: profile.cnes || ''
                };
                setCurrentUser(userData);
                const cnesListFb = (profile.cnes || '').split(',').map((c: string) => c.trim()).filter(Boolean);
                setSelectedCnes(cnesListFb[0] || '');
                setIsAuthenticated(true);
              }
            } else if (profileError && session.user.email === 'sessp.css3@gmail.com') {
              // Admin provisório se erro mas é admin padrão
              setCurrentUser({
                id: session.user.id,
                username: session.user.email,
                name: 'Admin Provisório',
                role: 'admin',
                cnes: ''
              });
              setIsAuthenticated(true);
            }
          } catch (err) {
            // Catch all para qualquer erro de query
            console.warn('⚠️ Erro ao carregar profile:', err);
          }
        }
      } catch (e) {
        // Erro ao verificar sessão - silenciado por segurança
      } finally {
        setIsLoadingAuth(false);
      }
    };
    checkSession();
  }, []);

  // Carregar min_justificativa do Supabase (fonte de verdade para todos os usuários)
  useEffect(() => {
    supabase.from('app_settings').select('value').eq('key', 'min_justificativa').maybeSingle()
      .then(({ data }) => {
        if (data?.value !== undefined && data.value !== null) {
          const val = Number(data.value);
          setMinJustificativa(val);
          try { localStorage.setItem('min_justificativa', String(val)); } catch {}
        }
      });
  }, []);

  // Fetch users if admin
  const prevShowUserManagementRef = useRef(showUserManagement);
  const loadingPlanIdRef = useRef<string | null>(null);
  const planLoadCompletedRef = useRef<Set<string>>(new Set()); // ← NOVO: Tracks which plans have been loaded
  const isSavingRef = useRef(false); // ← NOVO: Sincronamente previne dupla execução de save
  
  useEffect(() => {
    // Only fetch when modal opens (showUserManagement becomes true)
    if (isAuthenticated && showUserManagement && currentUser?.role === 'admin' && !prevShowUserManagementRef.current) {
      const fetchUsers = async () => {
        try {
          console.log('👥 fetchUsers() - Iniciando carregamento de usuários');
          
          // Buscar profiles com user_roles (LEFT JOIN)
          const { data: profiles, error: profileError } = await supabase
            .from('profiles')
            .select('id, full_name, email, cnes')
            .order('full_name');
          
          if (profileError) {
            console.error('❌ Erro ao carregar profiles:', profileError);
            setUsersList([]);
            avisar(`⚠ Erro ao carregar usuários: ${profileError.message}`);
            return;
          }

          console.log('✅ Profiles carregados:', profiles?.length || 0, profiles);

          // Buscar roles de cada usuário
          const { data: userRoles, error: roleError } = await supabase
            .from('user_roles')
            .select('user_id, role, disabled');
          
          if (roleError) {
            console.error('❌ Erro ao carregar user_roles:', roleError);
            setUsersList([]);
            avisar(`⚠ Erro ao carregar permissões: ${roleError.message}`);
            return;
          }

          console.log('✅ User roles carregados:', userRoles?.length || 0, userRoles);

          // Fazer merge dos dados
          if (profiles && userRoles) {
            // Usar objeto simples em vez de Map (compatibilidade)
            const usersByRole: { [key: string]: { role: string; disabled: boolean } } = {};
            userRoles.forEach(ur => {
              usersByRole[ur.user_id] = { role: ur.role, disabled: ur.disabled };
            });
            
            const usersList = profiles.map(p => {
              const role = usersByRole[p.id]?.role || 'user';
              const disabled = usersByRole[p.id]?.disabled || false;
              console.log(`  - User: ${p.email} => role: ${role}, disabled: ${disabled}, cnes: ${p.cnes}`);
              return {
                id: p.id,
                username: p.email || '',
                email: p.email || '',
                name: p.full_name,
                role: role,
                cnes: p.cnes || '',
                disabled: disabled
              };
            });
            
            // ⭐ ORDENAR: Admins sempre primeiro
            const sortedUsersList = usersList.sort((a, b) => {
              // Prioridade 1: Admins sempre primeiro
              if (a.role !== b.role) {
                return a.role === 'admin' ? -1 : 1;
              }
              // Prioridade 2: Por nome dentro do mesmo role
              return (a.name || '').localeCompare(b.name || '');
            });
            
            setUsersList(sortedUsersList);
            console.log('✅ Lista de usuários atualizada (ADMINS PRIMEIRO):', sortedUsersList.length, sortedUsersList);
          } else {
            console.warn('⚠️ Profiles ou userRoles vazios:', { profiles_count: profiles?.length, roles_count: userRoles?.length });
            setUsersList([]);
          }
        } catch (err: any) {
          console.error('❌ Erro ao buscar usuários:', err);
          setUsersList([]);
        }
      };
      fetchUsers();
    }
    
    // Clear list when modal closes (showUserManagement becomes false)
    if (!showUserManagement && prevShowUserManagementRef.current) {
      setUsersList([]);
    }
    
    // Update ref
    prevShowUserManagementRef.current = showUserManagement;
  }, [isAuthenticated, showUserManagement]);

  // Carregar usuários inativos/desativados
  const loadInactiveUsers = async () => {
    try {
      console.log('👻 loadInactiveUsers() - Carregando usuários desativados');
      
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name, email, cnes')
        .order('full_name');
      
      if (profileError) {
        console.error('❌ Erro ao carregar profiles:', profileError);
        return;
      }

      const { data: userRoles, error: roleError } = await supabase
        .from('user_roles')
        .select('user_id, role, disabled');
      
      if (roleError) {
        console.error('❌ Erro ao carregar user_roles:', roleError);
        return;
      }

      // Filtrar APENAS os desativados
      if (profiles && userRoles) {
        const usersByRole: { [key: string]: { role: string; disabled: boolean } } = {};
        userRoles.forEach(ur => {
          usersByRole[ur.user_id] = { role: ur.role, disabled: ur.disabled };
        });
        
        const inactiveList = profiles
          .filter(p => usersByRole[p.id]?.disabled === true)
          .map(p => {
            const role = usersByRole[p.id]?.role || 'user';
            return {
              id: p.id,
              username: p.email || '',
              email: p.email || '',
              name: p.full_name,
              role: role,
              cnes: p.cnes || '',
              disabled: true
            };
          })
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        
        setInactiveUsersList(inactiveList);
        console.log('✅ Usuários inativos carregados:', inactiveList.length, inactiveList);
      }
    } catch (error: any) {
      console.error('❌ Erro ao carregar usuários inativos:', error);
      avisar(`Erro ao carregar inativos: ${error.message}`);
    }
  };

  // Reativar usuário desativado
  const handleReactivateUser = async (userId: string, userName: string) => {
    if (!(await confirmar(`Deseja reativar ${userName}?`, { rotuloConfirmar: 'Reativar' }))) return;

    try {
      console.log('🔄 Reativando usuário:', userId);
      
      const { error } = await supabase
        .from('user_roles')
        .update({ disabled: false, updated_at: new Date().toISOString() })
        .eq('user_id', userId);

      if (error) throw error;

      avisar(`✅ ${userName} foi reativado com sucesso!`);
      console.log('✅ Reativação concluída');

      // Recarregar ambas as listas
      loadInactiveUsers();
      
      // Recarregar lista ativa também
      const { data: profiles2 } = await supabase
        .from('profiles')
        .select('id, full_name, email, cnes')
        .order('full_name');
      const { data: userRoles2 } = await supabase
        .from('user_roles')
        .select('user_id, role, disabled');
      
      if (profiles2 && userRoles2) {
        const usersByRole: { [key: string]: any } = {};
        userRoles2.forEach(ur => {
          usersByRole[ur.user_id] = ur;
        });
        setUsersList(profiles2.map(p => ({
          id: p.id,
          username: p.email || '',
          email: p.email || '',
          name: p.full_name,
          role: usersByRole[p.id]?.role || 'user',
          cnes: p.cnes || '',
          disabled: usersByRole[p.id]?.disabled || false
        })).sort((a, b) => a.role !== b.role ? (a.role === 'admin' ? -1 : 1) : (a.name || '').localeCompare(b.name || '')));
      }
    } catch (error: any) {
      console.error('❌ Erro ao reativar usuário:', error);
      avisar(`❌ Erro ao reativar: ${error.message}`);
    }
  };

  // Carrega planos quando muda para view de listagem ou dashboard
  useEffect(() => {
    if ((currentView === 'list' || currentView === 'dashboard') && isAuthenticated) {
      loadPlanos();
    }
  }, [currentView, isAuthenticated]);

  // Carrega planos assim que o usuário se autentica (para usar no modal de seleção)
  useEffect(() => {
    if (isAuthenticated && planosList.length === 0 && !isLoadingPlanos) {
      loadPlanos();
    }
  }, [isAuthenticated]);

  // Carrega listas dinâmicas do banco de dados (programas, metas, naturezas)
  useEffect(() => {
    if (isAuthenticated) {
      loadTreeDataFromDB();
    }
  }, [isAuthenticated]);

  // Carrega base da planilha local para preenchimentos assistidos
  useEffect(() => {
    if (isAuthenticated) {
      loadSheetBaseRows();
    }
  }, [isAuthenticated]);

  // Carregar disponibilizações para admin quando abrir modal de disponibilização
  useEffect(() => {
    if (isAuthenticated && currentUser?.role === 'admin' && showDisponibilizarEmendaModal) {
      loadEmendasDisponibilizadasAdmin();
      // Marca como visto ao abrir o painel
      localStorage.setItem('lastSeenEmendasAt', new Date().toISOString());
      setEmendaNotifCount(0);
    }
  }, [isAuthenticated, currentUser?.role, showDisponibilizarEmendaModal]);

  // Carregar emendas disponíveis para usuário comum (em qualquer view)
  useEffect(() => {
    if (isAuthenticated && currentUser?.role === 'user') {
      loadEmendasDisponiveisUsuario();
    }
  }, [isAuthenticated, currentUser?.role, currentUser?.cnes]);

  // Usuários comuns sempre começam na lista de planos (não no formulário vazio)
  useEffect(() => {
    if (isAuthenticated && currentUser?.role === 'user' && !editingPlanId && !planoSalvoId) {
      setCurrentView('list');
    }
  }, [isAuthenticated, currentUser?.role]);

  // Atualizar badge de notificação quando a lista admin muda
  useEffect(() => {
    if (currentUser?.role !== 'admin') return;
    const lastSeenAt = localStorage.getItem('lastSeenEmendasAt');
    const count = emendasDisponibilizadasAdmin.filter((e: any) => {
      if (e.status !== 'utilizada') return false;
      if (!lastSeenAt) return true;
      return e.utilizada_em && new Date(e.utilizada_em) > new Date(lastSeenAt);
    }).length;
    setEmendaNotifCount(count);
  }, [emendasDisponibilizadasAdmin, currentUser?.role]);

  // Auto-preencher entidade e CNPJ ao escolher CNES no formulário de disponibilização
  useEffect(() => {
    if (!newEmendaDisponibilizada.cnes) return;
    const first = sheetRowsForSelectedCnes[0];
    if (!first) return;

    if (
      newEmendaDisponibilizada.entidade === (first.entidade || '') &&
      newEmendaDisponibilizada.cnpj === (first.cnpj || '')
    ) {
      return;
    }

    setNewEmendaDisponibilizada(prev => ({
      ...prev,
      entidade: first.entidade || prev.entidade,
      cnpj: first.cnpj || prev.cnpj
    }));
  }, [newEmendaDisponibilizada.cnes, sheetRowsForSelectedCnes]);

  // Auto-fill form when user logs in or form is initialized
  useEffect(() => {
    if (isAuthenticated && currentUser?.email && formData.beneficiario.email !== currentUser.email) {
      updateFormData('beneficiario', { 
        ...formData.beneficiario, 
        email: currentUser.email 
      });
    }
  }, [isAuthenticated, currentUser?.email]);

  // Garantir que selectedCnes é válido quando userCnesList muda
  useEffect(() => {
    if (userCnesList.length > 0 && (!selectedCnes || !userCnesList.includes(selectedCnes))) {
      console.log('🔄 Atualizando selectedCnes para:', userCnesList[0]);
      setSelectedCnes(userCnesList[0]);
    }
  }, [userCnesList]);

  // Cadastro do CNES: ao entrar em um CNES sem site de transparência, exige o preenchimento.
  // Depois de salvo no cnes_cadastro, não é pedido novamente.
  useEffect(() => {
    if (!isAuthenticated || currentUser?.role !== 'user' || !selectedCnes || selectedCnes.includes(',')) return;
    if (cnesCadastroCache[selectedCnes]) return;
    let cancelado = false;
    (async () => {
      const { data, error } = await supabase
        .from('cnes_cadastro')
        .select('site_transparencia')
        .eq('cnes', selectedCnes)
        .maybeSingle();
      if (cancelado) return;
      if (error) {
        // Tabela ausente/sem permissão: não bloqueia o uso do sistema
        console.warn('⚠️ Não foi possível consultar cnes_cadastro:', error.message);
        return;
      }
      const site = (data?.site_transparencia || '').trim();
      if (site) {
        setCnesCadastroCache(prev => ({ ...prev, [selectedCnes]: site }));
      } else {
        setSiteTransparenciaInput('');
        setSiteTransparenciaErro('');
        setSiteTransparenciaObrigatorio(true);
        setShowSiteTransparenciaModal(true);
      }
    })();
    return () => { cancelado = true; };
  }, [isAuthenticated, currentUser?.role, selectedCnes]);

  const abrirEdicaoSiteTransparencia = () => {
    setSiteTransparenciaInput(cnesCadastroCache[selectedCnes] || '');
    setSiteTransparenciaErro('');
    setSiteTransparenciaObrigatorio(false);
    setShowSiteTransparenciaModal(true);
  };

  const handleSalvarSiteTransparencia = async () => {
    let url = siteTransparenciaInput.trim();
    if (url && !/^https?:\/\//i.test(url)) url = `https://${url}`;
    let valido = false;
    try {
      const parsed = new URL(url);
      valido = /^https?:$/.test(parsed.protocol) && parsed.hostname.includes('.');
    } catch {}
    if (!valido) {
      setSiteTransparenciaErro('Informe um endereço válido, ex.: https://www.instituicao.org.br/transparencia');
      return;
    }
    setIsSavingSiteTransparencia(true);
    setSiteTransparenciaErro('');
    try {
      const { error } = await supabase.from('cnes_cadastro').upsert({
        cnes: selectedCnes,
        site_transparencia: url,
        atualizado_em: new Date().toISOString(),
        atualizado_por: currentUser?.id || null
      }, { onConflict: 'cnes' });
      if (error) throw error;
      setCnesCadastroCache(prev => ({ ...prev, [selectedCnes]: url }));
      setShowSiteTransparenciaModal(false);
      setSiteTransparenciaObrigatorio(false);
    } catch (e: any) {
      setSiteTransparenciaErro(`Erro ao salvar: ${e?.message || e}`);
    } finally {
      setIsSavingSiteTransparencia(false);
    }
  };

  // Central de Dúvidas: carrega perguntas e o liga/desliga do painel
  const carregarFaq = async (): Promise<{ itens: FaqItem[]; ativo: boolean }> => {
    const [itensRes, configRes] = await Promise.all([
      supabase.from('faq_itens').select('id, categoria, pergunta, resposta, ordem, ativo').order('ordem', { ascending: true }),
      supabase.from('app_settings').select('value').eq('key', 'faq_painel_ativo').maybeSingle()
    ]);
    if (itensRes.error) {
      // Tabela ausente/sem permissão: painel simplesmente não aparece
      console.warn('⚠️ Central de Dúvidas indisponível:', itensRes.error.message);
      setFaqItens([]);
      setFaqPainelAtivo(false);
      return { itens: [], ativo: false };
    }
    const itens = (itensRes.data || []) as FaqItem[];
    const ativo = configRes.data ? configRes.data.value !== 'false' : true;
    setFaqItens(itens);
    setFaqPainelAtivo(ativo);
    return { itens, ativo };
  };

  // Abre o painel a cada entrada no sistema (uma vez por sessão do navegador; reaparece após sair e entrar)
  useEffect(() => {
    if (!isAuthenticated || !currentUser?.id) return;
    let cancelado = false;
    carregarFaq().then(({ itens, ativo }) => {
      if (cancelado || currentUser.role === 'admin') return;
      const chave = `faq_visto_${currentUser.id}`;
      let jaViu = false;
      try { jaViu = sessionStorage.getItem(chave) === '1'; } catch {}
      if (ativo && itens.some(i => i.ativo) && !jaViu) {
        try { sessionStorage.setItem(chave, '1'); } catch {}
        setFaqBoasVindas(true);
        setShowFaqPanel(true);
      }
    });
    return () => { cancelado = true; };
  }, [isAuthenticated, currentUser?.id]);

  const faqDisponivel = currentUser?.role === 'admin' || (faqPainelAtivo && faqItens.some(i => i.ativo));

  // Abre um formulário de plano em branco
  const iniciarNovoPlano = () => {
    setCurrentView('new');
    setActiveSection('info-emenda');
    setSentSuccess(false);
    setEditingPlanId(null);
    setPlanoSalvoId(null);
    setSelectedOfertaEmendaId(null);
    setFormData(getInitialFormData());
    setLastSavedFormData(null);
    setFormHasChanges(false);
    setJustificativaAlteradaEm(null);
    setMobileMenuOpen(false);
  };

  const abrirFaq = () => {
    setFaqBoasVindas(false);
    setShowFaqPanel(true);
    setMobileMenuOpen(false);
  };

  // Auto-fill CNES para usuários regulares - usa apenas selectedCnes (nunca valor com vírgula)
  useEffect(() => {
    if (isAuthenticated && currentUser?.role === 'user' && selectedCnes) {
      // Apenas atualizar se o valor atual é diferente E se selectedCnes não contém vírgula
      if (!selectedCnes.includes(',') && formData.beneficiario.cnes !== selectedCnes) {
        updateFormData('beneficiario', { 
          ...formData.beneficiario, 
          cnes: selectedCnes
        });
      }
    }
  }, [isAuthenticated, selectedCnes, currentUser?.role]);

  // Quando muda para 'new', garante que CNES é preenchido para usuários padrão
  useEffect(() => {
    if (currentView === 'new' && isAuthenticated && currentUser?.role === 'user' && selectedCnes) {
      console.log('📝 View mudou para NEW, preenchendo CNES para usuário padrão:', selectedCnes);
      if (!selectedCnes.includes(',') && formData.beneficiario.cnes !== selectedCnes) {
        updateFormData('beneficiario', { 
          ...formData.beneficiario, 
          cnes: selectedCnes
        });
      }
    }
  }, [currentView, isAuthenticated, selectedCnes, currentUser?.role]);

  // Proteção: se formData.beneficiario.cnes tiver vírgula, forçar para selectedCnes ou primeiro CNES
  useEffect(() => {
    if (formData.beneficiario.cnes && formData.beneficiario.cnes.includes(',')) {
      const firstCnes = selectedCnes || formData.beneficiario.cnes.split(',')[0].trim();
      console.log('⚠️ CNES com vírgula detectado no form, corrigindo para:', firstCnes);
      updateFormData('beneficiario', { ...formData.beneficiario, cnes: firstCnes });
    }
  }, [formData.beneficiario.cnes]);

  useEffect(() => {
    if (currentUser?.role !== 'user' || currentView !== 'new') return;
    if (selectedOfertaEmendaId) return;
    if (emendasDisponiveisFiltradas.length === 0) return;
    const formJaTemDadosDeEmenda = Boolean(formData.emenda.parlamentar || formData.emenda.valor || formData.emenda.programa || formData.beneficiario.nome || formData.beneficiario.cnpj);
    if (formJaTemDadosDeEmenda) return;

    aplicarEmendaDisponibilizadaNoFormulario(emendasDisponiveisFiltradas[0]);
  }, [currentUser?.role, currentView, selectedOfertaEmendaId, emendasDisponiveisFiltradas]);

  // Track form changes - melhorado para detectar mudanças reais
  useEffect(() => {
    if (lastSavedFormData) {
      // Normalizar ambos os dados antes de comparar (remover espaçamento extra)
      const currentJson = JSON.stringify(formData);
      const savedJson = JSON.stringify(lastSavedFormData);
      const hasChanged = currentJson !== savedJson;
      setFormHasChanges(hasChanged);
    } else {
      // Se não há dados salvos anteriormente, é um novo plano - permitir salvar
      setFormHasChanges(false);
    }
  }, [formData, lastSavedFormData]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setIsSending(true);

    try {
      // Validação básica
      if (!loginInput.email || !loginInput.password) {
        throw new Error('E-mail e senha são obrigatórios');
      }

      if (!validateEmail(loginInput.email)) {
        throw new Error('E-mail inválido');
      }

      // PASSO 1: Fazer autenticação básica (SEM queries extras!)
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginInput.email,
        password: loginInput.password
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('E-mail ou senha incorretos');
        }
        if (error.message.includes('Email not confirmed')) {
          throw new Error('E-mail não confirmado. Verifique sua caixa de entrada.');
        }
        throw new Error(error.message || 'Erro ao fazer login');
      }

      if (data.user) {
        // ✅ LOGIN BEM-SUCEDIDO! Logar com dados mínimos
        const userObject = {
          id: data.user.id,
          username: data.user.email || '',
          name: data.user.email?.split('@')[0] || 'Usuário',
          role: 'user',
          cnes: ''
        };
        
        console.log('✅ Login bem-sucedido:', userObject.username);
        setCurrentUser(userObject);
        setIsAuthenticated(true);
        setLoginInput({ email: '', password: '' });

        // PASSO 2: Carregar dados extras em background (NÃO BLOQUEIA O LOGIN!)
        // Trata erros silenciosamente
        const loadUserData = async () => {
          try {
            // Buscar perfil
            const { data: profile } = await supabase
              .from('profiles')
              .select('full_name, cnes')
              .eq('id', data.user.id)
              .maybeSingle();
            
            if (profile) {
              setCurrentUser(prev => prev ? {
                ...prev,
                name: profile.full_name || prev.name,
                cnes: profile.cnes || ''
              } : null);
              // Selecionar primeiro CNES se ainda não selecionado
              const cnesLogin = (profile.cnes || '').split(',').map((c: string) => c.trim()).filter(Boolean);
              setSelectedCnes(prev => prev || cnesLogin[0] || '');
            }
          } catch (err) {
            console.warn('⚠️ Erro ao carregar profile:', err);
          }

          try {
            // Buscar role
            const { data: userRole } = await supabase
              .from('user_roles')
              .select('role, disabled')
              .eq('user_id', data.user.id)
              .maybeSingle();
            
            if (userRole) {
              if (userRole.disabled) {
                await supabase.auth.signOut();
                setCurrentUser(null);
                setIsAuthenticated(false);
                setLoginError('Este usuário foi desativado.');
              } else if (userRole.role) {
                setCurrentUser(prev => prev ? { ...prev, role: userRole.role } : null);
              }
            }
          } catch (err) {
            console.warn('⚠️ Erro ao carregar role:', err);
          }
        };

        // Executar sem await - não bloqueia o login
        loadUserData().catch(err => console.warn('Erro ao carregar dados do usuário:', err));
      }
    } catch (error: any) {
      console.error('Erro ao fazer login:', error);
      setLoginError(error.message || 'Erro ao realizar login. Verifique as credenciais.');
    } finally {
      setIsSending(false);
    }
  };

  const handleLogout = async () => {
    try { if (currentUser?.id) sessionStorage.removeItem(`faq_visto_${currentUser.id}`); } catch {}
    setShowFaqPanel(false);
    setShowFaqAdmin(false);
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setSelectedOfertaEmendaId(null);
    setActiveSection('info-emenda');
    setSentSuccess(false);
    setShowSiteTransparenciaModal(false);
    setSiteTransparenciaObrigatorio(false);
  };

  // Desativar/Ativar usuário
  const handleToggleUserDisable = async (userId: string) => {
    try {
      const user = usersList.find(u => u.id === userId);
      if (!user) return;

      const { error } = await supabase
        .from('user_roles')
        .update({ disabled: !user.disabled })
        .eq('user_id', userId);

      if (error) throw error;

      const action = user.disabled ? 'ativado' : 'desativado';
      avisar(`✓ Usuário ${action} com sucesso!`);

      // Recarregar lista
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email, cnes')
        .order('full_name');
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('user_id, role, disabled');
      
      if (profiles && userRoles) {
        const usersByRole: { [key: string]: any } = {};
        userRoles.forEach(ur => {
          usersByRole[ur.user_id] = ur;
        });
        setUsersList(profiles.map(p => ({
          id: p.id,
          username: p.email || '',
          email: p.email || '',
          name: p.full_name,
          role: usersByRole[p.id]?.role || 'user',
          cnes: p.cnes || '',
          disabled: usersByRole[p.id]?.disabled || false
        })));
      }
    } catch (error: any) {
      avisar(`❌ Erro ao alterar status: ${error.message}`);
    }
  };

  // Alterar senha - Enviar email de reset
  const handleChangePassword = async (userEmail: string) => {
    try {
      console.log('🔑 Enviando email de reset para:', userEmail);
      const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;
      avisar(`✅ Email de redefinição de senha enviado para ${userEmail}`);
    } catch (error: any) {
      console.error('❌ Erro ao enviar email de reset:', error);
      avisar(`❌ Erro: ${error.message}`);
    }
  };

  // Promover usuário a admin
  const handlePromoteUserToAdmin = async (userId: string, userName: string) => {
    try {
      console.log('👑 Promovendo usuário para admin:', userId);
      
      const { error } = await supabase
        .from('user_roles')
        .update({ role: 'admin' })
        .eq('user_id', userId);

      if (error) throw error;

      avisar(`✅ ${userName} foi promovido a ADMINISTRADOR!`);
      console.log('✅ Promoção concluída');

      // Recarregar lista
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email, cnes')
        .order('full_name');
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('user_id, role, disabled');
      
      if (profiles && userRoles) {
        const usersByRole: { [key: string]: any } = {};
        userRoles.forEach(ur => {
          usersByRole[ur.user_id] = ur;
        });
        setUsersList(profiles.map(p => ({
          id: p.id,
          username: p.email || '',
          email: p.email || '',
          name: p.full_name,
          role: usersByRole[p.id]?.role || 'user',
          cnes: p.cnes || '',
          disabled: usersByRole[p.id]?.disabled || false
        })));
      }
    } catch (error: any) {
      console.error('❌ Erro ao promover usuário:', error);
      avisar(`❌ Erro ao promover: ${error.message}`);
    }
  };

  // Rebaixar de admin para usuário
  const handleDemoteUserToRegular = async (userId: string, userName: string) => {
    try {
      console.log('⬇️ Rebaixando usuário para regular:', userId);
      
      const { error } = await supabase
        .from('user_roles')
        .update({ role: 'user' })
        .eq('user_id', userId);

      if (error) throw error;

      avisar(`✅ ${userName} foi rebaixado para USUÁRIO PADRÃO!`);
      console.log('✅ Rebaixamento concluído');

      // Recarregar lista
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email, cnes')
        .order('full_name');
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('user_id, role, disabled');
      
      if (profiles && userRoles) {
        const usersByRole: { [key: string]: any } = {};
        userRoles.forEach(ur => {
          usersByRole[ur.user_id] = ur;
        });
        setUsersList(profiles.map(p => ({
          id: p.id,
          username: p.email || '',
          email: p.email || '',
          name: p.full_name,
          role: usersByRole[p.id]?.role || 'user',
          cnes: p.cnes || '',
          disabled: usersByRole[p.id]?.disabled || false
        })));
      }
    } catch (error: any) {
      console.error('❌ Erro ao rebaixar usuário:', error);
      avisar(`❌ Erro ao rebaixar: ${error.message}`);
    }
  };

  // Alterar papel do usuário de forma genérica
  const handleChangeUserRole = async (userId: string, userName: string, newRole: 'user' | 'intermediate' | 'admin') => {
    try {
      const roleNames = {
        'user': 'USUÁRIO PADRÃO',
        'intermediate': 'USUÁRIO INTERMEDIÁRIO',
        'admin': 'ADMINISTRADOR'
      };

      console.log(`🔄 Alterando papel de ${userName} para ${newRole}:`, userId);
      
      const { error } = await supabase
        .from('user_roles')
        .update({ role: newRole })
        .eq('user_id', userId);

      if (error) throw error;

      avisar(`✅ ${userName} foi alterado para ${roleNames[newRole]}!`);
      console.log('✅ Alteração de papel concluída');

      // Recarregar lista
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email, cnes')
        .order('full_name');
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('user_id, role, disabled');
      
      if (profiles && userRoles) {
        const usersByRole: { [key: string]: any } = {};
        userRoles.forEach(ur => {
          usersByRole[ur.user_id] = ur;
        });
        setUsersList(profiles.map(p => ({
          id: p.id,
          username: p.email || '',
          email: p.email || '',
          name: p.full_name,
          role: usersByRole[p.id]?.role || 'user',
          cnes: p.cnes || '',
          disabled: usersByRole[p.id]?.disabled || false
        })));
      }
    } catch (error: any) {
      console.error('❌ Erro ao alterar papel do usuário:', error);
      avisar(`❌ Erro ao alterar papel: ${error.message}`);
    }
  };

  // Excluir usuário - Delete direto na tabela
  const handleDeleteUser = async (userId: string, rotuloUsuario: string) => {
    if (!(await confirmar(`Excluir o usuário ${rotuloUsuario}?\n\nEsta ação não pode ser desfeita.`, { rotuloConfirmar: 'Excluir usuário' }))) return;

    try {
      // Deletar de user_roles primeiro (foreign key)
      const { error: rolesError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (rolesError) throw rolesError;

      // Deletar da tabela profiles
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      avisar('✓ Usuário deletado com sucesso!');

      // Recarregar lista
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email, cnes')
        .order('full_name');
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('user_id, role, disabled');
      
      if (profiles && userRoles) {
        const usersByRole: { [key: string]: any } = {};
        userRoles.forEach(ur => {
          usersByRole[ur.user_id] = ur;
        });
        setUsersList(profiles.map(p => ({
          id: p.id,
          username: p.email || '',
          email: p.email || '',
          name: p.full_name,
          role: usersByRole[p.id]?.role || 'user',
          cnes: p.cnes || '',
          disabled: usersByRole[p.id]?.disabled || false
        })));
      }
    } catch (error: any) {
      console.error('Erro ao deletar usuário:', error);
      avisar(`❌ Erro ao deletar usuário: ${error.message}`);
    }
  };

  // Editar usuário - Update nome, email, CNES e opcionalmente senha
  const handleEditUser = async () => {
    if (!editingUser.id) return;
    
    try {
      console.log('🔧 Editando usuário:', editingUser);
      
      // Validação de nome
      if (!editingUser.name || editingUser.name.trim().length < 3) {
        throw new Error('Nome deve ter no mínimo 3 caracteres');
      }

      // Validação de email
      if (!editingUser.email || !validateEmail(editingUser.email)) {
        throw new Error('E-mail inválido. Use um formato válido (ex: usuario@example.com)');
      }

      // 1️⃣ Atualizar email via RPC (atualiza auth.users + auth.identities + profiles)
      const { data: emailResult, error: emailError } = await supabase.rpc('update_user_email', {
        user_id: editingUser.id,
        new_email: editingUser.email
      });
      
      if (emailError) {
        throw new Error('Erro ao atualizar email: ' + emailError.message);
      }
      
      if (emailResult && !emailResult.success) {
        throw new Error(emailResult.error || 'Erro ao atualizar email no banco de autenticação');
      }
      
      console.log('✅ Email atualizado em auth.users, auth.identities e profiles');

      // 2️⃣ Atualizar perfil (nome, CNES) - email já foi atualizado pela RPC
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          full_name: editingUser.name, 
          cnes: editingUser.cnes || null
        })
        .eq('id', editingUser.id);

      if (profileError) throw profileError;

      // 3️⃣ Se houver nova senha, enviar email de reset
      if (editingUser.password && editingUser.password.length >= 6) {
        try {
          // Usar RPC function para atualizar senha
          const { error: passwordError } = await supabase.rpc('update_user_password', {
            user_id: editingUser.id,
            new_password: editingUser.password
          });
          if (passwordError) throw passwordError;
        } catch (err) {
          console.warn('⚠️ Não foi possível atualizar senha via RPC, tentando email de reset...');
          // Fallback: enviar email de reset
          await supabase.auth.resetPasswordForEmail(editingUser.email, {
            redirectTo: `${window.location.origin}/reset-password`
          });
        }
      }

      avisar('✅ Usuário atualizado com sucesso!\n\nSe o email foi alterado, o usuário deve fazer login com o novo email.');
      setShowEditUserModal(false);
      setEditingUser({ id: '', email: '', name: '', cnes: '', password: '' });

      // Recarregar lista
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email, cnes')
        .order('full_name');
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('user_id, role, disabled');
      
      if (profiles && userRoles) {
        const usersByRole: { [key: string]: any } = {};
        userRoles.forEach(ur => {
          usersByRole[ur.user_id] = ur;
        });
        setUsersList(profiles.map(p => ({
          id: p.id,
          username: p.email || '',
          email: p.email || '',
          name: p.full_name,
          role: usersByRole[p.id]?.role || 'user',
          cnes: p.cnes || '',
          disabled: usersByRole[p.id]?.disabled || false
        })));
      }
    } catch (error: any) {
      console.error('❌ Erro ao editar usuário:', error);
      avisar(`❌ Erro ao editar: ${error.message}`);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);
    try {
      // Validação de email
      if (!newUser.email || !validateEmail(newUser.email)) {
        throw new Error('E-mail inválido. Use um formato válido (ex: usuario@example.com)');
      }

      // Validação de senha
      if (!newUser.password || newUser.password.length < 6) {
        throw new Error('Senha deve ter no mínimo 6 caracteres');
      }

      // Validação de nome
      if (!newUser.name || newUser.name.trim().length < 3) {
        throw new Error('Nome deve ter no mínimo 3 caracteres');
      }

      // Validação de perfil
      if (!newUser.role) {
        throw new Error('Perfil do usuário é obrigatório');
      }

      // Validação de CNES - OBRIGATÓRIO (aceita múltiplos separados por vírgula)
      if (!newUser.cnes || newUser.cnes.trim().length === 0) {
        throw new Error('🔴 CNES da instituição é OBRIGATÓRIO (máximo 8 dígitos cada)');
      }

      const cnesValues = newUser.cnes.split(',').map(c => c.trim()).filter(Boolean);
      for (const cnes of cnesValues) {
        if (cnes.length > 8) {
          throw new Error(`CNES "${cnes}" não pode ter mais de 8 dígitos`);
        }
        if (!/^\d+$/.test(cnes)) {
          throw new Error(`CNES "${cnes}" deve conter apenas números`);
        }
      }

      console.log('👤 Criando novo usuário:', { email: newUser.email, name: newUser.name, role: newUser.role });

      // 1. Criar usuário automaticamente em auth.users via RPC
      const { data: authData, error: authError } = await supabase.rpc(
        'criar_usuario_automático',
        {
          p_email: newUser.email,
          p_password: newUser.password,
          p_full_name: newUser.name,
          p_cnes: newUser.cnes.trim(),
          p_role: newUser.role
        }
      );

      if (authError) {
        throw new Error(authError.message || 'Erro ao criar usuário');
      } else if (authData && authData.success) {
        // Novo usuário criado com sucesso
        console.log('✅ Usuário criado automaticamente:', authData);

        // Sucesso - novo usuário
        const roleName = newUser.role === 'admin' ? 'Administrador' : newUser.role === 'intermediate' ? 'Usuário Intermediário' : 'Usuário Padrão';
        avisar(`✅ Usuário registrado com sucesso!\n\nE-mail: ${newUser.email}\nCNES: ${newUser.cnes}\nPerfil: ${roleName}\n\nO usuário pode fazer login imediatamente.`);
      } else if (authData && !authData.success) {
        throw new Error(authData.error || 'Erro ao criar usuário');
      } else {
        throw new Error('Falha ao criar usuário');
      }
      
      // Reset do formulário
      setNewUser({ email: '', password: '', name: '', role: 'user' as 'user' | 'admin' | 'intermediate', cnes: '' });
      
      // Recarregar lista de usuários
      const { data: profiles, error: fetchError } = await supabase
        .from('profiles')
        .select('id, full_name, email, cnes')
        .order('full_name');
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('user_id, role, disabled');
      
      if (!fetchError && profiles && userRoles) {
        const usersByRole: { [key: string]: any } = {};
        userRoles.forEach(ur => {
          usersByRole[ur.user_id] = ur;
        });
        setUsersList(profiles.map(p => ({
          id: p.id,
          username: p.email || '',
          email: p.email || '',
          name: p.full_name,
          role: usersByRole[p.id]?.role || 'user',
          cnes: p.cnes || '',
          disabled: usersByRole[p.id]?.disabled || false
        })));
      }
    } catch (error: any) {
      console.error('Erro ao registrar usuário:', error);
      avisar(`❌ Erro ao registrar: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // IMPORTAR USUARIOS VIA CSV
  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCsvUploading(true);
    setCsvResults(null);
    
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      
      if (lines.length < 2) {
        throw new Error('O arquivo CSV deve ter pelo menos um cabeçalho e uma linha de dados.');
      }

      // Parser CSV que respeita campos entre aspas (para CNES com vírgulas ex: "2078813,2089335")
      const parseCsvLine = (line: string, sep: string): string[] => {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
              current += '"';
              i++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (ch === sep && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += ch;
          }
        }
        result.push(current.trim());
        return result;
      };

      // Detectar separador (vírgula ou ponto-e-vírgula)
      const headerLine = lines[0];
      const separator = headerLine.includes(';') ? ';' : ',';
      
      // Parsear cabeçalho (normalizar: minúsculo, sem acento, sem espaço extra)
      const normalize = (s: string) => s.toLowerCase().trim()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
      
      const headers = parseCsvLine(headerLine, separator).map(h => normalize(h));
      
      // Mapear colunas flexivelmente
      const findCol = (keywords: string[]) => {
        return headers.findIndex(h => keywords.some(k => h.includes(k)));
      };
      
      const colNome = findCol(['nome', 'name', 'fullname', 'nomecompleto', 'responsavel']);
      const colEmail = findCol(['email', 'mail', 'emailresponsavel']);
      const colCnes = findCol(['cnes', 'codigocnes']);
      const colSenha = findCol(['senha', 'password', 'pass']);
      
      if (colEmail === -1) {
        throw new Error('Coluna "email" não encontrada no CSV. O cabeçalho deve conter: nome, email, cnes, senha');
      }
      if (colNome === -1) {
        throw new Error('Coluna "nome" não encontrada no CSV.');
      }
      if (colCnes === -1) {
        throw new Error('Coluna "cnes" não encontrada no CSV.');
      }
      
      // Parsear linhas de dados
      const usuarios: any[] = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCsvLine(lines[i], separator);
        const email = cols[colEmail]?.trim();
        const nome = cols[colNome]?.trim();
        const cnes = cols[colCnes]?.trim();
        const senha = colSenha !== -1 ? cols[colSenha]?.trim() : (cnes?.includes(',') ? cnes.split(',')[0].trim() : cnes);
        
        if (!email || !nome) continue;
        
        usuarios.push({ nome, email, cnes: cnes || '', senha: senha || cnes || '' });
      }
      
      if (usuarios.length === 0) {
        throw new Error('Nenhum usuário válido encontrado no CSV.');
      }
      
      // Confirmar com o admin
      const confirma = await confirmar(
        `Foram encontrados ${usuarios.length} usuários no CSV.\n\n` +
        `Primeiros 3:\n` +
        usuarios.slice(0, 3).map(u => `  • ${u.nome} - ${u.email} - CNES: ${u.cnes}`).join('\n') +
        (usuarios.length > 3 ? `\n  ... e mais ${usuarios.length - 3}` : '') +
        `\n\nDeseja criar todos agora?`
      );
      
      if (!confirma) {
        setCsvUploading(false);
        e.target.value = '';
        return;
      }

      console.log('🚀 Iniciando criação de usuários...');

      // Criar cliente separado para signUp (não afeta sessão do admin)
      const { createClient: createSupabaseClient } = await import('@supabase/supabase-js');
      const signUpClient = createSupabaseClient(
        'https://tlpmspfnswaxwqzmwski.supabase.co',
        'sb_publishable_a_t5QoKSL53wf1uT6GjqYg_wk2ENe-9'
      );

      let criados = 0;
      let erros = 0;
      const mensagens_erro: string[] = [];

      // Processar um por vez para não perder sessão
      for (let i = 0; i < usuarios.length; i++) {
        const usuario = usuarios[i];
        try {
          console.log(`⏳ Criando usuário ${i + 1}/${usuarios.length}: ${usuario.email}`);

          // 1. Criar usuário via auth.signUp (trigger auto-cria profile)
          const { data: signUpData, error: signUpError } = await signUpClient.auth.signUp({
            email: usuario.email,
            password: usuario.senha,
            options: {
              data: {
                full_name: usuario.nome,
                cnes: usuario.cnes,
                role: 'user'
              }
            }
          });

          if (signUpError) {
            console.warn(`⚠️ Erro ao criar ${usuario.email}:`, signUpError);
            erros++;
            mensagens_erro.push(`${i + 1}. ${usuario.email}: ${signUpError.message}`);
            continue;
          }

          const userId = signUpData?.user?.id;
          if (!userId) {
            erros++;
            mensagens_erro.push(`${i + 1}. ${usuario.email}: Usuário não retornou ID`);
            continue;
          }

          // 2. Atualizar CNES no profile (trigger pode não setar)
          await supabase.from('profiles').upsert({
            id: userId,
            full_name: usuario.nome,
            email: usuario.email,
            cnes: usuario.cnes
          }, { onConflict: 'id' });

          // 3. Criar role
          await supabase.from('user_roles').upsert({
            user_id: userId,
            role: 'user',
            disabled: false
          }, { onConflict: 'user_id' });

          console.log(`✅ Usuário criado: ${usuario.email} (ID: ${userId})`);
          criados++;

          // Logout do signUpClient para não manter sessão do novo usuário
          await signUpClient.auth.signOut();

        } catch (err: any) {
          console.error(`❌ Erro ao processar ${usuario.email}:`, err);
          erros++;
          mensagens_erro.push(`${i + 1}. ${usuario.email}: ${err.message}`);
        }
      }

      const resultado = {
        success: erros === 0,
        total: usuarios.length,
        criados,
        erros,
        mensagens_erro
      };

      setCsvResults(resultado);

      // Mensagem de conclusão
      let alertMsg = 
        `✅ Importação concluída!\n\n` +
        `Total processado: ${resultado.total}\n` +
        `Criados: ${resultado.criados}\n` +
        `Erros: ${resultado.erros}`;
      
      if (resultado.erros > 0) {
        alertMsg += `\n\n❌ Erros encontrados:\n${resultado.mensagens_erro.slice(0, 5).join('\n')}`;
        if (resultado.mensagens_erro.length > 5) {
          alertMsg += `\n... e mais ${resultado.mensagens_erro.length - 5}`;
        }
      }
      
      avisar(alertMsg);

      // Recarregar lista de usuários
      const { data: profiles } = await supabase.from('profiles').select('id, full_name, email, cnes').order('full_name');
      const { data: userRoles } = await supabase.from('user_roles').select('user_id, role, disabled');
      if (profiles && userRoles) {
        const usersByRole: { [key: string]: any } = {};
        userRoles.forEach(ur => { usersByRole[ur.user_id] = ur; });
        setUsersList(profiles.map(p => ({
          id: p.id, username: p.email || '', email: p.email || '',
          name: p.full_name, role: usersByRole[p.id]?.role || 'user',
          cnes: p.cnes || '', disabled: usersByRole[p.id]?.disabled || false
        })));
      }

    } catch (error: any) {
      console.error('Erro ao importar CSV:', error);
      avisar(`❌ Erro ao importar: ${error.message}`);
    } finally {
      setCsvUploading(false);
      e.target.value = '';
    }
  };

  const handleDisponibilizarEmenda = async () => {
    try {
      if (!newEmendaDisponibilizada.cnes || !newEmendaDisponibilizada.entidade || !newEmendaDisponibilizada.cnpj || !newEmendaDisponibilizada.parlamentar || !newEmendaDisponibilizada.valor || !newEmendaDisponibilizada.programa) {
        avisar('Preencha todos os campos da disponibilização de emenda.');
        return;
      }

      const valorNumerico = parseCurrency(newEmendaDisponibilizada.valor);
      if (valorNumerico <= 0) {
        avisar('Informe um valor válido para a emenda.');
        return;
      }

      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) throw new Error('Sessão expirada. Faça login novamente.');

      setIsSavingDisponibilizacao(true);

      const { error } = await supabase
        .from('emendas_disponibilizadas')
        .insert([
          {
            cnes: newEmendaDisponibilizada.cnes,
            entidade: newEmendaDisponibilizada.entidade,
            cnpj: newEmendaDisponibilizada.cnpj,
            parlamentar: newEmendaDisponibilizada.parlamentar,
            tipo_parlamentar: newEmendaDisponibilizada.tipoParlamentar || null,
            valor: valorNumerico,
            programa: newEmendaDisponibilizada.programa,
            numero_emenda: newEmendaDisponibilizada.numeroEmenda || null,
            status: 'disponibilizada',
            disponibilizada_por: userId
          }
        ]);

      if (error) throw error;

      avisar('✅ Emenda disponibilizada com sucesso para a entidade/CNES selecionados.');

      setNewEmendaDisponibilizada({
        cnes: '',
        entidade: '',
        cnpj: '',
        parlamentar: '',
        tipoParlamentar: '',
        valor: '',
        programa: '',
        numeroEmenda: ''
      });

      await loadEmendasDisponibilizadasAdmin();
      if (currentUser?.role === 'user') {
        await loadEmendasDisponiveisUsuario();
      }
    } catch (error: any) {
      console.error('❌ Erro ao disponibilizar emenda:', error);
      avisar(`❌ Erro ao disponibilizar emenda: ${error.message}`);
    } finally {
      setIsSavingDisponibilizacao(false);
    }
  };

  const openEditEmendaModal = (emenda: any) => {
    setEditingEmenda(emenda);
    setEditingEmendaForm({
      cnes: emenda.cnes || '',
      entidade: emenda.entidade || '',
      cnpj: emenda.cnpj || '',
      parlamentar: emenda.parlamentar || '',
      tipoParlamentar: (emenda.tipo_parlamentar as 'deputado' | 'senador') || '',
      valor: maskCurrency(String(emenda.valor || '')),
      programa: emenda.programa || '',
      numeroEmenda: emenda.numero_emenda || ''
    });
    setShowEditEmendaModal(true);
  };

  const handleSaveEditEmenda = async () => {
    if (!editingEmenda) return;
    const valorNumerico = parseCurrency(editingEmendaForm.valor);
    if (!editingEmendaForm.cnes || !editingEmendaForm.entidade || !editingEmendaForm.cnpj || !editingEmendaForm.parlamentar || valorNumerico <= 0 || !editingEmendaForm.programa) {
      avisar('Preencha todos os campos obrigatórios.');
      return;
    }
    setIsSavingEditEmenda(true);
    try {
      const { error } = await supabase
        .from('emendas_disponibilizadas')
        .update({
          cnes: editingEmendaForm.cnes,
          entidade: editingEmendaForm.entidade,
          cnpj: editingEmendaForm.cnpj,
          parlamentar: editingEmendaForm.parlamentar,
          tipo_parlamentar: editingEmendaForm.tipoParlamentar || null,
          valor: valorNumerico,
          programa: editingEmendaForm.programa,
          numero_emenda: editingEmendaForm.numeroEmenda || null
        })
        .eq('id', editingEmenda.id);
      if (error) throw error;
      setShowEditEmendaModal(false);
      setEditingEmenda(null);
      await loadEmendasDisponibilizadasAdmin();
    } catch (error: any) {
      avisar(`❌ Erro ao salvar edição: ${error.message}`);
    } finally {
      setIsSavingEditEmenda(false);
    }
  };

  const handleCancelEmenda = async (id: string) => {
    if (!(await confirmar('Cancelar esta emenda disponibilizada?\n\nO usuário não poderá mais utilizá-la.', { rotuloConfirmar: 'Cancelar emenda' }))) return;
    try {
      const { error } = await supabase
        .from('emendas_disponibilizadas')
        .update({ status: 'cancelada' })
        .eq('id', id);
      if (error) throw error;
      await loadEmendasDisponibilizadasAdmin();
    } catch (error: any) {
      avisar(`❌ Erro ao cancelar emenda: ${error.message}`);
    }
  };

  const formatElapsedTime = (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `há ${days} dia${days > 1 ? 's' : ''}`;
    if (hours > 0) return `há ${hours}h`;
    if (minutes > 0) return `há ${minutes}min`;
    return 'agora mesmo';
  };

  const formatCurrencyDisplay = (value: any) => {
    const n = Number(value || 0);
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // CARREGA LISTA DE PLANOS DO SUPABASE
  const loadPlanos = async () => {
    setIsLoadingPlanos(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Sessão expirada");

      let query = supabase.from('planos_trabalho').select('*');

      // Se for admin ou intermediário, carregar TODOS os planos
      // Se for usuário padrão, filtrar apenas pelos planos do usuário
      if (isAdmin() || currentUser?.role === 'intermediate') {
      } else {
        query = query.eq('created_by', user.id);
      }

      const { data: planos, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Erro ao carregar planos:', error);
        throw error;
      }
      
      // Enriquecer planos sem created_by_name buscando do profiles
      const enriched = planos || [];
      const semNome = enriched.filter(p => !p.created_by_name && p.created_by);
      if (semNome.length > 0) {
        const userIds = [...new Set(semNome.map(p => p.created_by))];
        const { data: profiles } = await supabase.from('profiles').select('id, full_name, email').in('id', userIds);
        if (profiles && profiles.length > 0) {
          // Usar objeto simples em vez de Map (compatibilidade)
          const profileMap: { [key: string]: { id: string; full_name: string; email: string } } = {};
          profiles.forEach(p => { profileMap[p.id] = p; });
          for (const p of enriched) {
            if (!p.created_by_name && p.created_by && profileMap[p.created_by]) {
              const prof = profileMap[p.created_by];
              p.created_by_name = prof.full_name || prof.email || null;
              p.created_by_email = p.created_by_email || prof.email || null;
              // Atualizar no banco para não precisar buscar de novo
              supabase.from('planos_trabalho').update({ created_by_name: p.created_by_name, created_by_email: p.created_by_email }).eq('id', p.id).then(() => {});
            }
          }
        }
      }
      setPlanosList(enriched);
    } catch (error: any) {
      console.error('❌ Erro ao carregar planos (catch):', error);
      avisar(`Erro ao carregar planos: ${error.message}`);
      console.error('Erro ao carregar planos:', error);
    } finally {
      setIsLoadingPlanos(false);
    }
  };

  // RESET: Quando fecha a edição, limpar o cache para permitir novo carregamento
  useEffect(() => {
    if (!editingPlanId) {
      // Usuário fechou a edição - SEMPRE limpar refs
      console.log('🚪 Edição fechada - limpando refs para forçar recarga');
      planLoadCompletedRef.current.clear();
      loadingPlanIdRef.current = null;
    }
  }, [editingPlanId]);

  // Carrega um plano específico para editar 
  // REMOVE CACHE: Carrega SEMPRE quando clica editar  
  useEffect(() => {
    if (!editingPlanId) {
      return;
    }

    // Se está carregando OUTRO plano, ignora
    if (loadingPlanIdRef.current && loadingPlanIdRef.current !== editingPlanId) {
      return;
    }

    // Marcar como "em carregamento"
    loadingPlanIdRef.current = editingPlanId;

    // Carregar o plano (SEMPRE, sem cache)
    loadPlanForEditing(editingPlanId)
      .then(() => {
        // Se há impressão pendente para este plano, mostrar documento PDF e imprimir
        if (pendingPrintPlanoIdRef.current === editingPlanId) {
          pendingPrintPlanoIdRef.current = null;
          // Mostrar a view de documento PDF estruturado
          setShowDocument(true);
          // Dar tempo para o documento renderizar completamente na tela
          setTimeout(() => { 
            window.print();
          }, 1200);
        }
      })
      .catch((error) => {
        console.error('❌ Erro ao carregar plano:', error);
        pendingPrintPlanoIdRef.current = null;
      })
      .finally(() => {
        // Limpar "em carregamento"
        loadingPlanIdRef.current = null;
      });
  }, [editingPlanId]);

  // Monitorar mudanças no formData e atualizar sectionStatus automaticamente
  useEffect(() => {
    setSectionStatus(prev => ({
      ...prev,
      // Seção 1: Emenda - completa se todos os campos obrigatórios estão preenchidos
      'info-emenda': !!(
        formData.emenda.parlamentar?.trim() &&
        formData.emenda.numero?.trim() &&
        formData.emenda.valor?.trim() &&
        formData.emenda.valor !== '0,00' &&
        formData.emenda.programa?.trim()
      ),
      // Seção 2: Beneficiário - completa se nome e cnpj preenchidos (conta/extrato são opcionais)
      'beneficiario': !!(
        formData.beneficiario.nome?.trim() &&
        formData.beneficiario.cnpj?.trim()
      ),
      // Seção 3: Alinhamento - completa se diretriz e objetivo selecionados
      'alinhamento': !!(
        formData.planejamento.diretrizId &&
        formData.planejamento.objetivoId
      ),
      // Seção 4: Metas Quantitativas - completa se houver pelo menos uma ação
      'metas-quantitativas': formData.acoesServicos.length > 0,
      // Seção 5: Indicadores Qualitativos - OPCIONAL (só completa se preenchido)
      'metas-qualitativas': formData.metasQualitativas.length > 0,
      // Seção 6: Execução Financeira - OPCIONAL (só completa se houver itens)
      'execucao-financeira': formData.naturezasDespesa.length > 0,
      // Seção 7: Finalização - completa se todos os 5 critérios SAES >= mínimo e responsável informado
      'finalizacao': (() => {
        if (!formData.responsavelAssinatura?.trim()) return false;
        const criterios = parseJustCriterios(formData.justificativa);
        return criterios.every(f => f.trim().length >= 100);
      })()
    }));
  }, [formData, minJustificativa]);

  // Sincronizar justCriterios quando formData.justificativa muda externamente (ex: carga do banco)
  useEffect(() => {
    if (justFromUserRef.current) {
      justFromUserRef.current = false;
      return;
    }
    setJustCriterios(parseJustCriterios(formData.justificativa));
  }, [formData.justificativa]);

  const loadPlanForEditing = async (planoId: string) => {
    // Validar planoId
    if (!planoId || typeof planoId !== 'string') {
      console.error('❌ ERRO: planoId inválido:', planoId);
      avisar('Erro: ID do plano inválido');
      return;
    }

    // CRÍTICO: Limpar formData ANTES de carregar novos dados
    // Isso garante que não há dados antigos misturados
    console.log('🗑️ Limpando formData anterior...');
    setFormData(getInitialFormData());
    setSelectedOfertaEmendaId(null);
    setCurrentSelection({ categoria: '', item: '', metas: [''] });
    setCurrentMetaQualitativa({ meta: '', valor: '' });
    setCurrentNatureza({ codigo: '', valor: '' });

    // Nota: O check de carregamento duplicado AGORA é feito no useEffect
    // Aqui apenas executamos o carregamento
    
    try {
      // 1. Buscar plano principal COM VALIDAÇÃO
      const { data: plano, error: planoError } = await supabase
        .from('planos_trabalho')
        .select('*')
        .eq('id', planoId)
        .single();

      if (planoError) {
        console.error('❌ Erro ao buscar plano:', planoError);
        throw new Error(`Plano não encontrado: ${planoError.message}`);
      }
      
      if (!plano || typeof plano !== 'object') {
        throw new Error('Plano inválido ou não encontrado');
      }

      // 2. Buscar metas quantitativas - SÓ O MAIS RECENTE por cada categoria
      let acoes = [];
      try {
        const { data: acoesRaw, error: acoesError } = await supabase
          .from('acoes_servicos')
          .select('*')
          .eq('plano_id', planoId)
          .order('created_at', { ascending: false })
          .limit(10); // Limitar a últimas 10 para evitar muitos dados

        if (acoesError) {
          console.warn('⚠️ Erro ao buscar ações:', acoesError);
        } else {
          // DEDUP: Manter só UMA ação (a mais recente)
          // Se tiver múltiplas, quer dizer que está duplicado
          const acoesUnique: any[] = [];
          const seen = new Set<string>();
          
          for (const a of acoesRaw || []) {
            const key = `${a.categoria}|${a.item}`; // Chave única por categoria+item
            if (!seen.has(key)) {
              acoesUnique.push(a);
              seen.add(key);
            }
          }
          
          acoes = acoesUnique;
          console.log(`✅ Ações/Serviços carregadas DO BANCO: ${acoesRaw?.length || 0} registros (${acoes.length} únicos após dedup)`);
        }
      } catch (e) {
        console.warn('⚠️ Erro ao buscar ações (try/catch):', e);
        acoes = [];
      }

      // 3. Buscar metas qualitativas - SÓ A MAIS RECENTE
      let metas = [];
      try {
        const { data: metasRaw, error: metasError } = await supabase
          .from('metas_qualitativas')
          .select('*')
          .eq('plano_id', planoId)
          .order('created_at', { ascending: false })
          .limit(10);

        if (metasError) {
          console.warn('⚠️ Erro ao buscar metas qualitativas:', metasError);
        } else {
          // DEDUP: Manter só UMA meta por descrição
          const metasUnique: any[] = [];
          const seen = new Set<string>();
          
          for (const m of metasRaw || []) {
            const key = `${m.meta_descricao}`; // Chave única por meta_descricao
            if (!seen.has(key)) {
              metasUnique.push(m);
              seen.add(key);
            }
          }
          
          metas = metasUnique;
          console.log(`✅ Metas Qualitativas carregadas DO BANCO: ${metasRaw?.length || 0} registros (${metas.length} únicos após dedup)`);
        }
      } catch (e) {
        console.warn('⚠️ Erro ao buscar metas qualitativas (try/catch):', e);
        metas = [];
      }

      // 4. Buscar naturezas de despesa - SÓ A MAIS RECENTE
      let naturezas = [];
      try {
        const { data: naturezasRaw, error: naturezasError } = await supabase
          .from('naturezas_despesa_plano')
          .select('*')
          .eq('plano_id', planoId)
          .order('created_at', { ascending: false })
          .limit(10);

        if (naturezasError) {
          console.warn('⚠️ Erro ao buscar naturezas:', naturezasError);
        } else {
          // DEDUP: Manter só UMA natureza por código
          const naturezasUnique: any[] = [];
          const seen = new Set<string>();
          
          for (const n of naturezasRaw || []) {
            const key = `${n.codigo}`; // Chave única por código
            if (!seen.has(key)) {
              naturezasUnique.push(n);
              seen.add(key);
            }
          }
          
          naturezas = naturezasUnique;
          console.log(`✅ Naturezas de Despesa carregadas DO BANCO: ${naturezasRaw?.length || 0} registros (${naturezas.length} únicos após dedup)`);
        }
      } catch (e) {
        console.warn('⚠️ Erro ao buscar naturezas (try/catch):', e);
        naturezas = [];
      }

      // 5. Montar formData GARANTINDO INTEGRIDADE DOS DADOS
      const loadedFormData: FormState = {
        emenda: {
          parlamentar: (plano.parlamentar || '').trim(),
          numero: (plano.numero_emenda || '').trim(),
          valor: (plano.valor_total ? (Number(plano.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : '0,00'),
          valorExtenso: '',
          programa: (plano.programa || '').trim()
        },
        beneficiario: {
          nome: (plano.beneficiario_nome || '').trim(),
          cnes: (plano.cnes || '').trim(),
          cnpj: (plano.beneficiario_cnpj || '').trim(),
          email: (plano.beneficiario_email || '').trim(),
          telefone: (plano.beneficiario_telefone || '').trim(),
          contaBancaria: (plano.conta_bancaria || '').trim(),
          extratoUrl: (plano.extrato_url || '').trim(),
          extratoFilename: (plano.extrato_filename || '').trim()
        },
        planejamento: {
          diretrizId: (plano.diretriz_id || '').trim(),
          objetivoId: (plano.objetivo_id || '').trim(),
          metaIds: Array.isArray(plano.metas_ids) ? [...plano.metas_ids] : []
        },
        acoesServicos: acoes.map((a, index) => {
          if (!a || typeof a !== 'object') {
            console.warn(`⚠️ Ação inválida no índice ${index}:`, a);
            return { categoria: '', item: '', metasQuantitativas: [''], valor: '0,00' };
          }
          return {
            categoria: (a.categoria || '').trim(),
            item: (a.item || '').trim(),
            metasQuantitativas: [(a.meta || '').trim()],
            valor: (a.valor ? (Number(a.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : '0,00')
          };
        }),
        metasQualitativas: metas.map((m, index) => {
          if (!m || typeof m !== 'object') {
            console.warn(`⚠️ Meta qualitativa inválida no índice ${index}:`, m);
            return { meta: '', valor: '0' };
          }
          return {
            meta: (m.meta_descricao || '').trim(),
            valor: String(m.indicador || '0').trim()
          };
        }),
        naturezasDespesa: naturezas.map((n, index) => {
          if (!n || typeof n !== 'object') {
            console.warn(`⚠️ Natureza inválida no índice ${index}:`, n);
            return { codigo: '', valor: '0,00' };
          }
          return {
            codigo: (n.codigo || '').trim(),
            valor: (n.valor ? (Number(n.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : '0,00')
          };
        }),
        justificativa: (plano.justificativa || '').trim(),
        responsavelAssinatura: (plano.responsavel_assinatura || '').trim()
      };
      
      console.log('📝 FormData montado para edição:', {
        emenda: loadedFormData.emenda,
        beneficiario: loadedFormData.beneficiario,
        planejamento: loadedFormData.planejamento,
        acoesServicos: loadedFormData.acoesServicos.length,
        metasQualitativas: loadedFormData.metasQualitativas.length,
        naturezasDespesa: loadedFormData.naturezasDespesa.length
      });
      
      // 6. ATUALIZAR ESTADO COM DADOS VALIDADOS
      // IMPORTANTE: Limpar COMPLETAMENTE o formulário antes de settar novos dados
      // Para evitar duplicação em React StrictMode
      
      setFormData(prev => {
        // Garantir que é um reset completo, não uma merge
        return loadedFormData;
      });
      
      // Limpar inputs temporários para evitar dados residuais
      setCurrentSelection({ categoria: '', item: '', metas: [''] });
      setCurrentMetaQualitativa({ meta: '', valor: '' });
      setCurrentNatureza({ codigo: '', valor: '' });
      
      setPlanoSalvoId(planoId);
      setSelectedOfertaEmendaId(plano.oferta_emenda_id || null);
      if (plano.cnes) {
        setSelectedCnes(String(plano.cnes));
      }
      const savedCopy = JSON.parse(JSON.stringify(loadedFormData));
      setLastSavedFormData(savedCopy);
      setFormHasChanges(false);
      
      // 7. Carregar data de alteração da justificativa (banco ou localStorage)
      const justAltDB = plano.justificativa_alterada_em || null;
      const justAltLocal = (() => { try { return localStorage.getItem(`justificativa_alterada_${planoId}`); } catch(e) { return null; } })();
      setJustificativaAlteradaEm(justAltDB || justAltLocal);
      

    } catch (error: any) {
      console.error('❌ ERRO CRÍTICO ao carregar plano:', error);
      avisar(`Erro ao carregar plano para editar:\n\n${error?.message || 'Erro desconhecido'}`);
      setEditingPlanId(null);
      setFormData(getInitialFormData());
      setPlanoSalvoId(null);
      setSelectedOfertaEmendaId(null);
      // Resetar refs em caso de erro
      loadingPlanIdRef.current = null;
      planLoadCompletedRef.current.clear();
    } finally {
      // SEMPRE LIMPAR A REF ao final
      loadingPlanIdRef.current = null;
    }
  };

  // CARREGAR PLANO PARA VISUALIZAÇÃO (read-only)
  const loadPlanForViewing = async (planoId: string) => {
    setIsLoadingViewPlan(true);
    setShowViewPlanModal(true);
    setViewingPlanData(null);
    try {
      const { data: plano, error: planoErr } = await supabase
        .from('planos_trabalho').select('*').eq('id', planoId).single();
      if (planoErr || !plano) throw new Error(planoErr?.message || 'Plano não encontrado');

      const { data: acoesRaw } = await supabase
        .from('acoes_servicos').select('*').eq('plano_id', planoId).order('created_at', { ascending: false }).limit(50);
      const { data: metasRaw } = await supabase
        .from('metas_qualitativas').select('*').eq('plano_id', planoId).order('created_at', { ascending: false }).limit(20);
      const { data: naturezasRaw } = await supabase
        .from('naturezas_despesa_plano').select('*').eq('plano_id', planoId).order('created_at', { ascending: false }).limit(20);

      // Dedup
      const dedupBy = <T extends Record<string, any>>(arr: T[], key: (item: T) => string): T[] => {
        const seen = new Set<string>();
        return (arr || []).filter(item => { const k = key(item); if (seen.has(k)) return false; seen.add(k); return true; });
      };
      const acoes = dedupBy(acoesRaw || [], a => `${a.categoria}|${a.item}`);
      const metas = dedupBy(metasRaw || [], m => m.meta_descricao);
      const naturezas = dedupBy(naturezasRaw || [], n => n.codigo);

      setViewingPlanData({ plano, acoes, metas, naturezas });
    } catch (err: any) {
      avisar(`Erro ao carregar plano: ${err.message}`);
      setShowViewPlanModal(false);
    } finally {
      setIsLoadingViewPlan(false);
    }
  };

  // DELETA UM PLANO (requer senha de admin)
  const deletePlan = async (planoId: string) => {
    if (!(await confirmar('Excluir este plano de trabalho?\n\nEsta ação não pode ser desfeita.', { rotuloConfirmar: 'Excluir plano' }))) return;
    
    // Verificar se é admin
    if (!isAdmin()) {
      avisar('⚠️ Apenas administradores podem deletar planos.');
      return;
    }

    // Solicitar senha de admin
    const adminPassword = await pedirSenha('Digite a sua senha de administrador para confirmar a exclusão.', { rotuloConfirmar: 'Excluir plano' });
    if (!adminPassword) return;

    try {
      // Validar senha verificando com a API
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sessão expirada');

      // Tentar fazer login novamente com a senha fornecida para validar
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email || '',
        password: adminPassword
      });

      if (authError) {
        avisar('❌ Senha de administrador incorreta.');
        return;
      }

      // Senha correta, proceed com delete
      const plano = planosList.find(p => p.id === planoId);
      if (plano?.pdf_url) {
        await supabase.storage
          .from('planos-trabalho-pdfs')
          .remove([plano.pdf_url]);
      }

      const { error } = await supabase
        .from('planos_trabalho')
        .delete()
        .eq('id', planoId);

      if (error) throw error;
      avisar('✅ Plano deletado com sucesso!');
      await loadPlanos();
    } catch (error: any) {
      console.error('Erro ao deletar:', error);
      avisar(`Erro ao deletar: ${error.message}`);
    }
  };

  // DELETAR VÁRIOS PLANOS (bulk delete com senha de admin)
  const bulkDeletePlanos = async () => {
    if (selectedPlanos.size === 0) {
      avisar('Selecione planos para deletar');
      return;
    }

    const confirmDelete = await confirmar(`Excluir ${selectedPlanos.size} ${selectedPlanos.size === 1 ? 'plano' : 'planos'}?\n\nEsta ação não pode ser desfeita.`, { rotuloConfirmar: 'Excluir' });
    if (!confirmDelete) return;

    // Solicitar senha de admin
    const adminPassword = await pedirSenha('Digite a sua senha de administrador para confirmar a exclusão em massa.', { rotuloConfirmar: 'Excluir planos' });
    if (!adminPassword) return;

    try {
      // Validar senha
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sessão expirada');

      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email || '',
        password: adminPassword
      });

      if (authError) {
        avisar('❌ Senha de administrador incorreta.');
        return;
      }

      // Deletar PDFs e registros
      for (const planoId of selectedPlanos) {
        const plano = planosList.find(p => p.id === planoId);
        if (plano?.pdf_url) {
          await supabase.storage
            .from('planos-trabalho-pdfs')
            .remove([plano.pdf_url]);
        }

        const { error } = await supabase
          .from('planos_trabalho')
          .delete()
          .eq('id', planoId);

        if (error) throw error;
      }

      avisar(`✅ ${selectedPlanos.size} plano(s) deletado(s) com sucesso!`);
      setSelectedPlanos(new Set());
      setShowBulkDeleteModal(false);
      await loadPlanos();
    } catch (error: any) {
      console.error('Erro ao deletar:', error);
      avisar(`Erro ao deletar: ${error.message}`);
    }
  };

  // EXPORTA RELATORIO PROFISSIONAL PARA XLSX (normalizado, um registro por linha)
  const exportToXLSX = async () => {
    if (planosList.length === 0) {
      avisar('Nenhum plano para exportar');
      return;
    }

    try {
      // Biblioteca de planilha carregada só na hora (não pesa no carregamento do sistema)
      const XLSX = await import('xlsx');
      // Buscar dados completos de cada plano
      const fullPlanos = await Promise.all(
        planosList.map(async (p) => {
          // Buscar metas quantitativas (ações/serviços)
          const { data: acoes } = await supabase
            .from('acoes_servicos')
            .select('*')
            .eq('plano_id', p.id);
          
          // Buscar metas qualitativas (indicadores)
          const { data: metas } = await supabase
            .from('metas_qualitativas')
            .select('*')
            .eq('plano_id', p.id);
          
          // Buscar naturezas de despesa
          const { data: naturezas } = await supabase
            .from('naturezas_despesa_plano')
            .select('*')
            .eq('plano_id', p.id);
          
          return {
            ...p,
            acoes: acoes || [],
            metas: metas || [],
            naturezas: naturezas || []
          };
        })
      );

      const toNumber = (value: any) => {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const normalized = value.replace(/\./g, '').replace(',', '.').trim();
          return parseFloat(normalized) || 0;
        }
        return 0;
      };

      const formatCurrency = (value: any) => {
        const num = toNumber(value);
        return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };

      const formatDate = (value: string | null | undefined) => {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('pt-BR');
      };

      const getDiretrizObjetivoMetas = (plano: any) => {
        const diretriz = DIRETRIZES.find(d => d.id === plano.diretriz_id);
        const objetivo = diretriz?.objetivos?.find(o => o.id === plano.objetivo_id);
        const metasIds = Array.isArray(plano.metas_ids) ? plano.metas_ids : [];
        const metasSelecionadas = (objetivo?.metas || [])
          .filter((m: any) => metasIds.includes(m.id))
          .map((m: any) => m.descricao);

        return {
          diretrizNumero: diretriz?.numero || '',
          diretrizTexto: diretriz?.titulo || '',
          objetivoTexto: objetivo?.titulo || '',
          metasTexto: metasSelecionadas.join(' | ')
        };
      };

      const detalhamentoAcoesRows = fullPlanos.flatMap(p => {
        const planejamento = getDiretrizObjetivoMetas(p);
        const acoes = (p.acoes || []).length > 0 ? p.acoes : [null];
        const totalAcoes = (p.acoes || []).reduce((sum: number, a: any) => sum + toNumber(a.valor), 0);
        const totalNaturezas = (p.naturezas || []).reduce((sum: number, n: any) => sum + toNumber(n.valor), 0);

        return acoes.map((a: any) => ({
          'ID do Plano': p.id,
          'Nº Emenda': p.numero_emenda,
          'Parlamentar': p.parlamentar,
          'Programa': p.programa,
          'Beneficiário (Nome)': p.beneficiario_nome,
          'Beneficiário (CNPJ)': p.beneficiario_cnpj,
          'CNES': p.cnes || '',
          'Valor Total do Plano (R$)': toNumber(p.valor_total),
          'Diretriz Nº': planejamento.diretrizNumero,
          'Diretriz': planejamento.diretrizTexto,
          'Objetivo': planejamento.objetivoTexto,
          'Metas Selecionadas': planejamento.metasTexto,
          'Grupo de Ação': a?.categoria || '',
          'Ação Específica': a?.item || '',
          'Meta Quantitativa': a?.meta || '',
          'Valor da Ação (R$)': toNumber(a?.valor),
          'Total Metas Quantitativas do Plano (R$)': totalAcoes,
          'Total Execução Financeira do Plano (R$)': totalNaturezas,
          'Conta Bancária': p.conta_bancaria || '',
          'Criado em': formatDate(p.created_at),
          'Atualizado em': formatDate(p.updated_at)
        }));
      });

      // ========== SHEET 1: PLANOS ==========
      const planosRows = fullPlanos.map(p => {
        const planejamento = getDiretrizObjetivoMetas(p);
        const totalAcoes = (p.acoes || []).reduce((sum: number, a: any) => sum + toNumber(a.valor), 0);
        const totalNaturezas = (p.naturezas || []).reduce((sum: number, n: any) => sum + toNumber(n.valor), 0);

        return {
          'ID do Plano': p.id,
          'Nº Emenda': p.numero_emenda,
          'Parlamentar': p.parlamentar,
          'Programa': p.programa,
          'Beneficiário (Nome)': p.beneficiario_nome,
          'Beneficiário (CNPJ)': p.beneficiario_cnpj,
          'CNES': p.cnes || '',
          'Valor Total (R$)': toNumber(p.valor_total),
          'Diretriz Nº': planejamento.diretrizNumero,
          'Diretriz': planejamento.diretrizTexto,
          'Objetivo': planejamento.objetivoTexto,
          'Metas Selecionadas': planejamento.metasTexto,
          'Total Metas Quantitativas (R$)': totalAcoes,
          'Total Execução Financeira (R$)': totalNaturezas,
          'Conta Bancária': p.conta_bancaria || '',
          'Criado em': formatDate(p.created_at),
          'Atualizado em': formatDate(p.updated_at)
        };
      });

      // ========== SHEET 2: METAS QUANTITATIVAS ==========
      // Uma linha por ação/serviço com dados do plano pai
      const metasQuantitativasRows = fullPlanos.flatMap(p =>
        (p.acoes || []).map((a: any) => ({
          'ID do Plano': p.id,
          'Nº Emenda': p.numero_emenda,
          'Parlamentar': p.parlamentar,
          'Programa': p.programa,
          'Beneficiário': p.beneficiario_nome,
          'Grupo de Ação': a.categoria || '',
          'Ação Específica': a.item || '',
          'Meta Quantitativa': a.meta || '',
          'Valor (R$)': toNumber(a.valor),
          'Criado em': formatDate(a.created_at),
          'Atualizado em': formatDate(a.updated_at)
        }))
      );

      // ========== SHEET 3: INDICADORES QUALITATIVOS ==========
      // Uma linha por indicador com dados do plano pai
      const indicadoresRows = fullPlanos.flatMap(p =>
        (p.metas || []).map((m: any) => ({
          'ID do Plano': p.id,
          'Nº Emenda': p.numero_emenda,
          'Parlamentar': p.parlamentar,
          'Programa': p.programa,
          'Beneficiário': p.beneficiario_nome,
          'Descrição da Meta': m.meta_descricao || m.meta || '',
          'Indicador/Referência': m.indicador || m.valor || '',
          'Criado em': formatDate(m.created_at),
          'Atualizado em': formatDate(m.updated_at)
        }))
      );

      // ========== SHEET 4: EXECUÇÃO FINANCEIRA ==========
      // Uma linha por natureza/despesa com dados do plano pai
      const execucaoFinanceiraRows = fullPlanos.flatMap(p =>
        (p.naturezas || []).map((n: any) => ({
          'ID do Plano': p.id,
          'Nº Emenda': p.numero_emenda,
          'Parlamentar': p.parlamentar,
          'Programa': p.programa,
          'Beneficiário': p.beneficiario_nome,
          'Código da Natureza': n.codigo || '',
          'Descrição da Natureza': n.descricao || '',
          'Valor (R$)': toNumber(n.valor),
          'Banco': p.banco || '',
          'Agência': p.agencia || '',
          'Conta': p.conta_bancaria || '',
          'Criado em': formatDate(n.created_at),
          'Atualizado em': formatDate(n.updated_at)
        }))
      );

      // ========== CRIAR WORKBOOK ==========
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detalhamentoAcoesRows), 'Relatorio Detalhado');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(planosRows), 'Planos');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(metasQuantitativasRows), 'Metas Quantitativas');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(indicadoresRows), 'Indicadores Qualitativos');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(execucaoFinanceiraRows), 'Execução Financeira');

      // Ajustar largura das colunas
      const setColWidth = (ws: any) => {
        const cols: any[] = [];
        const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
        for (let C = range.s.c; C <= range.e.c; ++C) {
          cols.push({ wch: 20 });
        }
        ws['!cols'] = cols;
      };

      setColWidth(wb.Sheets['Relatorio Detalhado']);
      setColWidth(wb.Sheets['Planos']);
      setColWidth(wb.Sheets['Metas Quantitativas']);
      setColWidth(wb.Sheets['Indicadores Qualitativos']);
      setColWidth(wb.Sheets['Execução Financeira']);

      const dataRef = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `relatorio-meus-planos-${dataRef}.xlsx`);
      avisar('✅ Relatório XLSX profissional exportado com sucesso!\n\nAgora você pode criar tabelas dinâmicas no Excel com os dados normalizados.');
    } catch (error: any) {
      avisar(`Erro ao exportar relatório XLSX: ${error.message}`);
    }
  };

  const parseCurrency = (val: string) => {
    return parseFloat(val.replace(/\./g, '').replace(',', '.')) || 0;
  };



  // ======== COMPARAÇÃO DE DADOS PARA EVITAR DUPLICAÇÃO ========
  const hasDataChanged = async (): Promise<boolean> => {
    // Se não há plano salvo, não há mudanças a comparar
    if (!planoSalvoId) {
      return true; // É novo plano
    }

    try {
      // Buscar o plano salvo
      const { data: plano, error } = await supabase
        .from('planos_trabalho')
        .select('*, acoes_servicos(*), metas_qualitativas(*), naturezas_despesa_plano(*)')
        .eq('id', planoSalvoId)
        .single();

      if (error || !plano) return true; // Se não encontrar, considerar como novo

      // Comparar dados principales
      const mainDataSame =
        plano.parlamentar === formData.emenda.parlamentar &&
        plano.numero_emenda === formData.emenda.numero &&
        plano.valor_total === parseCurrency(formData.emenda.valor) &&
        plano.programa === formData.emenda.programa &&
        plano.beneficiario_nome === formData.beneficiario.nome &&
        plano.beneficiario_cnpj === formData.beneficiario.cnpj &&
        plano.justificativa === formData.justificativa;

      // Comparar quantidades de dados relacionados
      const acoesSame = plano.acoes_servicos?.length === formData.acoesServicos.length;
      const metasQualitSame = plano.metas_qualitativas?.length === formData.metasQualitativas.length;
      const naturezasSame = plano.naturezas_despesa_plano?.length === formData.naturezasDespesa.length;

      // Se tudo é igual, não há mudanças
      return !(mainDataSame && acoesSame && metasQualitSame && naturezasSame);
    } catch (error) {
      console.error("Erro ao comparar dados:", error);
      return true; // Em caso de erro, considerar como potencialmente novo
    }
  };

  // ======== AUTO SAVE ========
  const handleAutoSave = async () => {
    // Proteção sincronizada contra dupla execução
    if (isSavingRef.current) {
      return;
    }

    if (!isAuthenticated || !currentUser) return;

    isSavingRef.current = true;
    setAutoSaveStatus('saving');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Se já existe plano, atualizar. Se não, criar
      if (planoSalvoId) {
        // Atualizar plano existente
        const { error } = await supabase
          .from('planos_trabalho')
          .update({
            parlamentar: formData.emenda.parlamentar,
            numero_emenda: formData.emenda.numero,
            valor_total: parseCurrency(formData.emenda.valor),
            programa: formData.emenda.programa,
            beneficiario_nome: formData.beneficiario.nome,
            beneficiario_cnpj: formData.beneficiario.cnpj,
            cnes: formData.beneficiario.cnes || null,
            conta_bancaria: formData.beneficiario.contaBancaria || null,
            extrato_url: formData.beneficiario.extratoUrl || null,
            extrato_filename: formData.beneficiario.extratoFilename || null,
            justificativa: formData.justificativa,
            responsavel_assinatura: formData.responsavelAssinatura,
            created_by_name: currentUser?.name || null,
            created_by_email: currentUser?.username || user.email || null,
            oferta_emenda_id: selectedOfertaEmendaId,
            updated_at: new Date().toISOString()
          })
          .eq('id', planoSalvoId);

        if (error) throw error;

        // Deletar e reinserir dados relacionados (para evitar duplicação)
        await supabase.from('acoes_servicos').delete().eq('plano_id', planoSalvoId);
        await supabase.from('metas_qualitativas').delete().eq('plano_id', planoSalvoId);
        await supabase.from('naturezas_despesa_plano').delete().eq('plano_id', planoSalvoId);

        // Reinserir dados relacionados
        if (formData.acoesServicos.length > 0) {
          const acoesData = formData.acoesServicos.map(a => ({
            plano_id: planoSalvoId,
            categoria: a.categoria,
            item: a.item,
            meta: a.metasQuantitativas[0],
            valor: parseCurrency(a.valor),
            created_by: user.id
          }));
          await supabase.from('acoes_servicos').insert(acoesData);
        }

        if (formData.metasQualitativas.length > 0) {
          const qualData = formData.metasQualitativas.map(q => ({
            plano_id: planoSalvoId,
            meta_descricao: q.meta,
            indicador: q.valor,
            created_by: user.id
          }));
          await supabase.from('metas_qualitativas').insert(qualData);
        }

        if (formData.naturezasDespesa.length > 0) {
          const natData = formData.naturezasDespesa.map(n => ({
            plano_id: planoSalvoId,
            codigo: n.codigo,
            valor: parseCurrency(n.valor),
            created_by: user.id
          }));
          await supabase.from('naturezas_despesa_plano').insert(natData);
        }
      } else {
        // Criar novo plano
        const { data: plano, error: planoError } = await supabase
          .from('planos_trabalho')
          .insert([{
            parlamentar: formData.emenda.parlamentar,
            numero_emenda: formData.emenda.numero,
            valor_total: parseCurrency(formData.emenda.valor),
            programa: formData.emenda.programa,
            beneficiario_nome: formData.beneficiario.nome,
            beneficiario_cnpj: formData.beneficiario.cnpj,
            cnes: formData.beneficiario.cnes || null,
            conta_bancaria: formData.beneficiario.contaBancaria || null,
            extrato_url: formData.beneficiario.extratoUrl || null,
            extrato_filename: formData.beneficiario.extratoFilename || null,
            justificativa: formData.justificativa,
            responsavel_assinatura: formData.responsavelAssinatura,
            pdf_url: null,
            created_by: user.id,
            created_by_name: currentUser?.name || null,
            created_by_email: currentUser?.username || user.email || null,
            oferta_emenda_id: selectedOfertaEmendaId
          }])
          .select()
          .single();

        if (planoError) throw planoError;
        if (plano) {
          setPlanoSalvoId(plano.id);

          // Inserir dados relacionados (ações, metas, etc)
          if (formData.acoesServicos.length > 0) {
            const acoesData = formData.acoesServicos.map(a => ({
              plano_id: plano.id,
              categoria: a.categoria,
              item: a.item,
              meta: a.metasQuantitativas[0],
              valor: parseCurrency(a.valor),
              created_by: user.id
            }));
            await supabase.from('acoes_servicos').insert(acoesData);
          }

          if (formData.metasQualitativas.length > 0) {
            const qualData = formData.metasQualitativas.map(q => ({
              plano_id: plano.id,
              meta_descricao: q.meta,
              indicador: q.valor,
              created_by: user.id
            }));
            await supabase.from('metas_qualitativas').insert(qualData);
          }

          if (formData.naturezasDespesa.length > 0) {
            const natData = formData.naturezasDespesa.map(n => ({
              plano_id: plano.id,
              codigo: n.codigo,
              valor: parseCurrency(n.valor),
              created_by: user.id
            }));
            await supabase.from('naturezas_despesa_plano').insert(natData);
          }
        }
      }

      setAutoSaveStatus('saved');
      setLastAutoSaveTime(new Date());
      
      // Voltar ao idle após 2 segundos
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      console.error("❌ Erro no autosave:", error);
      setAutoSaveStatus('idle');
    } finally {
      isSavingRef.current = false; // ← Reset ref após auto-save
    }
  };

  // Auto-save desativado - salvar apenas ao enviar
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     if (isAuthenticated && currentView === 'new') {
  //       handleAutoSave();
  //     }
  //   }, 3000); // Salvar 3 segundos após a última mudança
  //
  //   return () => clearTimeout(timer);
  // }, [formData, isAuthenticated, currentView]);

  // Helper: marca emenda como utilizada via RPC (SECURITY DEFINER) com fallback para update direto
  const marcarEmendaUtilizada = async (emendaId: string, planoId: string): Promise<boolean> => {
    // Tentativa 1: RPC com SECURITY DEFINER (ignora RLS)
    const { data: rpcOk, error: rpcErr } = await supabase.rpc('mark_emenda_utilizada', {
      p_emenda_id: emendaId,
      p_plano_id: planoId
    });
    if (!rpcErr) {
      if (rpcOk) {
        console.log('✅ Emenda marcada via RPC:', emendaId);
        return true;
      }
      console.warn('⚠️ RPC mark_emenda_utilizada retornou false para emenda:', emendaId);
      return false;
    }

    // Tentativa 2: fallback para update direto (caso o RPC ainda não exista no banco)
    console.warn('⚠️ RPC indisponível, tentando update direto:', rpcErr.message);
    const { error: updErr } = await supabase
      .from('emendas_disponibilizadas')
      .update({
        status: 'utilizada',
        plano_id: planoId,
        utilizada_em: new Date().toISOString()
      })
      .eq('id', emendaId);

    if (updErr) {
      console.error('❌ Falha no update direto da emenda:', updErr.message);
      return false;
    }
    console.log('✅ Emenda marcada via update direto:', emendaId);
    return true;
  };

  const handleFinalSend = async () => {
    // Proteção MÁXIMA contra duplo clique/envio (usando useRef em vez de useState)
    if (isSavingRef.current) {
      avisar("⚠️ BLOQUEADO: Operação já em andamento! Espere...");
      return null;
    }

    isSavingRef.current = true;
    setIsSending(true);

    try {
      // VALIDAÇÃO 1: Validar TODOS os campos obrigatórios PRIMEIRO
      const validation = validateRequiredFields();
      if (!validation.isValid) {
        const missingList = validation.missingFields.map((field, idx) => `${idx + 1}. ${field}`).join('\n');
        avisar(
          `⚠️ FORMULÁRIO INCOMPLETO!\n\n` +
          `Os campos obrigatórios abaixo devem ser preenchidos:\n\n${missingList}\n\n` +
          `Por favor, complete todos os campos indicados antes de salvar o plano.`
        );
        setIsSending(false);
        isSavingRef.current = false; // ← Reset ref on validation error
        return null;
      }

      // VALIDAÇÃO 2: Total de Naturezas de Despesa não pode ultrapassar Total de Metas Quantitativas
      const totalMetasQuantitativas = formData.acoesServicos.reduce((sum, acao) => sum + parseCurrency(acao.valor), 0);
      const totalDespesas = formData.naturezasDespesa.reduce((sum, despesa) => sum + parseCurrency(despesa.valor), 0);

      // VALIDAÇÃO: Limitar a 50% do valor do recurso para Portaria 10.169, Grupo de Ação VI
      const isPortaria10169 = formData.emenda.programa && formData.emenda.programa.includes('PORTARIA 10.169');
      const hasGrupoAcaoVI = formData.acoesServicos.some(acao => acao.categoria && acao.categoria.includes('VI - CUSTEIO DE OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE'));
      if (isPortaria10169 && hasGrupoAcaoVI) {
        const totalGrupoVI = formData.acoesServicos
          .filter(acao => acao.categoria && acao.categoria.includes('VI - CUSTEIO DE OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE'))
          .reduce((sum, acao) => sum + parseCurrency(acao.valor), 0);
        const valorRecurso = parseCurrency(formData.emenda.valor);
        const limiteGrupoVI = valorRecurso * 0.5;
        if (totalGrupoVI > limiteGrupoVI) {
          const diferenca = (totalGrupoVI - limiteGrupoVI).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          avisar(`⚠️ ERRO DE VALIDAÇÃO!\n\nPara ações do Grupo VI - CUSTEIO DE OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE, o valor total não pode ultrapassar 50% do valor do recurso (R$ ${limiteGrupoVI.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}).\n\nO valor informado foi R$ ${totalGrupoVI.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, excedendo o limite em R$ ${diferenca}.\n\nAjuste os valores antes de salvar.`);
          setIsSending(false);
          isSavingRef.current = false;
          return null;
        }
      }

      // VALIDAÇÃO: Limitar a 70% do valor do recurso para Portaria 10.352 CUSTEIO, OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE
      const isPortaria10352Custeio = formData.emenda.programa === PROGRAMA_EMENDA_COLETIVA_10352_CUSTEIO;
      const hasOutrasAcoes = formData.acoesServicos.some(acao => acao.categoria === 'OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE');
      if (isPortaria10352Custeio && hasOutrasAcoes) {
        const totalOutrasAcoes = formData.acoesServicos
          .filter(acao => acao.categoria === 'OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE')
          .reduce((sum, acao) => sum + parseCurrency(acao.valor), 0);
        const valorRecurso = parseCurrency(formData.emenda.valor);
        const limiteOutrasAcoes = valorRecurso * 0.7;
        if (totalOutrasAcoes > limiteOutrasAcoes) {
          const diferenca = (totalOutrasAcoes - limiteOutrasAcoes).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          avisar(`⚠️ ERRO DE VALIDAÇÃO!\n\nPara ações de OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE, o valor total não pode ultrapassar 70% do valor do recurso (R$ ${limiteOutrasAcoes.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}).\n\nO valor informado foi R$ ${totalOutrasAcoes.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, excedendo o limite em R$ ${diferenca}.\n\nAjuste os valores antes de salvar.`);
          setIsSending(false);
          isSavingRef.current = false;
          return null;
        }
      }

      if (totalDespesas > totalMetasQuantitativas) {
        const diferenca = (totalDespesas - totalMetasQuantitativas).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        avisar(`⚠️ ERRO DE VALIDAÇÃO!\n\nO total de Naturezas de Despesa (R$ ${totalDespesas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) ultrapassa o Total de Metas Quantitativas (R$ ${totalMetasQuantitativas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) em R$ ${diferenca}.\n\nAjuste os valores de despesa antes de salvar.`);
        setIsSending(false);
        isSavingRef.current = false; // ← Reset ref on validation error
        return null;
      }

      // VALIDAÇÃO 3: Se já tem plano salvo E não há mudanças = não fazer nada
      // (mas ainda marcar emenda como utilizada se pendente)
      if (planoSalvoId && lastSavedFormData) {
        const currentJson = JSON.stringify(formData);
        const savedJson = JSON.stringify(lastSavedFormData);
        if (currentJson === savedJson) {
          // Mesmo sem mudanças no formulário, marcar emenda como utilizada se ainda pendente
          if (selectedOfertaEmendaId) {
            const emendaIdUsada = selectedOfertaEmendaId;
            await marcarEmendaUtilizada(emendaIdUsada, planoSalvoId);
            setSelectedOfertaEmendaId(null);
            setEmendasDisponiveisUsuario(prev => prev.filter(e => e.id !== emendaIdUsada));
          }
          avisar('⚠️ Nenhuma mudança detectada!\n\nO plano não foi alterado desde o último salvamento.');
          setIsSending(false);
          isSavingRef.current = false;
          return planoSalvoId;
        }
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Sessão expirada. Faça login novamente.");

      // LÓGICA SIMPLES: SE TEM planoSalvoId, é UPDATE. SENÃO, é CREATE
      if (planoSalvoId) {
        
        // Buscar o edit_count atual e justificativa para comparar
        const { data: currentPlano } = await supabase
          .from('planos_trabalho')
          .select('edit_count, justificativa')
          .eq('id', planoSalvoId)
          .single();
        
        const newEditCount = (currentPlano?.edit_count || 0) + 1;
        const justificativaMudou = (currentPlano?.justificativa || '') !== formData.justificativa;
        
        // Atualizar plano existente
        const valorTotalUpdate = parseCurrency(formData.emenda.valor);
        const { error: updateError } = await supabase
          .from('planos_trabalho')
          .update({
            parlamentar: formData.emenda.parlamentar,
            numero_emenda: formData.emenda.numero,
            valor_total: valorTotalUpdate,
            programa: formData.emenda.programa,
            beneficiario_nome: formData.beneficiario.nome,
            beneficiario_cnpj: formData.beneficiario.cnpj,
            beneficiario_email: formData.beneficiario.email || null,
            beneficiario_telefone: formData.beneficiario.telefone || null,
            cnes: formData.beneficiario.cnes || null,
            conta_bancaria: formData.beneficiario.contaBancaria || null,
            extrato_url: formData.beneficiario.extratoUrl || null,
            extrato_filename: formData.beneficiario.extratoFilename || null,
            justificativa: formData.justificativa,
            responsavel_assinatura: formData.responsavelAssinatura,
            diretriz_id: formData.planejamento.diretrizId || null,
            objetivo_id: formData.planejamento.objetivoId || null,
            metas_ids: formData.planejamento.metaIds,
            updated_at: new Date().toISOString(),
            edit_count: newEditCount,
            last_edited_at: new Date().toISOString(),
            last_edited_by: user.id,
            created_by_name: currentUser?.name || null,
            created_by_email: currentUser?.username || user.email || null,
            oferta_emenda_id: selectedOfertaEmendaId
          })
          .eq('id', planoSalvoId);

        if (updateError) {
          console.error("❌ ERRO ao atualizar plano:", updateError);
          avisar(`❌ ERRO ao atualizar plano:\n${updateError.message}`);
          setIsSending(false);
          isSavingRef.current = false;
          return null;
        }
        
        console.log("✅ Plano atualizado (edição #" + newEditCount + ")");
        
        // Atualizar estado local + localStorage se justificativa mudou
        if (justificativaMudou) {
          const novaData = new Date().toISOString();
          setJustificativaAlteradaEm(novaData);
          try { localStorage.setItem(`justificativa_alterada_${planoSalvoId}`, novaData); } catch(e) {}
          // Tentar salvar na coluna do banco (pode falhar se schema cache não atualizou)
          try {
            await supabase.from('planos_trabalho').update({ justificativa_alterada_em: novaData }).eq('id', planoSalvoId);
          } catch(e) { console.warn('⚠️ Não foi possível salvar justificativa_alterada_em no banco:', e); }
        }
        
        // ⚠️ Usar RPC para DELETE+INSERT atômico (resolve problema de RLS)
        console.log("📝 Substituindo dados relacionados via RPC...");
        
        const acoesJson = formData.acoesServicos.map(a => ({
          categoria: a.categoria,
          item: a.item,
          meta: a.metasQuantitativas[0],
          valor: parseCurrency(a.valor),
          created_by: user.id
        }));

        const metasQualJson = formData.metasQualitativas.map(q => ({
          meta_descricao: q.meta,
          indicador: q.valor,
          created_by: user.id
        }));

        const naturezasJson = formData.naturezasDespesa.map(n => ({
          codigo: n.codigo,
          valor: parseCurrency(n.valor),
          created_by: user.id
        }));

        const { data: rpcResult, error: rpcError } = await supabase.rpc('replace_plano_children', {
          p_plano_id: planoSalvoId,
          p_acoes: acoesJson,
          p_metas_qual: metasQualJson,
          p_naturezas: naturezasJson
        });

        if (rpcError) {
          console.warn("⚠️ RPC replace_plano_children falhou, usando fallback:", rpcError.message);
          // Fallback: inserir sem deletar (comportamento antigo)
          if (formData.acoesServicos.length > 0) {
            const acoesData = formData.acoesServicos.map(a => ({
              plano_id: planoSalvoId,
              categoria: a.categoria,
              item: a.item,
              meta: a.metasQuantitativas[0],
              valor: parseCurrency(a.valor),
              created_by: user.id
            }));
            await supabase.from('acoes_servicos').insert(acoesData);
          }
          if (formData.metasQualitativas.length > 0) {
            const qualData = formData.metasQualitativas.map(q => ({
              plano_id: planoSalvoId,
              meta_descricao: q.meta,
              indicador: q.valor,
              created_by: user.id
            }));
            await supabase.from('metas_qualitativas').insert(qualData);
          }
          if (formData.naturezasDespesa.length > 0) {
            const natData = formData.naturezasDespesa.map(n => ({
              plano_id: planoSalvoId,
              codigo: n.codigo,
              valor: parseCurrency(n.valor),
              created_by: user.id
            }));
            await supabase.from('naturezas_despesa_plano').insert(natData);
          }
        } else {
          console.log("✅ Dados relacionados substituídos via RPC:", rpcResult);
        }
        if (selectedOfertaEmendaId) {
          const emendaIdUsada = selectedOfertaEmendaId;
          await marcarEmendaUtilizada(emendaIdUsada, planoSalvoId);
          setSelectedOfertaEmendaId(null);
          setEmendasDisponiveisUsuario(prev => prev.filter(e => e.id !== emendaIdUsada));
        }
        
        setLastSavedFormData(JSON.parse(JSON.stringify(formData)));
        setFormHasChanges(false);
        
        // Recarregar lista de planos para atualizar contagem de edições
        await loadPlanos();
        
        setIsSending(false);
        return planoSalvoId;
      } else {
        // ====== CREATE NOVO PLANO ======
      const valorParsado = parseCurrency(formData.emenda.valor);
      const { data: plano, error: planoError } = await supabase
        .from('planos_trabalho')
        .insert([{
          parlamentar: formData.emenda.parlamentar,
          numero_emenda: formData.emenda.numero,
          valor_total: valorParsado,
          programa: formData.emenda.programa,
          beneficiario_nome: formData.beneficiario.nome,
          beneficiario_cnpj: formData.beneficiario.cnpj,
          beneficiario_email: formData.beneficiario.email || null,
          beneficiario_telefone: formData.beneficiario.telefone || null,
          cnes: formData.beneficiario.cnes || null,
          conta_bancaria: formData.beneficiario.contaBancaria || null,
          extrato_url: formData.beneficiario.extratoUrl || null,
          extrato_filename: formData.beneficiario.extratoFilename || null,
          justificativa: formData.justificativa,
          responsavel_assinatura: formData.responsavelAssinatura,
          diretriz_id: formData.planejamento.diretrizId || null,
          objetivo_id: formData.planejamento.objetivoId || null,
          metas_ids: formData.planejamento.metaIds,
          pdf_url: null,
          created_by: user.id,
          created_by_name: currentUser?.name || null,
          created_by_email: currentUser?.username || user.email || null,
          oferta_emenda_id: selectedOfertaEmendaId
        }])
        .select()
        .single();

      if (planoError) {
        console.error("❌ Erro ao inserir plano:", planoError);
        console.error("❌ DETALHES DO ERRO:", JSON.stringify(planoError, null, 2));
        console.error("❌ code:", planoError.code, "message:", planoError.message, "details:", planoError.details, "hint:", planoError.hint);
        throw planoError;
      }

      // Usar RPC para INSERT atômico (SECURITY DEFINER - bypassa RLS)
      const acoesJsonCreate = formData.acoesServicos.map(a => ({
        categoria: a.categoria,
        item: a.item,
        meta: a.metasQuantitativas[0],
        valor: parseCurrency(a.valor),
        created_by: user.id
      }));
      const metasQualJsonCreate = formData.metasQualitativas.map(q => ({
        meta_descricao: q.meta,
        indicador: q.valor,
        created_by: user.id
      }));
      const naturezasJsonCreate = formData.naturezasDespesa.map(n => ({
        codigo: n.codigo,
        valor: parseCurrency(n.valor),
        created_by: user.id
      }));

      const { error: rpcCreateError } = await supabase.rpc('replace_plano_children', {
        p_plano_id: plano.id,
        p_acoes: acoesJsonCreate,
        p_metas_qual: metasQualJsonCreate,
        p_naturezas: naturezasJsonCreate
      });

      if (rpcCreateError) {
        console.warn("⚠️ RPC replace_plano_children falhou no create, usando fallback:", rpcCreateError.message);
        // Fallback: inserção direta
        if (formData.acoesServicos.length > 0) {
          const acoesData = formData.acoesServicos.map(a => ({
            plano_id: plano.id,
            categoria: a.categoria,
            item: a.item,
            meta: a.metasQuantitativas[0],
            valor: parseCurrency(a.valor),
            created_by: user.id
          }));
          const { error: acoesError } = await supabase.from('acoes_servicos').insert(acoesData);
          if (acoesError) {
            console.error("❌ Erro ao inserir ações:", acoesError);
            throw acoesError;
          }
        }
        if (formData.metasQualitativas.length > 0) {
          const qualData = formData.metasQualitativas.map(q => ({
            plano_id: plano.id,
            meta_descricao: q.meta,
            indicador: q.valor,
            created_by: user.id
          }));
          const { error: qualError } = await supabase.from('metas_qualitativas').insert(qualData);
          if (qualError) {
            console.error("❌ Erro ao inserir metas qualitativas:", qualError);
            throw qualError;
          }
        }
        if (formData.naturezasDespesa.length > 0) {
          const natData = formData.naturezasDespesa.map(n => ({
            plano_id: plano.id,
            codigo: n.codigo,
            valor: parseCurrency(n.valor),
            created_by: user.id
          }));
          const { error: natError } = await supabase.from('naturezas_despesa_plano').insert(natData);
          if (natError) {
            console.error("❌ Erro ao inserir naturezas:", natError);
            throw natError;
          }
        }
      }

      setPlanoSalvoId(plano.id);
      
      if (selectedOfertaEmendaId) {
        const emendaIdUsada = selectedOfertaEmendaId;
        await marcarEmendaUtilizada(emendaIdUsada, plano.id);
        setSelectedOfertaEmendaId(null);
        setEmendasDisponiveisUsuario(prev => prev.filter(e => e.id !== emendaIdUsada));
      }

      
      // Atualizar lastSavedFormData após salvar com sucesso
      const savedCopy = JSON.parse(JSON.stringify(formData));
      setLastSavedFormData(savedCopy);
      // Resetar flag de mudanças após sucesso
      setFormHasChanges(false);
      console.log(`📌 Plano ${plano.id} salvo. lastSavedFormData atualizado.`);
      
      // Recarregar lista de planos para atualizar contagem de edições e datas
      await loadPlanos();
      
      avisar('✅ PLANO NOVO CRIADO E SALVO COM SUCESSO!');
      setShowEmailModal(true);
      // ← NÃO reseta isSavingRef aqui! Reset apenas no finally block
      return plano.id; // Retornar ID para uso síncrono - SAIR AGORA!
      }
    } catch (error: any) {
      console.error("❌ ERRO COMPLETO:", error);
      const errorMsg = error?.message || error?.toString() || "Erro desconhecido";
      avisar(`⚠️ ERRO:\n\n${errorMsg}\n\nVerifique o console (F12) para mais detalhes.`);
      return null; // Retornar null em caso de erro
    } finally {
      setIsSending(false);
      isSavingRef.current = false; // ← NOVO: Libera guard para próxima tentativa
    }
  };

  const handleSendEmail = async () => {
    // Abrir email para SES-SP
    handleSendToSES();
  };

  // Validação de campos obrigatórios
  const validateRequiredFields = (): { isValid: boolean; missingFields: string[] } => {
    const missingFields: string[] = [];

    // IDENTIFICAÇÃO GERAL - obrigatório
    if (!formData.emenda.parlamentar?.trim()) missingFields.push('Parlamentar Autor');
    if (!formData.emenda.numero?.trim()) missingFields.push('Número da Emenda');
    if (!formData.emenda.valor?.trim() || formData.emenda.valor === '0,00') missingFields.push('Valor da Emenda');
    if (!formData.emenda.programa?.trim()) missingFields.push('Programa de Saúde');

    // DADOS DO BENEFICIÁRIO - obrigatório
    if (!formData.beneficiario.nome?.trim()) missingFields.push('Nome do Beneficiário');
    if (!formData.beneficiario.cnpj?.trim()) missingFields.push('CNPJ do Beneficiário');
    // Conta bancária e extrato bancário não são mais obrigatórios

    // ALINHAMENTO ESTRATÉGICO - obrigatório
    if (!formData.planejamento.diretrizId) missingFields.push('Diretriz Estratégica');
    if (!formData.planejamento.objetivoId) missingFields.push('Objetivo Específico');

    // METAS QUANTITATIVAS - obrigatório (pelo menos um item)
    if (formData.acoesServicos.length === 0) {
      missingFields.push('Metas Quantitativas — adicione pelo menos uma Ação/Serviço');
    } else {
      // Verificar se cada item está completamente preenchido
      formData.acoesServicos.forEach((acao, idx) => {
        const n = idx + 1;
        if (!acao.categoria?.trim()) missingFields.push(`Ação ${n}: Grupo de Ação não selecionado`);
        if (!acao.item?.trim()) missingFields.push(`Ação ${n}: Ação Específica não selecionada`);
        if (!acao.metasQuantitativas[0]?.trim()) missingFields.push(`Ação ${n}: Meta Quantitativa não informada`);
        if (!acao.valor?.trim() || acao.valor === '0,00') missingFields.push(`Ação ${n}: Valor da ação não informado`);
      });
    }

    // INDICADORES QUALITATIVOS - OPCIONAL (não é obrigatório)
    // Removido: validação de requiredFields

    // EXECUÇÃO FINANCEIRA - obrigatório (pelo menos um item)
    if (formData.naturezasDespesa.length === 0) {
      missingFields.push('Execução Financeira — adicione pelo menos uma Natureza de Despesa');
    } else {
      formData.naturezasDespesa.forEach((nd, idx) => {
        const n = idx + 1;
        if (!nd.codigo?.trim()) missingFields.push(`Natureza de Despesa ${n}: Código não selecionado`);
        if (!nd.valor?.trim() || nd.valor === '0,00') missingFields.push(`Natureza de Despesa ${n}: Valor não informado`);
      });
    }

    // JUSTIFICATIVA TÉCNICA - 5 critérios SAES, mínimo de 100 caracteres cada
    {
      const criterios = parseJustCriterios(formData.justificativa);
      criterios.forEach((f, i) => {
        if (f.trim().length < 100)
          missingFields.push(`Justificativa — ${SAES_LABELS_SHORT[i]}: mínimo 100 caracteres (atual: ${f.trim().length})`);
      });
    }

    // RESPONSÁVEL PELA ASSINATURA - obrigatório
    if (!formData.responsavelAssinatura?.trim()) missingFields.push('Responsável pela Assinatura');

    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  };

  // Registra evento de visualização/download de PDF no banco de dados
  const recordPdfViewEvent = async (planoId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn("⚠️ Não foi possível registrar evento PDF: usuário não autenticado");
        return;
      }

      // Buscar dados do plano para registrar no histórico
      const { data: plano, error: fetchError } = await supabase
        .from('planos_trabalho')
        .select('numero_emenda, parlamentar, valor_total')
        .eq('id', planoId)
        .single();

      if (fetchError) {
        console.error("❌ Erro ao buscar dados do plano:", fetchError);
        return;
      }

      // Registrar no histórico
      const { error: insertError } = await supabase
        .from('pdf_download_history')
        .insert({
          plano_id: planoId,
          user_id: user.id,
          user_email: user.email,
          user_name: currentUser?.name || 'Usuário',
          numero_emenda: plano.numero_emenda,
          parlamentar: plano.parlamentar,
          valor_total: plano.valor_total,
          action_type: 'view_pdf'
        });

      if (insertError) {
        console.error("❌ Erro ao registrar evento PDF:", insertError);
      } else {
        console.log("✅ Evento de visualização de PDF registrado com sucesso!");
      }
    } catch (error: any) {
      console.error("❌ Erro ao registrar evento de PDF:", error);
      // Não interrompe o fluxo se falhar ao registrar
    }
  };

  // ======== UPLOAD E COMPRESSÃO DE EXTRATO BANCÁRIO ========
  const compressImage = (file: File, maxWidth: number = 900, quality: number = 0.5): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Redimensionar para largura máxima
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas não suportado')); return; }
        ctx.drawImage(img, 0, 0, width, height);
        
        // Tentar WebP primeiro com qualidade baixa
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Falha ao comprimir imagem'));
          },
          'image/webp',
          quality
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Falha ao carregar imagem')); };
      img.src = url;
    });
  };

  const compressPDF = async (file: File): Promise<Blob> => {
    const { PDFDocument } = await import('pdf-lib');
    const arrayBuffer = await file.arrayBuffer();
    
    // Carregar o PDF original
    const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    
    // Copiar para um novo documento limpo (remove metadados, objetos órfãos, etc.)
    const compressedDoc = await PDFDocument.create();
    const pages = await compressedDoc.copyPages(pdfDoc, pdfDoc.getPageIndices());
    pages.forEach(page => compressedDoc.addPage(page));
    
    // Salvar com otimizações
    const compressedBytes = await compressedDoc.save({
      useObjectStreams: true,    // Comprime streams de objetos
      addDefaultPage: false,
      objectsPerTick: 100,
    });
    
    // Se o PDF comprimido ainda for grande, converter páginas para imagens comprimidas
    if (compressedBytes.length > 500 * 1024) {
      // Tentar renderizar via canvas para máxima compressão
      try {
        return await pdfToCompressedImage(file);
      } catch {
        // Se falhar a conversão, retornar o PDF otimizado
        return new Blob([compressedBytes], { type: 'application/pdf' });
      }
    }
    
    return new Blob([compressedBytes], { type: 'application/pdf' });
  };

  const pdfToCompressedImage = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      // Usar iframe oculto para renderizar o PDF e capturar como imagem
      const url = URL.createObjectURL(file);
      const canvas = document.createElement('canvas');
      const img = new Image();
      
      // Para PDFs, criar uma versão simplificada em imagem usando embed
      // Fallback: retornar o arquivo original se não conseguir converter
      const reader = new FileReader();
      reader.onload = () => {
        // Criar um object URL do PDF e tentar renderizar
        const embed = document.createElement('embed');
        embed.src = url;
        embed.type = 'application/pdf';
        
        // Timeout - se não conseguir em 3s, rejeitar
        const timeout = setTimeout(() => {
          URL.revokeObjectURL(url);
          reject(new Error('Timeout ao converter PDF'));
        }, 3000);
        
        // Para garantir compressão, montamos o canvas com dimensões reduzidas
        canvas.width = 900;
        canvas.height = 1270; // A4 proportion
        const ctx = canvas.getContext('2d');
        if (!ctx) { 
          clearTimeout(timeout);
          URL.revokeObjectURL(url);
          reject(new Error('Canvas não suportado')); 
          return; 
        }
        
        // Fundo branco
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Texto indicativo
        ctx.fillStyle = '#333333';
        ctx.font = '14px Arial';
        ctx.fillText('Extrato Bancário - Documento PDF', 20, 30);
        ctx.font = '11px Arial';
        ctx.fillText(`Arquivo original: ${file.name}`, 20, 55);
        ctx.fillText(`Tamanho original: ${(file.size / 1024).toFixed(0)} KB`, 20, 75);
        
        clearTimeout(timeout);
        URL.revokeObjectURL(url);
        
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Falha ao gerar imagem do PDF'));
          },
          'image/webp',
          0.5
        );
      };
      reader.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Falha ao ler PDF')); };
      reader.readAsArrayBuffer(file);
    });
  };

  const compressFile = async (file: File): Promise<{ blob: Blob; ext: string; mime: string }> => {
    if (file.type.startsWith('image/')) {
      // Imagens: comprimir agressivamente para WebP
      const blob = await compressImage(file, 900, 0.5);
      return { blob, ext: 'webp', mime: 'image/webp' };
    }
    
    if (file.type === 'application/pdf') {
      // PDFs: otimizar com pdf-lib
      const blob = await compressPDF(file);
      // Se foi convertido para imagem, ajustar extensão
      if (blob.type === 'image/webp') {
        return { blob, ext: 'webp', mime: 'image/webp' };
      }
      return { blob, ext: 'pdf', mime: 'application/pdf' };
    }
    
    // Outros: retornar sem compressão
    const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
    return { blob: file, ext, mime: file.type };
  };

  const handleExtratoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      avisar('❌ Tipo de arquivo não permitido.\n\nFormatos aceitos: PDF, JPEG, PNG, WebP');
      if (extratoInputRef.current) extratoInputRef.current.value = '';
      return;
    }

    // Aceitar arquivos maiores antes da compressão (até 10MB) pois serão comprimidos
    if (file.size > 10 * 1024 * 1024) {
      avisar('❌ Arquivo muito grande.\n\nTamanho máximo: 10MB (será comprimido automaticamente)');
      if (extratoInputRef.current) extratoInputRef.current.value = '';
      return;
    }

    setIsUploadingExtrato(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sessão expirada');

      // Comprimir o arquivo (imagem ou PDF)
      const { blob: uploadBlob, ext: uploadExt, mime: uploadMimeType } = await compressFile(file);

      // Verificar se após compressão está dentro do limite de 1MB
      if (uploadBlob.size > 1 * 1024 * 1024) {
        avisar(`❌ Arquivo ainda muito grande após compressão.\n\nTamanho após compressão: ${(uploadBlob.size / 1024).toFixed(0)} KB\nLimite: 1.000 KB\n\nTente enviar um arquivo menor ou com menos páginas.`);
        if (extratoInputRef.current) extratoInputRef.current.value = '';
        setIsUploadingExtrato(false);
        return;
      }

      // Nome único para o arquivo: userId/timestamp.ext
      const filePath = `${user.id}/${Date.now()}.${uploadExt}`;

      // Se já existe um extrato anterior, remover do storage
      if (formData.beneficiario.extratoUrl) {
        await supabase.storage
          .from('extratos-bancarios')
          .remove([formData.beneficiario.extratoUrl]);
      }

      // Upload para Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('extratos-bancarios')
        .upload(filePath, uploadBlob, {
          contentType: uploadMimeType,
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Atualizar formData com os dados do extrato
      updateFormData('beneficiario', {
        ...formData.beneficiario,
        extratoUrl: filePath,
        extratoFilename: file.name
      });

      const reducao = ((1 - uploadBlob.size / file.size) * 100).toFixed(0);
      avisar(`✅ Extrato enviado com sucesso!\n\nArquivo: ${file.name}\nOriginal: ${(file.size / 1024).toFixed(0)} KB\nComprimido: ${(uploadBlob.size / 1024).toFixed(0)} KB\nRedução: ${reducao}%`);
    } catch (error: any) {
      console.error('❌ Erro ao enviar extrato:', error);
      avisar(`❌ Erro ao enviar extrato:\n${error.message}`);
    } finally {
      setIsUploadingExtrato(false);
      if (extratoInputRef.current) extratoInputRef.current.value = '';
    }
  };

  const handleRemoveExtrato = async () => {
    if (!formData.beneficiario.extratoUrl) return;
    
    try {
      await supabase.storage
        .from('extratos-bancarios')
        .remove([formData.beneficiario.extratoUrl]);

      updateFormData('beneficiario', {
        ...formData.beneficiario,
        extratoUrl: '',
        extratoFilename: ''
      });
    } catch (error: any) {
      console.error('Erro ao remover extrato:', error);
    }
  };

  const getExtratoPublicUrl = (path: string): string => {
    const { data } = supabase.storage
      .from('extratos-bancarios')
      .getPublicUrl(path);
    return data.publicUrl;
  };

  const handleViewExtrato = async (extratoPath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('extratos-bancarios')
        .createSignedUrl(extratoPath, 300); // URL válida por 5 minutos

      if (error) throw error;
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (error: any) {
      console.error('❌ Erro ao visualizar extrato:', error);
      avisar(`❌ Erro ao abrir extrato:\n${error.message}`);
    }
  };

  const handleDownloadExtrato = async (extratoPath: string, filename?: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('extratos-bancarios')
        .createSignedUrl(extratoPath, 300);
      if (error) throw error;
      const response = await fetch(data.signedUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || extratoPath.split('/').pop() || 'extrato.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('❌ Erro ao baixar extrato:', error);
      avisar(`❌ Erro ao baixar extrato:\n${error.message}`);
    }
  };

  // ======== VALIDAR PLANO (Admin) ========
  const handleValidarPlano = async (planoId: string, currentlyValidated: boolean) => {
    if (!isAdmin()) return;
    const action = currentlyValidated ? 'desvalidar' : 'validar';
    if (!(await confirmar(
      `${currentlyValidated ? 'Desvalidar' : 'Validar'} este plano?\n\n${currentlyValidated ? 'O usuário poderá editar novamente.' : 'O usuário não poderá mais editar o plano após a validação.'}`,
      { rotuloConfirmar: currentlyValidated ? 'Desvalidar' : 'Validar', destrutivo: false }
    ))) return;
    setValidandoPlanoId(planoId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sessão expirada');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão expirada');

      const updatePayload = {
        validado: !currentlyValidated,
        validado_por: !currentlyValidated ? user.id : null,
        validado_em: !currentlyValidated ? new Date().toISOString() : null,
      };

      // Tentar via RPC primeiro (mais confiável, bypassa schema cache)
      let success = false;
      try {
        const { error: rpcError } = await supabase.rpc('toggle_validacao_plano', {
          p_plano_id: planoId,
          p_user_id: user.id,
          p_validar: !currentlyValidated,
        });
        if (!rpcError) success = true;
      } catch (e) { /* fallback abaixo */ }

      // Fallback: tentar .update() direto (funciona se PostgREST schema cache está atualizado)
      if (!success) {
        try {
          const { error: updateError } = await supabase
            .from('planos_trabalho')
            .update(updatePayload)
            .eq('id', planoId);
          if (!updateError) success = true;
        } catch (e) { /* fallback abaixo */ }
      }

      // Último fallback: REST API direto (bypassa SDK e schema cache)
      if (!success) {
        const supabaseUrl = 'https://tlpmspfnswaxwqzmwski.supabase.co';
        const response = await fetch(`${supabaseUrl}/rest/v1/planos_trabalho?id=eq.${encodeURIComponent(planoId)}`, {
          method: 'PATCH',
          headers: {
            'apikey': session.access_token,
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify(updatePayload)
        });
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`Erro ao atualizar: ${errText}`);
        }
        success = true;
      }

      await loadPlanos();
      avisar(`✅ Plano ${!currentlyValidated ? 'validado' : 'desvalidado'} com sucesso!`);
    } catch (error: any) {
      console.error('❌ Erro ao validar plano:', error);
      avisar(`❌ Erro ao validar plano:\n${error.message}`);
    } finally {
      setValidandoPlanoId(null);
    }
  };

  // ======== SALVAR CONTA BANCÁRIA INLINE ========
  const handleSaveInlineConta = async (planoId: string) => {
    try {
      const { error } = await supabase
        .from('planos_trabalho')
        .update({ conta_bancaria: inlineContaValue.trim() || null })
        .eq('id', planoId);
      if (error) throw error;
      setInlineEditContaId(null);
      await loadPlanos();
    } catch (error: any) {
      avisar(`❌ Erro ao salvar conta bancária:\n${error.message}`);
    }
  };

  // ======== UPLOAD EXTRATO INLINE (diretamente de Meus Planos) ========
  const handleInlineExtratoUpload = async (planoId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      avisar('❌ Tipo de arquivo não permitido.\n\nFormatos aceitos: PDF, JPEG, PNG, WebP');
      if (inlineExtratoInputRef.current) inlineExtratoInputRef.current.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      avisar('❌ Arquivo muito grande.\n\nTamanho máximo: 10MB');
      if (inlineExtratoInputRef.current) inlineExtratoInputRef.current.value = '';
      return;
    }
    setIsUploadingInlineExtrato(true);
    setInlineExtratoUploadId(planoId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sessão expirada');
      const { blob: uploadBlob, ext: uploadExt, mime: uploadMimeType } = await compressFile(file);
      if (uploadBlob.size > 1 * 1024 * 1024) {
        avisar(`❌ Arquivo ainda muito grande após compressão (${(uploadBlob.size / 1024).toFixed(0)} KB). Limite: 1.000 KB.`);
        return;
      }
      // Buscar extrato atual para remover
      const { data: planoAtual } = await supabase.from('planos_trabalho').select('extrato_url').eq('id', planoId).single();
      if (planoAtual?.extrato_url) {
        await supabase.storage.from('extratos-bancarios').remove([planoAtual.extrato_url]);
      }
      const filePath = `${user.id}/${Date.now()}.${uploadExt}`;
      const { error: uploadError } = await supabase.storage
        .from('extratos-bancarios')
        .upload(filePath, uploadBlob, { contentType: uploadMimeType, upsert: false });
      if (uploadError) throw uploadError;
      const { error: updateError } = await supabase
        .from('planos_trabalho')
        .update({ extrato_url: filePath, extrato_filename: file.name })
        .eq('id', planoId);
      if (updateError) throw updateError;
      await loadPlanos();
      avisar(`✅ Extrato enviado com sucesso!\n\nArquivo: ${file.name}`);
    } catch (error: any) {
      console.error('❌ Erro ao enviar extrato inline:', error);
      avisar(`❌ Erro ao enviar extrato:\n${error.message}`);
    } finally {
      setIsUploadingInlineExtrato(false);
      setInlineExtratoUploadId(null);
      if (inlineExtratoInputRef.current) inlineExtratoInputRef.current.value = '';
    }
  };

  // ======== IMPRIMIR PLANO DIRETO DA LISTA ========
  const handlePrintFromList = (planoId: string) => {
    setFormData(getInitialFormData());
    setLastSavedFormData(null);
    setPlanoSalvoId(null);
    setSelectedOfertaEmendaId(null);
    setFormHasChanges(false);
    setCurrentSelection({ categoria: '', item: '', metas: [''] });
    setCurrentMetaQualitativa({ meta: '', valor: '' });
    setCurrentNatureza({ codigo: '', valor: '' });
    setCurrentView('new');
    setActiveSection('info-emenda');
    setSentSuccess(false);
    pendingPrintPlanoIdRef.current = planoId;
    loadingPlanIdRef.current = null;
    planLoadCompletedRef.current.delete(planoId);
    setTimeout(() => { setEditingPlanId(planoId); }, 50);
  };
  const handleGeneratePDF = async () => {
    // Proteção contra duplo clique/envio
    if (isSending) {
      console.log("⚠️ Operação já em andamento, ignorando clique");
      return;
    }

    setIsSending(true);
    try {
      // VALIDAÇÃO 1: Total de Naturezas de Despesa não pode ultrapassar Total de Metas Quantitativas
      const totalMetasQuantitativas = formData.acoesServicos.reduce((sum, acao) => sum + parseCurrency(acao.valor), 0);
      const totalDespesas = formData.naturezasDespesa.reduce((sum, despesa) => sum + parseCurrency(despesa.valor), 0);
      
      if (totalDespesas > totalMetasQuantitativas) {
        const diferenca = (totalDespesas - totalMetasQuantitativas).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        avisar(`⚠️ ERRO DE VALIDAÇÃO!\n\nO total de Naturezas de Despesa (R$ ${totalDespesas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) ultrapassa o Total de Metas Quantitativas (R$ ${totalMetasQuantitativas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) em R$ ${diferenca}.\n\nNÃO É POSSÍVEL GERAR PDF!\n\nAjuste os valores de despesa antes de tentar novamente.`);
        setIsSending(false);
        return;
      }

      // VALIDAÇÃO 2: Validar campos obrigatórios
      const validation = validateRequiredFields();
      if (!validation.isValid) {
        const missingList = validation.missingFields.map((field, idx) => `${idx + 1}. ${field}`).join('\n');
        avisar(
          `⚠️ NÃO É POSSÍVEL GERAR PDF!\n\n` +
          `Os campos obrigatórios abaixo devem ser preenchidos:\n\n${missingList}\n\n` +
          `Por favor, complete todos os campos indicados antes de salvar o PDF.`
        );
        setIsSending(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Sessão expirada. Faça login novamente.");
      
      // 1️⃣ ETAPA CRÍTICA: GARANTIR QUE O PLANO ESTÁ SALVO NO BANCO
      let currentPlanoId = planoSalvoId;
      
      if (!currentPlanoId) {
        // Novo plano: DEVE SALVAR PRIMEIRO
        console.log("📝 NOVO PLANO: Salvando no banco antes de prosseguir...");
        currentPlanoId = await handleFinalSend();
        if (!currentPlanoId) {
          throw new Error("❌ CRÍTICO: Falha ao salvar novo plano. PDF NÃO foi gerado.");
        }
      } else {
        // Plano já existe: GARANTIR ATUALIZAÇÃO ANTES DO PDF
        const updateSuccess = await handleFinalSend();
        if (!updateSuccess) {
          throw new Error("❌ CRÍTICO: Falha ao atualizar plano. PDF NÃO foi gerado.");
        }
      }

      // 2️⃣ VERIFICAÇÃO: Confirmar que o plano foi salvo
      console.log("✔️ Verificando se plano está realmente salvo no banco...");
      const { data: verifyPlano, error: verifyError } = await supabase
        .from('planos_trabalho')
        .select('id, numero_emenda')
        .eq('id', currentPlanoId)
        .single();

      if (verifyError || !verifyPlano) {
        throw new Error("❌ CRÍTICO: Plano não foi encontrado no banco após salvamento. PDF NÃO foi gerado.");
      }

      // 3️⃣ Registrar evento de visualização/download no banco de dados
      await recordPdfViewEvent(currentPlanoId);

      // 4️⃣ Abrir diálogo de impressão (navegador respeitará quebras naturalmente)
      setTimeout(() => {
        window.print();
      }, 500);
    } catch (error: any) {
      console.error("❌ ERRO CRÍTICO:", error);
      avisar(`⚠️ ERRO AO PROCESSAR PDF:\n\n${error.message}\n\n` +
            `O plano NÃO foi processado. Por favor, tente novamente.`);
    } finally {
      setIsSending(false);
    }
  };

  const selectedDiretriz = useMemo(() => 
    DIRETRIZES.find(d => d.id === formData.planejamento.diretrizId), 
    [formData.planejamento.diretrizId]
  );

  const selectedObjetivo = useMemo(() => 
    selectedDiretriz?.objetivos.find(o => o.id === formData.planejamento.objetivoId),
    [selectedDiretriz, formData.planejamento.objetivoId]
  );

  const getAcoesForPrograma = (programa: string) => {
    const acoes = dynamicAcoes[programa] || [];

    if (programa !== PROGRAMA_EMENDA_COLETIVA_10352_CUSTEIO) {
      return acoes;
    }

    return GRUPOS_ACAO_EMENDA_COLETIVA_10352_CUSTEIO
      .map(categoria => acoes.find(acao => acao.categoria === categoria))
      .filter((acao): acao is AcaoServico => Boolean(acao));
  };

  const availableAcoes = useMemo(() => 
    getAcoesForPrograma(formData.emenda.programa), 
    [formData.emenda.programa, dynamicAcoes]
  );

  // Metas Quantitativas disponíveis para o programa selecionado. Prioridade:
  // 1) configuração específica do banco para este programa (painel admin,
  //    aba "Programas Orçamentários" → seção "Metas Quantitativas" de cada
  //    programa) — mesma lógica usada para Natureza de Despesa;
  // 2) caso especial legado: "EMENDA INDIVIDUAL - CUSTEIO MAC - 2E90" sem
  //    configuração própria ainda usa o catálogo global de metas;
  // 3) demais programas sem configuração própria continuam usando as
  //    ações de serviços disponíveis (comportamento histórico preservado).
  const availableMetasQuantitativas = useMemo(() => {
    const prog = formData.emenda.programa;
    if (dynamicProgramaMetas[prog]?.length) {
      return dynamicProgramaMetas[prog];
    }
    if (prog === "EMENDA INDIVIDUAL - CUSTEIO MAC - 2E90") {
      return Object.entries(dynamicMetasQuant).map(([categoria, itens]) => ({
        categoria,
        itens
      }));
    }
    // Para outros programas sem configuração própria, usar as ações de serviços disponíveis
    return getAcoesForPrograma(prog);
  }, [formData.emenda.programa, dynamicAcoes, dynamicMetasQuant, dynamicProgramaMetas]);

  // Naturezas de despesa disponíveis para o programa selecionado. Prioridade:
  // 1) configuração específica do banco para este programa (fonte de verdade,
  //    mantida pelo admin em "Configuração de Listas");
  // 2) se o banco foi consultado com sucesso e o admin não configurou nenhuma
  //    restrição para este programa, mostra todas as naturezas (escolha
  //    deliberada do admin — ver mensagem em AdminTreeEditor: "nenhuma natureza
  //    configurada, o formulário exibirá todas");
  // 3) se o banco ainda não respondeu OU a consulta falhou definitivamente,
  //    usa a whitelist estática por programa como rede de segurança — cada
  //    programa continua restrito às suas próprias naturezas mesmo offline,
  //    em vez de liberar tudo indiscriminadamente.
  // Ordenado por código para facilitar a localização de itens adicionados
  // manualmente (que entram com "ordem" mais alta no banco).
  const getNaturezasForPrograma = (programa: string): NaturezaDespesa[] => {
    let list: NaturezaDespesa[];
    if (dynamicProgramaNaturezas[programa]?.length) {
      list = dynamicProgramaNaturezas[programa];
    } else if (treeDataLoaded && !treeDataError) {
      list = dynamicNaturezas;
    } else {
      const allowedCodigos: string[] | undefined = NATUREZAS_POR_PROGRAMA[programa];
      list = allowedCodigos && allowedCodigos.length > 0
        ? dynamicNaturezas.filter((n: NaturezaDespesa) => allowedCodigos.includes(n.codigo))
        : dynamicNaturezas;
    }
    return [...list].sort((a, b) => a.codigo.localeCompare(b.codigo));
  };

  const availableNaturezas: NaturezaDespesa[] = useMemo(
    () => getNaturezasForPrograma(formData.emenda.programa),
    [formData.emenda.programa, dynamicProgramaNaturezas, dynamicNaturezas, treeDataLoaded, treeDataError]
  );

  const updateFormData = (section: keyof FormState, value: any) => {
    if (section === 'beneficiario' && value?.cnes) {
      console.log('🔄 updateFormData - beneficiario:', value);
    }
    setFormData(prev => ({ ...prev, [section]: value }));
  };

  const updateJustCriterio = (index: number, value: string) => {
    justFromUserRef.current = true;
    const newFields = [...justCriterios];
    newFields[index] = value;
    setJustCriterios(newFields);
    updateFormData('justificativa', serializeJustCriterios(newFields));
  };

  // Scroll to section
  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections = [
    { id: 'info-emenda', title: 'Emenda Identificação' },
    { id: 'beneficiario', title: 'Beneficiário Dados' },
    { id: 'alinhamento', title: 'Estratégia Alinhamento' },
    { id: 'metas-quantitativas', title: 'Metas Metas' },
    { id: 'metas-qualitativas', title: 'Indicadores Indicadores' },
    { id: 'execucao-financeira', title: 'Financeiro Execução' },
    { id: 'finalizacao', title: 'Finalizar Finalização' }
  ];

  const confirmAddAcao = () => {
    if (!currentSelection.categoria || !currentSelection.item) {
      avisar('Por favor, selecione o Grupo de Ação e a Ação Específica.');
      return;
    }

    // Validação de limite antes de adicionar nova meta
    const recursoValue = parseCurrency(formData.emenda.valor);
    const isPortaria10169 = formData.emenda.programa && formData.emenda.programa.includes('PORTARIA 10.169');
    const isPortaria10352Custeio = formData.emenda.programa === PROGRAMA_EMENDA_COLETIVA_10352_CUSTEIO;
    const isGrupoVI = currentSelection.categoria.includes('VI - CUSTEIO DE OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE');
    const isOutrasAcoesMAC = currentSelection.categoria === 'OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE';
    
    let totalMetas = formData.acoesServicos.reduce((sum, acao) => sum + parseCurrency(acao.valor || '0'), 0);
    let totalGrupoVI = formData.acoesServicos
      .filter(acao => acao.categoria && acao.categoria.includes('VI - CUSTEIO DE OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE'))
      .reduce((sum, acao) => sum + parseCurrency(acao.valor || '0'), 0);
    let totalOutrasAcoes = formData.acoesServicos
      .filter(acao => acao.categoria === 'OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE')
      .reduce((sum, acao) => sum + parseCurrency(acao.valor || '0'), 0);

    // Validação para Portaria 10.352 - OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE: máximo 70%
    if (isPortaria10352Custeio && isOutrasAcoesMAC) {
      if (totalOutrasAcoes >= recursoValue * 0.7) {
        avisar('Você já atingiu o limite de 70% do valor do recurso para OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE. Não é possível adicionar mais metas deste grupo.');
        return;
      }
    }
    // Se for adicionar mais uma do Grupo VI (Portaria 10.169), já soma +0 (pois valor inicial é vazio)
    else if (isPortaria10169 && isGrupoVI) {
      if ((totalGrupoVI) >= recursoValue * 0.5) {
        avisar('Você já atingiu o limite de 50% do valor do recurso para o Grupo VI. Não é possível adicionar mais metas deste grupo.');
        return;
      }
    } else {
      if (totalMetas >= recursoValue) {
        avisar('Você já atingiu o valor total do recurso. Não é possível adicionar mais metas.');
        return;
      }
    }

    const newAcao = {
      categoria: currentSelection.categoria,
      item: currentSelection.item,
      metasQuantitativas: [currentSelection.item],
      valor: ''
    };
    setFormData(prev => ({
      ...prev,
      acoesServicos: [...prev.acoesServicos, newAcao]
    }));
    setCurrentSelection({ categoria: '', item: '', metas: [''] });
  };

  const removeAcao = (index: number) => {
    setFormData(prev => ({
      ...prev,
      acoesServicos: prev.acoesServicos.filter((_, i) => i !== index)
    }));
  };

  const confirmAddMetaQualitativa = () => {
    if (!currentMetaQualitativa.meta) {
      avisar('Por favor, selecione um indicador.');
      return;
    }
    setFormData(prev => ({
      ...prev,
      metasQualitativas: [...prev.metasQualitativas, { ...currentMetaQualitativa }]
    }));
    setCurrentMetaQualitativa({ meta: '', valor: '' });
  };

  const removeMetaQualitativa = (index: number) => {
    setFormData(prev => ({
      ...prev,
      metasQualitativas: prev.metasQualitativas.filter((_, i) => i !== index)
    }));
  };

  const confirmAddNatureza = () => {
    if (!currentNatureza.codigo) {
      avisar('Por favor, selecione uma natureza de despesa.');
      return;
    }
    setFormData(prev => ({
      ...prev,
      naturezasDespesa: [...prev.naturezasDespesa, { ...currentNatureza }]
    }));
    setCurrentNatureza({ codigo: '', valor: '' });
  };

  const removeNatureza = (index: number) => {
    setFormData(prev => ({
      ...prev,
      naturezasDespesa: prev.naturezasDespesa.filter((_, i) => i !== index)
    }));
  };

  const handleSendToSES = () => {
    // Se o formulário está vazio, mostrar modal para selecionar um plano
    // Verifica se campos críticos estão realmente preenchidos
    const hasEmendaData = formData.emenda?.numero && formData.emenda?.numero.toString().trim() !== '';
    const hasParliamentarData = formData.emenda?.parlamentar && formData.emenda?.parlamentar.toString().trim() !== '';
    const hasBeneficiarioData = formData.beneficiario?.nome && formData.beneficiario?.nome.toString().trim() !== '';
    
    const isFormEmpty = !hasEmendaData || !hasParliamentarData || !hasBeneficiarioData;
    
    if (isFormEmpty && planosList.length > 0) {
      setShowSelectPlanModal(true);
      return;
    }

    if (isFormEmpty && planosList.length === 0) {
      avisar('⚠️ Nenhum plano disponível.\n\nPreencha o formulário com os dados do plano que deseja enviar ou crie um novo plano.');
      return;
    }

    // Formulário preenchido, gerar email
    const subject = `Plano de Trabalho 2026 - Emenda ${formData.emenda.numero} - CNES ${formData.beneficiario.cnes || 'N/A'}`;
    const emailBody = `Prezados,

Segue em anexo o Plano de Trabalho 2026 relativo à Emenda Parlamentar nº ${formData.emenda.numero}.

INFORMAÇÕES DO PLANO:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Parlamentar: ${formData.emenda.parlamentar}
Nº Emenda: ${formData.emenda.numero}
Programa: ${formData.emenda.programa}
Valor Total: R$ ${formData.emenda.valor}
Beneficiário: ${formData.beneficiario.nome}
CNPJ: ${formData.beneficiario.cnpj}
CNES: ${formData.beneficiario.cnes}${formData.beneficiario.contaBancaria ? `\nConta Bancária: ${formData.beneficiario.contaBancaria}` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Por favor, anexe o PDF assinado a este email antes de enviar.

Atenciosamente,
Sistema de Planos de Trabalho
Secretaria de Estado da Saúde de São Paulo`;

    const mailtoLink = `mailto:gcf-emendasfederais@saude.sp.gov.br?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
    window.location.href = mailtoLink;
  };

  const handleSelectPlanForEmail = async (plano: any) => {
    // Validar plano
    if (!plano || !plano.id) {
      console.error('❌ Plano inválido:', plano);
      avisar('Erro: Plano inválido. Tente novamente.');
      return;
    }

    console.log('📂 handleSelectPlanForEmail disparado para plano:', plano.id);
    
    // Reset PRIMEIRO - ordem é IMPORTANTE!
    setFormData(getInitialFormData());
    setLastSavedFormData(null);
    setPlanoSalvoId(null);
    setSelectedOfertaEmendaId(null);
    setFormHasChanges(false);
    
    // Limpar inputs temporários TAMBÉM
    setCurrentSelection({ categoria: '', item: '', metas: [''] });
    setCurrentMetaQualitativa({ meta: '', valor: '' });
    setCurrentNatureza({ codigo: '', valor: '' });
    
    setCurrentView('new');
    setActiveSection('info-emenda');
    setSentSuccess(false);
    setShowSelectPlanModal(false);
    
    // RESET CRÍTICO: Limpar refs para permitir novo carregamento
    loadingPlanIdRef.current = null;
    planLoadCompletedRef.current.delete(plano.id);
    
    // Aguardar React processar o reset, DEPOIS carregar o plano
    setTimeout(() => {
      console.log('⏰ Agora carregando plano:', plano.id);
      setEditingPlanId(plano.id);
    }, 50);
  };

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <img src={LOGO_URL_BRANCA} alt="Logotipo Oficial" className="h-12 w-auto mb-8 opacity-80" />
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
          <p className="text-xs font-medium uppercase tracking-widest text-gray-500">Sincronizando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <LoginScreen
        loginInput={loginInput}
        setLoginInput={setLoginInput}
        handleLogin={handleLogin}
        loginError={loginError}
        isSending={isSending}
      />
    );
  }

  if (showDocument) {
    return (
      <PlanoDocumento
        formData={formData}
        selectedDiretriz={selectedDiretriz}
        selectedObjetivo={selectedObjetivo}
        dynamicNaturezas={dynamicNaturezas}
        justificativaAlteradaEm={justificativaAlteradaEm}
        getPortariaForPrograma={getPortariaForPrograma}
        isSending={isSending}
        handleGeneratePDF={handleGeneratePDF}
        setShowDocument={setShowDocument}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      <a href="#conteudo" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[300] focus:px-4 focus:py-3 focus:rounded-lg focus:bg-white focus:text-[#08162D] focus:font-semibold focus:shadow-lg">Pular para o conteúdo</a>

      <AppHeader
        isAuthenticated={isAuthenticated}
        currentUser={currentUser}
        isAdmin={isAdmin()}
        currentView={currentView}
        setCurrentView={setCurrentView}
        iniciarNovoPlano={iniciarNovoPlano}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        emendaNotifCount={emendaNotifCount}
        selectedCnes={selectedCnes}
        parseCnesList={parseCnesList}
        onTrocarCnes={(cnes) => { setSelectedCnes(cnes); updateFormData('beneficiario', { ...formData.beneficiario, cnes }); }}
        cnesCadastroCache={cnesCadastroCache}
        abrirEdicaoSiteTransparencia={abrirEdicaoSiteTransparencia}
        faqDisponivel={faqDisponivel}
        abrirFaq={abrirFaq}
        abrirDisponibilizarEmenda={() => setShowDisponibilizarEmendaModal(true)}
        abrirGestaoUsuarios={() => setShowUserManagement(true)}
        abrirCadastroCnes={() => setShowCnesCadastroAdmin(true)}
        abrirConfiguracoes={() => setShowAdminTreeEditor(true)}
        handleLogout={handleLogout}
      />

      <main id="conteudo" tabIndex={-1} className="min-h-screen bg-slate-50 outline-none">
        {/* Container centralizado - largura adaptável por view */}
        <div style={{ maxWidth: currentView === 'list' || currentView === 'dashboard' ? '1600px' : '1120px', margin: '0 auto', padding: '2rem 1.5rem' }}>

          {/* MODAL VISUALIZAR PLANO - READ ONLY */}
          {showViewPlanModal && (
            <VisualizarPlanoModal
              viewingPlanData={viewingPlanData}
              isLoadingViewPlan={isLoadingViewPlan}
              dynamicNaturezas={dynamicNaturezas}
              onClose={() => { setShowViewPlanModal(false); setViewingPlanData(null); }}
            />
          )}

          {/* MODAL GERENCIAMENTO DE USUÁRIOS */}
          {/* MODAL CONFIGURAÇÃO DE LISTAS (Admin Tree Editor) */}
          {showAdminTreeEditor && currentUser?.role === 'admin' && (
            <AdminTreeEditor
              supabaseClient={supabase}
              onClose={() => setShowAdminTreeEditor(false)}
              onDataChanged={loadTreeDataFromDB}
            />
          )}

          {/* MODAL CADASTRO DOS CNES (sites de transparência) */}
          {showCnesCadastroAdmin && currentUser?.role === 'admin' && (
            <CnesCadastroAdmin
              supabaseClient={supabase}
              currentUserId={currentUser?.id}
              entidadesPorCnes={entidadesPorCnes}
              onClose={() => setShowCnesCadastroAdmin(false)}
            />
          )}

          {/* CENTRAL DE DÚVIDAS */}
          {showFaqPanel && (
            <FaqPanel
              itens={faqItens}
              nomeUsuario={currentUser?.name}
              boasVindas={faqBoasVindas}
              isAdmin={currentUser?.role === 'admin'}
              painelAtivo={faqPainelAtivo}
              onGerenciar={() => { setShowFaqPanel(false); setShowFaqAdmin(true); }}
              onClose={() => setShowFaqPanel(false)}
            />
          )}
          {showFaqAdmin && currentUser?.role === 'admin' && (
            <FaqAdmin
              supabaseClient={supabase}
              currentUserId={currentUser?.id}
              onChange={carregarFaq}
              onClose={() => setShowFaqAdmin(false)}
            />
          )}

          {showDisponibilizarEmendaModal && currentUser?.role === 'admin' && (
            <div className="fixed inset-0 z-[105] bg-black/40 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl w-full max-w-4xl shadow-2xl animate-slideUp flex flex-col max-h-[92vh] overflow-hidden">
                <div className="bg-gradient-to-r from-blue-700 to-blue-600 px-8 py-6 border-b border-blue-800/40 flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-black text-white">Disponibilizar Emenda para Beneficiário</h2>
                    <p className="text-xs text-blue-100 mt-1">Admin define a emenda e o usuário recebe pré-preenchida ao entrar no sistema.</p>
                  </div>
                  <button onClick={() => setShowDisponibilizarEmendaModal(false)} className="p-2 rounded-lg hover:bg-blue-500/40 transition-colors" title="Fechar">
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Número da Emenda */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">Número da Emenda*</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 rounded-xl border border-blue-300 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                        placeholder="Ex: 20240001"
                        value={newEmendaDisponibilizada.numeroEmenda}
                        onChange={(e) => setNewEmendaDisponibilizada(prev => ({ ...prev, numeroEmenda: e.target.value }))}
                      />
                    </div>

                    {/* Programa */}
                    <Select
                      label="Programa Orçamentário*"
                      value={newEmendaDisponibilizada.programa}
                      onChange={(e) => setNewEmendaDisponibilizada(prev => ({ ...prev, programa: e.target.value }))}
                      options={dynamicProgramas.map(p => ({ value: p, label: p }))}
                    />

                    {/* CNES */}
                    <ComboboxField
                      label="CNES*"
                      value={newEmendaDisponibilizada.cnes}
                      placeholder="Digite ou selecione o CNES"
                      options={[...new Set(sheetBaseRows.map(r => r.cnes))].map(c => ({ label: c, value: c }))}
                      onChange={(text) => setNewEmendaDisponibilizada(prev => ({
                        ...prev,
                        cnes: text.replace(/\s/g, ''),
                        entidade: '',
                        cnpj: '',
                        parlamentar: '',
                        tipoParlamentar: ''
                      }))}
                      onSelect={(val) => setNewEmendaDisponibilizada(prev => ({
                        ...prev,
                        cnes: val,
                        entidade: '',
                        cnpj: '',
                        parlamentar: '',
                        tipoParlamentar: ''
                      }))}
                    />

                    {/* Entidade */}
                    <ComboboxField
                      label="Entidade*"
                      value={newEmendaDisponibilizada.entidade}
                      placeholder="Digite ou selecione a entidade"
                      options={entidadesOptionsForSelectedCnes.length > 0
                        ? entidadesOptionsForSelectedCnes
                        : [...new Set(sheetBaseRows.map(r => r.entidade).filter(Boolean))].map(e => ({ label: e, value: e }))}
                      onChange={(text) => {
                        const row = sheetRowsForSelectedCnes.find(r => r.entidade === text)
                          || sheetBaseRows.find(r => r.entidade === text);
                        setNewEmendaDisponibilizada(prev => ({
                          ...prev,
                          entidade: text,
                          cnpj: row?.cnpj || prev.cnpj
                        }));
                      }}
                      onSelect={(val) => {
                        const row = sheetRowsForSelectedCnes.find(r => r.entidade === val)
                          || sheetBaseRows.find(r => r.entidade === val);
                        setNewEmendaDisponibilizada(prev => ({
                          ...prev,
                          entidade: val,
                          cnpj: row?.cnpj || prev.cnpj
                        }));
                      }}
                    />

                    {/* CNPJ */}
                    <ComboboxField
                      label="CNPJ*"
                      value={newEmendaDisponibilizada.cnpj}
                      placeholder="Digite ou selecione o CNPJ"
                      options={cnpjsOptionsForSelectedCnes.length > 0
                        ? cnpjsOptionsForSelectedCnes
                        : [...new Set(sheetBaseRows.map(r => r.cnpj).filter(Boolean))].map(c => ({ label: c, value: c }))}
                      onChange={(text) => setNewEmendaDisponibilizada(prev => ({ ...prev, cnpj: maskCNPJ(text) }))}
                      onSelect={(val) => setNewEmendaDisponibilizada(prev => ({ ...prev, cnpj: maskCNPJ(val) }))}
                    />

                    {/* Parlamentar */}
                    <ComboboxField
                      label="Parlamentar (Deputado ou Senador)*"
                      value={newEmendaDisponibilizada.parlamentar}
                      placeholder="Digite ou selecione o parlamentar"
                      options={parlamentaresOptionsForSelectedCnes.length > 0
                        ? parlamentaresOptionsForSelectedCnes.map(o => ({ label: o.label, value: `${o.tipo}:${o.value}` }))
                        : [
                            ...sheetBaseRows.filter(r => r.deputado).map(r => ({ label: `Dep.: ${r.deputado}`, value: `deputado:${r.deputado}` })),
                            ...sheetBaseRows.filter(r => r.senador).map(r => ({ label: `Sen.: ${r.senador}`, value: `senador:${r.senador}` }))
                          ].filter((o, i, arr) => arr.findIndex(x => x.value === o.value) === i)}
                      onChange={(text) => {
                        const matchedRow = sheetBaseRows.find(row => row.deputado === text || row.senador === text);
                        const tipo = matchedRow?.deputado === text ? 'deputado' : matchedRow?.senador === text ? 'senador' : '';
                        setNewEmendaDisponibilizada(prev => ({
                          ...prev,
                          parlamentar: text,
                          tipoParlamentar: (tipo as 'deputado' | 'senador') || ''
                        }));
                      }}
                      onSelect={(val) => {
                        const colonIdx = val.indexOf(':');
                        const tipo = val.slice(0, colonIdx) as 'deputado' | 'senador';
                        const nome = val.slice(colonIdx + 1);
                        setNewEmendaDisponibilizada(prev => ({
                          ...prev,
                          parlamentar: nome,
                          tipoParlamentar: tipo
                        }));
                      }}
                    />

                    {/* Valor */}
                    <InputField
                      label="Valor da Emenda (R$)*"
                      name="valor-disponibilizacao-modal"
                      value={newEmendaDisponibilizada.valor}
                      onChange={(e) => setNewEmendaDisponibilizada(prev => ({ ...prev, valor: maskCurrency(e.target.value) }))}
                      placeholder="R$ 0,00"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowDisponibilizarEmendaModal(false)}
                      className="px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-sm uppercase tracking-wide"
                    >
                      Fechar
                    </button>
                    <button
                      type="button"
                      onClick={handleDisponibilizarEmenda}
                      disabled={isSavingDisponibilizacao}
                      className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm uppercase tracking-wide disabled:opacity-50"
                    >
                      {isSavingDisponibilizacao ? 'Salvando...' : 'Disponibilizar Emenda'}
                    </button>
                  </div>

                  {/* Painel completo de emendas com filtros */}
                  <div className="space-y-3">
                    <p className="text-xs font-bold uppercase text-gray-700 flex items-center gap-2">
                      <Database className="w-3.5 h-3.5 text-blue-600" />
                      Histórico de Emendas Disponibilizadas
                    </p>
                    {/* Resumo + filtros */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {(['todas', 'disponibilizada', 'utilizada', 'cancelada'] as const).map((f) => {
                          const count = f === 'todas'
                            ? emendasDisponibilizadasAdmin.length
                            : emendasDisponibilizadasAdmin.filter((e: any) => e.status === f).length;
                          return (
                            <button
                              key={f}
                              onClick={() => setFiltroStatusEmendas(f)}
                              className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wide border transition-all ${filtroStatusEmendas === f
                                ? f === 'disponibilizada' ? 'bg-blue-600 text-white border-blue-600'
                                  : f === 'utilizada' ? 'bg-green-600 text-white border-green-600'
                                  : f === 'cancelada' ? 'bg-red-500 text-white border-red-500'
                                  : 'bg-gray-800 text-white border-gray-800'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'}`}
                            >
                              {f === 'todas' ? 'Todas' : f === 'disponibilizada' ? 'Pendentes' : f === 'utilizada' ? 'Com Plano' : 'Canceladas'} ({count})
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={loadEmendasDisponibilizadasAdmin}
                        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors"
                        title="Recarregar lista"
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Atualizar
                      </button>
                    </div>

                    <div className="max-h-80 overflow-y-auto space-y-2">
                      {(() => {
                        const filtered = filtroStatusEmendas === 'todas'
                          ? emendasDisponibilizadasAdmin
                          : emendasDisponibilizadasAdmin.filter((e: any) => e.status === filtroStatusEmendas);
                        if (filtered.length === 0) return (
                          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">Nenhum registro encontrado.</div>
                        );
                        return filtered.map((item: any) => (
                          <div key={item.id} className={`border rounded-xl p-3 text-xs text-gray-700 ${item.status === 'utilizada' ? 'bg-green-50 border-green-200' : item.status === 'cancelada' ? 'bg-red-50 border-red-200 opacity-70' : 'bg-gray-50 border-gray-200'}`}>
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-gray-900 truncate">{item.entidade}</p>
                                <p className="text-gray-500">CNES {item.cnes} • CNPJ {item.cnpj}</p>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${item.status === 'disponibilizada' ? 'bg-blue-100 text-blue-700' : item.status === 'utilizada' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                  {item.status === 'disponibilizada' ? 'Pendente' : item.status === 'utilizada' ? 'Plano criado' : 'Cancelada'}
                                </span>
                                {item.status === 'disponibilizada' && (
                                  <>
                                    <button
                                      onClick={() => openEditEmendaModal(item)}
                                      className="p-1.5 rounded-lg bg-white border border-gray-300 hover:bg-blue-50 hover:border-blue-400 text-gray-600 hover:text-blue-700 transition-all"
                                      title="Editar emenda"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleCancelEmenda(item.id)}
                                      className="p-1.5 rounded-lg bg-white border border-gray-300 hover:bg-red-50 hover:border-red-400 text-gray-600 hover:text-red-700 transition-all"
                                      title="Cancelar emenda"
                                    >
                                      <Ban className="w-3.5 h-3.5" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-gray-600">
                              {item.numero_emenda && <span>Emenda Nº <strong className="text-gray-800">{item.numero_emenda}</strong></span>}
                              <span>{item.parlamentar}</span>
                              <span>{item.programa}</span>
                              <span className="font-bold text-teal-700">R$ {formatCurrencyDisplay(item.valor)}</span>
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-gray-400">
                              <span>Disponibilizada {formatElapsedTime(item.created_at)}</span>
                              {item.status === 'utilizada' && item.utilizada_em && (
                                <span className="text-green-600 font-semibold">Plano cadastrado {formatElapsedTime(item.utilizada_em)}</span>
                              )}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {showSiteTransparenciaModal && isAuthenticated && currentUser?.role === 'user' && selectedCnes && (
            <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl animate-slideUp overflow-hidden">
                <div className="bg-gradient-to-r from-blue-800 to-blue-700 px-7 py-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="w-6 h-6 text-white" />
                    <div>
                      <h2 className="text-lg font-black text-white">Site de Transparência</h2>
                      <p className="text-xs text-blue-100 mt-0.5">Cadastro do CNES {selectedCnes}</p>
                    </div>
                  </div>
                  {!siteTransparenciaObrigatorio && (
                    <button onClick={() => setShowSiteTransparenciaModal(false)} className="p-2 rounded-lg hover:bg-blue-600/40 transition-colors" aria-label="Fechar">
                      <X className="w-5 h-5 text-white" />
                    </button>
                  )}
                </div>
                <div className="p-7 space-y-4">
                  {siteTransparenciaObrigatorio && (
                    <p className="text-sm text-gray-700">
                      Antes de continuar, informe o endereço do <strong>portal de transparência</strong> da instituição deste CNES. O cadastro é feito uma única vez.
                    </p>
                  )}
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-900">Endereço do site (URL)*</label>
                    <input
                      type="url"
                      autoFocus
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                      placeholder="https://www.instituicao.org.br/transparencia"
                      value={siteTransparenciaInput}
                      onChange={(e) => setSiteTransparenciaInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSalvarSiteTransparencia(); }}
                    />
                    {siteTransparenciaErro && (
                      <p className="text-xs text-red-600 font-semibold flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{siteTransparenciaErro}</p>
                    )}
                  </div>
                </div>
                <div className="border-t border-gray-100 px-7 py-4 flex justify-end gap-3">
                  {siteTransparenciaObrigatorio ? (
                    <button type="button" onClick={handleLogout}
                      className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-sm">Sair</button>
                  ) : (
                    <button type="button" onClick={() => setShowSiteTransparenciaModal(false)}
                      className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-sm">Cancelar</button>
                  )}
                  <button type="button" onClick={handleSalvarSiteTransparencia} disabled={isSavingSiteTransparencia || !siteTransparenciaInput.trim()}
                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm disabled:opacity-50">
                    {isSavingSiteTransparencia ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showEditEmendaModal && editingEmenda && currentUser?.role === 'admin' && (
            <div className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl animate-slideUp flex flex-col max-h-[90vh] overflow-hidden">
                <div className="bg-gradient-to-r from-blue-800 to-blue-700 px-8 py-5 border-b border-blue-900/30 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-black text-white">Editar Emenda Disponibilizada</h2>
                    <p className="text-xs text-blue-100 mt-0.5">Alterações afetam o pré-preenchimento do beneficiário.</p>
                  </div>
                  <button onClick={() => { setShowEditEmendaModal(false); setEditingEmenda(null); }} className="p-2 rounded-lg hover:bg-blue-600/40 transition-colors">
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-7 space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">Número da Emenda</label>
                      <input type="text" className="w-full px-4 py-3 rounded-xl border border-blue-300 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                        value={editingEmendaForm.numeroEmenda}
                        onChange={(e) => setEditingEmendaForm(prev => ({ ...prev, numeroEmenda: e.target.value }))} />
                    </div>
                    <Select label="Programa Orçamentário*" value={editingEmendaForm.programa}
                      onChange={(e) => setEditingEmendaForm(prev => ({ ...prev, programa: e.target.value }))}
                      options={dynamicProgramas.map(p => ({ value: p, label: p }))} />
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">CNES*</label>
                      <input type="text" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-mono"
                        value={editingEmendaForm.cnes}
                        onChange={(e) => setEditingEmendaForm(prev => ({ ...prev, cnes: e.target.value.replace(/\D/g, '') }))} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">Entidade*</label>
                      <input type="text" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                        value={editingEmendaForm.entidade}
                        onChange={(e) => setEditingEmendaForm(prev => ({ ...prev, entidade: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">CNPJ*</label>
                      <input type="text" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-mono"
                        value={editingEmendaForm.cnpj}
                        onChange={(e) => setEditingEmendaForm(prev => ({ ...prev, cnpj: maskCNPJ(e.target.value) }))} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">Parlamentar*</label>
                      <input type="text" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                        value={editingEmendaForm.parlamentar}
                        onChange={(e) => setEditingEmendaForm(prev => ({ ...prev, parlamentar: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">Valor (R$)*</label>
                      <input type="text" className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                        value={editingEmendaForm.valor}
                        onChange={(e) => setEditingEmendaForm(prev => ({ ...prev, valor: maskCurrency(e.target.value) }))} />
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-100 px-7 py-4 flex justify-end gap-3">
                  <button type="button" onClick={() => { setShowEditEmendaModal(false); setEditingEmenda(null); }}
                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-sm">Cancelar</button>
                  <button type="button" onClick={handleSaveEditEmenda} disabled={isSavingEditEmenda}
                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm disabled:opacity-50">
                    {isSavingEditEmenda ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showUserManagement && currentUser?.role === 'admin' && (
            <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-md flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Gerenciar usuários">
              <EscParaFechar onClose={() => setShowUserManagement(false)} />
              <div className="bg-white rounded-3xl w-full max-w-4xl shadow-2xl animate-slideUp flex flex-col max-h-[95vh] overflow-hidden">

                {/* HEADER FIXO */}
                <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 px-8 py-6 border-b border-gray-700/50 flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-red-600/20">
                      <Users className="text-red-600 w-7 h-7" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black text-white">Gestão de Usuários</h2>
                      <p className="text-xs text-gray-400 font-medium mt-1">Administre usuários e permissões do sistema</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowUserManagement(false)} 
                    className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
                    title="Fechar"
                  >
                    <X className="w-6 h-6 text-gray-400 hover:text-white" />
                  </button>
                </div>

                {/* CONTEÚDO SCROLLÁVEL */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-8 space-y-8">

                    {/* SEÇÃO 1: REGISTRO DE NOVO USUÁRIO */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-black text-gray-900 flex items-center gap-3">
                          <UserPlus className="text-red-600 w-5 h-5" />
                          Registrar Novo Usuário
                        </h3>
                        <p className="text-sm text-gray-500 mt-2">Adicione um novo usuário ao sistema com as permissões necessárias.</p>
                      </div>

                      <form onSubmit={handleCreateUser} className="space-y-6 bg-gray-50 p-6 rounded-2xl border border-gray-200">
                        {/* Grid responsivo */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Nome Completo */}
                          <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-900">Nome Completo*</label>
                            <input 
                              type="text" 
                              placeholder="Ex: João Silva"
                              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600 text-base transition-all"
                              value={newUser.name}
                              onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                              required
                            />
                          </div>

                          {/* E-mail */}
                          <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-900">E-mail*</label>
                            <input 
                              type="email" 
                              placeholder="usuario@example.com"
                              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600 text-base transition-all"
                              value={newUser.email}
                              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                              required
                            />
                          </div>

                          {/* CNES da Instituição */}
                          <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-900">CNES da Instituição*</label>
                            <input 
                              type="text" 
                              placeholder="Ex: 1234567"
                              maxLength={7}
                              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600 text-base transition-all"
                              value={newUser.cnes}
                              onChange={(e) => setNewUser({...newUser, cnes: e.target.value.replace(/\D/g, '').slice(0, 7)})}
                              required
                            />
                            <p className="text-xs text-gray-500">7 dígitos - será associado automaticamente ao usuário</p>
                          </div>

                          {/* Senha Inicial */}
                          <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-900">Senha Inicial*</label>
                            <input 
                              type="password" 
                              placeholder="Mínimo 6 caracteres"
                              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600 text-base transition-all"
                              value={newUser.password}
                              onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                              required
                            />
                          </div>

                          {/* Perfil do Usuário */}
                          <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-900">Perfil do Usuário*</label>
                            <select 
                              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600 text-base font-medium transition-all"
                              value={newUser.role}
                              onChange={(e) => setNewUser({...newUser, role: e.target.value as 'user' | 'admin' | 'intermediate'})}
                            >
                              <option value="">Selecione um perfil</option>
                              <option value="user">Usuário Padrão</option>
                              <option value="intermediate">Usuário Intermediário</option>
                              <option value="admin">Administrador SES</option>
                            </select>
                          </div>
                        </div>

                        {/* Descrição do Perfil Selecionado */}
                        {newUser.role && (
                          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                            <p className="text-xs font-bold text-blue-900 uppercase tracking-wider">Permissões do Perfil</p>
                            <p className="text-sm text-blue-800 mt-2 leading-relaxed">
                              {newUser.role === 'user' 
                                ? '✓ Criar e gerenciar seus próprios planos de trabalho • ✓ Visualizar relatórios pessoais • ✓ Editar dados básicos da conta'
                                : newUser.role === 'intermediate'
                                ? '✓ Visualizar todos os planos de trabalho do sistema • ✗ Não pode criar novos planos • ✗ Não pode editar ou apagar planos • ✓ Apenas leitura'
                                : '✓ Acesso total ao sistema • ✓ Gerenciar todos os usuários e permissões • ✓ Visualizar relatórios globais • ✓ Configurar sistema'
                              }
                            </p>
                          </div>
                        )}

                        {/* Botão Registrar - Aparece apenas quando campos obrigatórios preenchidos */}
                        {newUser.name && newUser.email && newUser.password && newUser.role && newUser.cnes && (
                          <button 
                            type="submit" 
                            disabled={isSending} 
                            className="w-full py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-bold uppercase text-sm tracking-wider hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-3 shadow-lg"
                          >
                            {isSending ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Registrando...
                              </>
                            ) : (
                              <>
                                <UserPlus className="w-5 h-5" />
                                Registrar Novo Usuário
                              </>
                            )}
                          </button>
                        )}
                      </form>
                    </div>

                    {/* DIVISOR */}
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>

                    {/* SEÇÃO: IMPORTAR USUÁRIOS VIA CSV */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-black text-gray-900 flex items-center gap-3">
                          <UploadCloud className="text-green-600 w-5 h-5" />
                          Importar Usuários via CSV
                        </h3>
                        <p className="text-sm text-gray-500 mt-2">Envie uma planilha CSV para criar vários usuários de uma só vez.</p>
                      </div>

                      <div className="bg-green-50 p-6 rounded-2xl border border-green-200 space-y-4">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-green-700 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-green-800 space-y-1">
                            <p className="font-bold">Formato do CSV (separado por vírgula ou ponto-e-vírgula):</p>
                            <p className="font-mono text-xs bg-green-100 p-2 rounded-lg">nome;email;cnes;senha</p>
                            <p className="text-xs text-green-600">Se a coluna "senha" estiver vazia, o CNES será usado como senha.</p>
                            <p className="font-mono text-xs bg-green-100 p-2 rounded-lg mt-1">
                              MARIA SILVA;maria@hospital.com.br;1234567;1234567<br/>
                              JOAO SANTOS;joao@clinica.org.br;7654321;7654321
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <label className="flex-1 cursor-pointer">
                            <input
                              type="file"
                              accept=".csv,.txt"
                              onChange={handleCsvUpload}
                              disabled={csvUploading}
                              className="hidden"
                            />
                            <div className={`flex items-center justify-center gap-3 py-4 px-6 rounded-xl border-2 border-dashed transition-all ${
                              csvUploading 
                                ? 'border-gray-300 bg-gray-100 cursor-not-allowed' 
                                : 'border-green-400 bg-white hover:bg-green-50 hover:border-green-500'
                            }`}>
                              {csvUploading ? (
                                <>
                                  <Loader2 className="w-5 h-5 animate-spin text-green-600" />
                                  <span className="font-bold text-green-700">Importando...</span>
                                </>
                              ) : (
                                <>
                                  <UploadCloud className="w-5 h-5 text-green-600" />
                                  <span className="font-bold text-green-700">Clique para selecionar arquivo CSV</span>
                                </>
                              )}
                            </div>
                          </label>
                        </div>

                        {/* Resultados da importação */}
                        {csvResults && csvResults.detalhes && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-bold text-gray-800">
                              Resultado: {csvResults.criados} criado(s), {csvResults.erros} erro(s) de {csvResults.total} total
                            </p>
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {csvResults.detalhes.map((d: any, idx: number) => (
                                <div key={idx} className={`text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${
                                  d.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {d.success ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                                  <span className="font-medium">{d.email}</span>
                                  <span>— {d.success ? d.message : d.error}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* DIVISOR */}
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>

                    {/* SEÇÃO 2: LISTA DE USUÁRIOS */}
                    <div className="space-y-6">

                      {/* Configuração global do mínimo de caracteres */}
                      <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-amber-100 shrink-0">
                            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="text-amber-700"><path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.108-1.204l-.526-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                          </div>
                          <div>
                            <h3 className="text-sm font-black text-amber-900">Mínimo de caracteres por critério — Justificativa SAES (5 campos)</h3>
                            <p className="text-xs text-amber-700 mt-0.5">Define o mínimo de caracteres por campo para cada um dos 5 critérios SAES. Use <span className="font-bold">0</span> para sem limite.</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <input
                            type="number"
                            min={0}
                            value={minJustificativa}
                            onChange={e => setMinJustificativa(Number(e.target.value))}
                            className="w-24 px-3 py-2 border-2 border-amber-200 rounded-xl text-sm font-mono font-bold text-center focus:outline-none focus:border-amber-400 bg-white"
                          />
                          <button
                            onClick={async () => {
                              try {
                                const { error } = await supabase.from('app_settings')
                                  .upsert({ key: 'min_justificativa', value: String(minJustificativa), updated_at: new Date().toISOString() }, { onConflict: 'key' });
                                if (error) throw error;
                                try { localStorage.setItem('min_justificativa', String(minJustificativa)); } catch {}
                                avisar(`✅ Mínimo de ${minJustificativa} caracteres salvo para todos os usuários!`);
                              } catch (e: any) {
                                // Fallback: salvar apenas local­mente
                                try { localStorage.setItem('min_justificativa', String(minJustificativa)); } catch {}
                                avisar(`⚠️ Salvo localmente (${minJustificativa} chars). Para persistir para todos, execute o SQL:\nALTER TABLE public.app_settings DISABLE ROW LEVEL SECURITY;\nNOTIFY pgrst, 'reload schema';`);
                              }
                            }}
                            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white text-xs font-bold rounded-xl transition-colors shadow-sm"
                          >
                            Salvar
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-black text-gray-900 flex items-center gap-3">
                            <Users className="text-gray-600 w-5 h-5" />
                            Usuários do Sistema
                          </h3>
                          <p className="text-sm text-gray-500 mt-2">{usersList.length} usuário{usersList.length !== 1 ? 's' : ''} cadastrado{usersList.length !== 1 ? 's' : ''}</p>
                        </div>
                        <button
                          onClick={() => {
                            setShowInactiveUsers(!showInactiveUsers);
                            if (!showInactiveUsers) {
                              loadInactiveUsers();
                            }
                          }}
                          className={`px-4 py-2 rounded-lg font-bold text-xs uppercase transition-colors ${
                            showInactiveUsers
                              ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={showInactiveUsers ? 'Ocultar usuários inativos' : 'Mostrar usuários inativos'}
                        >
                          {inactiveUsersList.length > 0 ? `Inativos (${inactiveUsersList.length})` : 'Sem inativos'}
                        </button>
                      </div>

                      {/* CAMPO DE BUSCA */}
                      <div className="relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        <input
                          type="text"
                          placeholder="Digite nome ou e-mail..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all"
                        />
                      </div>

                      {/* Lista filtrada e ordenada */}
                      {(() => {
                        const filtered = usersList.filter(u => 
                          u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          u.email?.toLowerCase().includes(searchQuery.toLowerCase())
                        );
                        const sorted = filtered.sort((a, b) => {
                          // Admins primeiro
                          if (a.role === 'admin' && b.role !== 'admin') return -1;
                          if (a.role !== 'admin' && b.role === 'admin') return 1;
                          // Depois por nome
                          return (a.name || '').localeCompare(b.name || '');
                        });
                        return (
                          <>
                            {searchQuery && sorted.length > 0 && (
                              <p className="text-sm text-gray-500 font-semibold">{sorted.length} resultado{sorted.length !== 1 ? 's' : ''} encontrado{sorted.length !== 1 ? 's' : ''}</p>
                            )}

                            {/* Lista de Usuários em Cards Modernos */}
                            <div className="space-y-3">
                              {sorted && sorted.length > 0 ? (
                                sorted.map((u, i) => (
                            <div 
                              key={i} 
                              className="group p-6 bg-white rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200"
                            >
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white font-bold text-sm">
                                      {u.name?.charAt(0).toUpperCase() || 'U'}
                                    </div>
                                    <div>
                                      <p className="text-base font-bold text-gray-900">{u.name || 'Sem nome'}</p>
                                      <p className="text-xs text-gray-500 font-mono">{u.username}</p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Status Badge + Role Badge */}
                                <div className="flex items-center gap-2">
                                  {/* Status Indicator */}
                                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold ${
                                    u.disabled 
                                      ? 'bg-gray-100 text-gray-700' 
                                      : 'bg-green-100 text-green-700'
                                  }`}>
                                    <div className={`w-2 h-2 rounded-full ${u.disabled ? 'bg-gray-400' : 'bg-green-500'}`}></div>
                                    {u.disabled ? 'Inativo' : 'Ativo'}
                                  </div>

                                  {/* Role Badge */}
                                  <div className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
                                    u.role === 'admin'
                                      ? 'bg-red-100 text-red-700'
                                      : u.role === 'intermediate'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {u.role === 'admin' ? 'Admin' : u.role === 'intermediate' ? 'Intermediário' : 'Usuário'}
                                  </div>

                                  {/* Ações - Menu Hamburguer */}
                                  <MenuHamburguerUserActions
                                    user={u}
                                    onEdit={() => {
                                      setEditingUser({
                                        id: u.id,
                                        email: u.email,
                                        name: u.name,
                                        cnes: u.cnes || '',
                                        password: ''
                                      });
                                      setShowEditUserModal(true);
                                    }}
                                    onChangeRole={(role) => {
                                      const roleNames = {
                                        'user': 'USUÁRIO PADRÃO',
                                        'intermediate': 'USUÁRIO INTERMEDIÁRIO',
                                        'admin': 'ADMINISTRADOR'
                                      };
                                      confirmar(`Alterar o perfil de ${u.name} para ${roleNames[role]}?`, { rotuloConfirmar: 'Alterar perfil', destrutivo: false })
                                        .then(ok => { if (ok) handleChangeUserRole(u.id, u.name, role); });
                                    }}
                                    onToggleActive={() => handleToggleUserDisable(u.id)}
                                    onResetPassword={() => handleChangePassword(u.email)}
                                    onDelete={() => handleDeleteUser(u.id, `${u.name} (${u.email})`)}
                                  />
                                </div>
                              </div>

                              {/* E-mail */}
                              <p className="text-sm text-gray-600 mb-1 ml-13">{u.email}</p>

                              {/* CNES */}
                              {u.cnes && (
                                <p className="text-sm text-gray-600 ml-13 font-mono">
                                  <span className="font-bold text-gray-700">CNES:</span> {u.cnes}
                                </p>
                              )}
                            </div>
                          ))
                          ) : (
                            <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-dashed border-gray-300 shadow-sm">
                              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                              <p className="text-lg font-bold text-gray-600">Nenhum usuário encontrado</p>
                              <p className="text-sm text-gray-500 mt-2">
                                {searchQuery ? 'Nenhum resultado para a busca' : (usersList === null ? 'Carregando...' : usersList.length === 0 ? 'Nenhum usuário cadastrado' : '')}
                              </p>
                              <p className="text-xs text-gray-400 mt-3 max-w-sm mx-auto">
                                Se a lista deveria ter usuários, recarregue a página. Persistindo, verifique o console do navegador (F12).
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    )})()}
                    </div>

                    {/* SEÇÃO 3: USUÁRIOS INATIVOS/DESATIVADOS */}
                    {showInactiveUsers && (
                      <>
                        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
                        <div className="space-y-6 p-6 bg-orange-50 rounded-xl border-2 border-orange-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-black text-orange-900 flex items-center gap-3">
                                Usuários inativos
                              </h3>
                              <p className="text-sm text-orange-700 mt-2">{inactiveUsersList.length} usuário{inactiveUsersList.length !== 1 ? 's' : ''} inativo{inactiveUsersList.length !== 1 ? 's' : ''}</p>
                            </div>
                          </div>

                          {inactiveUsersList.length > 0 ? (
                            <div className="space-y-4">
                              {inactiveUsersList.map((u: any) => (
                                <div key={u.id} className="p-4 bg-white rounded-lg border border-orange-200 hover:border-orange-300 transition-colors">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h4 className="font-bold text-gray-900">{u.name || u.email}</h4>
                                      <p className="text-sm text-gray-600">{u.email}</p>
                                      {u.cnes && (
                                        <p className="text-xs text-gray-500 mt-1">CNES: {u.cnes}</p>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 ml-4">
                                      <button
                                        onClick={() => handleReactivateUser(u.id, u.name || u.email)}
                                        className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-bold text-xs uppercase hover:bg-green-200 transition-colors"
                                        title="Reativar usuário"
                                      >
                                        Reativar
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <Users className="w-12 h-12 text-orange-300 mx-auto mb-3" />
                              <p className="text-sm font-bold text-orange-700">Carregando usuários inativos...</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                  </div>
                </div>
              </div>
            </div>
          )}

          {/* EDIT USER MODAL */}
          {showEditUserModal && (
            <div className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl animate-slideUp">
                
                {/* Header - Cores verdes do sistema */}
                <div className="bg-gradient-to-r from-green-700 to-green-600 px-8 py-6 border-b border-green-800/50 flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                    <Settings2 className="w-6 h-6" />
                    Editar Usuário: {editingUser.name}
                  </h3>
                  <button
                    onClick={() => setShowEditUserModal(false)}
                    className="text-white hover:bg-green-800/30 p-2 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-8 space-y-6">
                  {/* Nome */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Nome</label>
                    <input
                      type="text"
                      value={editingUser.name}
                      onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
                      placeholder="Nome completo"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={editingUser.email}
                      onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
                      placeholder="email@example.com"
                    />
                  </div>

                  {/* CNES */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">CNES</label>
                    <input
                      type="text"
                      value={editingUser.cnes}
                      onChange={(e) => setEditingUser({ ...editingUser, cnes: maskCNES(e.target.value) })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
                      placeholder="Código CNES (8 dígitos)"
                      maxLength="8"
                    />
                  </div>

                  {/* Nova Senha (opcional) */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Nova Senha (opcional)</label>
                    <input
                      type="password"
                      value={editingUser.password}
                      onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
                      placeholder="Deixe em branco para não alterar"
                    />
                    {editingUser.password && editingUser.password.length < 6 && (
                      <p className="text-xs text-red-500 mt-1">Mínimo 6 caracteres</p>
                    )}
                  </div>

                  {/* Info text */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <p className="text-sm text-blue-800">
                      <strong>Dica:</strong> Se deixar a senha em branco, ela não será alterada. Preencha apenas se quiser resetar a senha do usuário.
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 border-t border-gray-200 flex items-center justify-end gap-3 rounded-b-3xl">
                  <button
                    onClick={() => setShowEditUserModal(false)}
                    className="px-6 py-3 font-bold text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleEditUser}
                    className="px-6 py-3 font-bold text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Settings2 className="w-5 h-5" />
                    Salvar Alterações
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* HELP MODAL */}
          {showHelpModal && helpSectionId && helpContent[helpSectionId] && (
            <div className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-md flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Ajuda da seção">
              <EscParaFechar onClose={() => setShowHelpModal(false)} />
              <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl animate-slideUp max-h-[90vh] overflow-y-auto">
                
                {/* Header - Cores vermelhas do sistema */}
                <div className="sticky top-0 bg-gradient-to-r from-red-700 to-red-600 px-8 py-6 border-b border-red-800/50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-red-600/40">
                      <Info className="text-white w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black text-white">{helpContent[helpSectionId]?.title}</h2>
                      <p className="text-red-100 text-sm mt-1">Guia de preenchimento</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowHelpModal(false)}
                    className="p-2 hover:bg-red-600/50 rounded-lg transition-colors"
                    aria-label="Fechar ajuda"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-8 space-y-6">
                  <div>
                    <p className="text-lg text-gray-700 leading-relaxed font-medium">
                      {helpContent[helpSectionId]?.description}
                    </p>
                  </div>

                  <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
                    <h3 className="text-base font-black text-red-900 mb-4 flex items-center gap-2">
                      <ArrowUp className="w-5 h-5" />
                      Orientações para Preenchimento
                    </h3>
                    <ul className="space-y-3">
                      {helpContent[helpSectionId]?.tips.map((tip, idx) => (
                        <li key={idx} className="flex gap-3 text-gray-700">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-700 text-white text-xs font-bold flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <span className="pt-0.5">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={() => setShowHelpModal(false)}
                    className="w-full py-3 bg-red-700 text-white rounded-xl font-bold uppercase text-sm tracking-wider hover:bg-red-800 transition-colors"
                  >
                    Entendi, Feche a Ajuda
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* BULK DELETE MODAL */}
          {showBulkDeleteModal && (
            <div className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl animate-slideUp">
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-6 border-b border-red-800/50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-red-500/30">
                      <AlertTriangle className="text-white w-6 h-6" />
                    </div>
                    <h2 className="text-2xl font-black text-white">Deletar Planos</h2>
                  </div>
                  <button 
                    onClick={() => setShowBulkDeleteModal(false)} 
                    className="p-2 hover:bg-red-500/50 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>

                <div className="p-8 space-y-6">
                  <div>
                    <p className="text-lg text-gray-700 leading-relaxed font-medium">
                      Você está prestes a deletar <span className="font-black text-red-600">{selectedPlanos.size}</span> plano(s). 
                      Esta ação é permanente e não pode ser desfeita.
                    </p>
                  </div>

                  <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
                    <p className="text-sm text-gray-700 font-bold">Planos selecionados:</p>
                    <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                      {Array.from(selectedPlanos).map(planoId => {
                        const plano = planosList.find(p => p.id === planoId);
                        return (
                          <div key={planoId} className="text-sm text-gray-600 bg-white p-2 rounded border border-red-200">
                            Emenda {plano?.numero_emenda} - {plano?.beneficiario_nome}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowBulkDeleteModal(false)}
                      className="flex-1 py-3 bg-gray-200 text-gray-800 rounded-xl font-bold uppercase text-sm tracking-wider hover:bg-gray-300 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={bulkDeletePlanos}
                      className="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold uppercase text-sm tracking-wider hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" /> Deletar {selectedPlanos.size}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* MODAL: SELECIONAR PLANO PARA ENVIAR EMAIL */}
          {showSelectPlanModal && (
            <div className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl animate-slideUp max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 border-b border-blue-800/50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-blue-500/30">
                      <ClipboardCheck className="text-white w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black text-white">Selecionar Plano</h2>
                      <p className="text-blue-100 text-sm mt-1">Escolha um plano salvo para enviar</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowSelectPlanModal(false)} 
                    className="p-2 hover:bg-blue-500/50 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-8 space-y-4">
                  {isLoadingPlanos ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
                      <p className="text-lg font-bold text-gray-700">Carregando seus planos...</p>
                      <p className="text-sm text-gray-500 mt-2">Aguarde um momento</p>
                    </div>
                  ) : planosList.length > 0 ? (
                    <div className="space-y-3">
                      {planosList.map((plano) => (
                        <button
                          key={plano.id}
                          onClick={() => handleSelectPlanForEmail(plano)}
                          className="w-full text-left p-6 bg-gradient-to-r from-gray-50 to-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Parlamentar</p>
                              <p className="text-sm font-bold text-gray-900">{plano.parlamentar}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Emenda</p>
                              <p className="text-sm font-bold text-gray-900">{plano.numero_emenda}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Valor</p>
                              <p className="text-sm font-bold text-red-600">R$ {parseFloat(plano.valor_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Beneficiário</p>
                              <p className="text-sm font-bold text-gray-900">{plano.beneficiario_nome}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">CNES</p>
                              <p className="text-sm font-bold text-gray-900 font-mono">{plano.cnes || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">CNPJ</p>
                              <p className="text-sm font-bold text-gray-900 font-mono">{plano.beneficiario_cnpj || '—'}</p>
                            </div>
                          </div>
                          <div className="mt-4 flex items-center justify-between">
                            <span className="text-xs text-blue-600 font-semibold">Clique para selecionar →</span>
                            <ChevronRight className="w-5 h-5 text-blue-600" />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <ClipboardCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-lg font-bold text-gray-700">Nenhum plano disponível</p>
                      <p className="text-sm text-gray-500 mt-2">Você não tem planos salvos para enviar.</p>
                      <button
                        onClick={() => loadPlanos()}
                        disabled={isLoadingPlanos}
                        className="mt-6 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold rounded-lg transition-colors flex items-center gap-2 mx-auto"
                      >
                        {isLoadingPlanos ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Carregando...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Tentar Novamente
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-500 mt-4">Dica: Você também pode preencher o formulário com os dados do plano que deseja enviar.</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 border-t border-gray-200 flex items-center justify-end gap-3 rounded-b-3xl">
                  <button
                    onClick={() => setShowSelectPlanModal(false)}
                    className="px-6 py-3 font-bold text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* MODAL DE AJUDA - PRÓXIMOS PASSOS */}
          {showHelpModal && !helpSectionId && (
            <div className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-md flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl w-full max-w-4xl shadow-2xl animate-slideUp max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-yellow-600 px-8 py-6 border-b border-yellow-700/50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-yellow-500/30">
                      <BookOpen className="text-white w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black text-white">Próximos Passos para Envio</h2>
                      <p className="text-yellow-100 text-sm mt-1">Guia completo para gerar, assinar e enviar seu plano</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowHelpModal(false)} 
                    className="p-2 hover:bg-yellow-500/50 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-8 space-y-6">
                  <p className="text-base text-gray-700 leading-relaxed font-semibold">
                    Siga os 4 passos abaixo para gerar o PDF, assinar e enviar seu plano de trabalho para a SES-SP.
                  </p>

                  {/* PASSO 1 */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-2xl border-2 border-blue-300">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-600 rounded-full">
                          <span className="text-white font-black text-lg">1</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-blue-900 text-lg mb-2 uppercase tracking-wide">Gere o PDF</h4>
                        <p className="text-sm text-blue-800 mb-4 leading-relaxed">
                          Clique no botão <strong>“Visualizar e baixar PDF”</strong> para gerar o documento em PDF. O sistema irá salvar automaticamente seu plano e abrir a visualização do PDF.
                        </p>
                        <ul className="text-sm text-blue-700 space-y-2 ml-4">
                          <li className="flex gap-2">
                            <span className="text-blue-600 font-bold">•</span>
                            <span>O PDF será gerado automaticamente após clicar no botão</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="text-blue-600 font-bold">•</span>
                            <span>Você verá uma prévia do documento antes de baixar</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="text-blue-600 font-bold">•</span>
                            <span>Salve o arquivo em um local seguro em seu computador</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* PASSO 2 */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border-2 border-purple-300">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-12 h-12 bg-purple-600 rounded-full">
                          <span className="text-white font-black text-lg">2</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-purple-900 text-lg mb-2 uppercase tracking-wide">Assine o PDF</h4>
                        <p className="text-sm text-purple-800 mb-4 leading-relaxed">
                          Abra o arquivo PDF em seu computador e assine digitalmente ou de forma manuscrita.
                        </p>
                        <div className="bg-white rounded-lg p-4 mb-3 border border-purple-200">
                          <p className="text-sm font-bold text-purple-900 mb-3">Opções de Assinatura:</p>
                          <ul className="text-sm text-purple-800 space-y-2">
                            <li className="flex gap-2">
                              <span className="text-purple-600 font-bold">✓</span>
                              <span><strong>Assinatura Digital:</strong> Use um leitor de PDF (Adobe Reader) com certificado digital</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-purple-600 font-bold">✓</span>
                              <span><strong>Assinatura Manuscrita:</strong> Imprima o PDF, assine com caneta azul ou preta, escaneie e salve como PDF</span>
                            </li>
                          </ul>
                        </div>
                        <ul className="text-sm text-purple-700 space-y-2 ml-4">
                          <li className="flex gap-2">
                            <span className="text-purple-600 font-bold">•</span>
                            <span>Responsável pela assinatura: <strong>{formData.responsavelAssinatura || 'Campo a preencher'}</strong></span>
                          </li>
                          <li className="flex gap-2">
                            <span className="text-purple-600 font-bold">•</span>
                            <span>Assine no local indicado no documento</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="text-purple-600 font-bold">•</span>
                            <span>Mantenha uma cópia assinada para seus registros</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* PASSO 3 */}
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 p-6 rounded-2xl border-2 border-red-300">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-600 rounded-full">
                          <span className="text-white font-black text-lg">3</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-red-900 text-lg mb-2 uppercase tracking-wide">Envie para SES-SP</h4>
                        <p className="text-sm text-red-800 mb-4 leading-relaxed">
                          Clique no botão <strong>“Enviar assinado”</strong> para abrir seu cliente de email padrão com o template de mensagem pré-preenchido.
                        </p>
                        <div className="bg-white rounded-lg p-4 mb-3 border border-red-200">
                          <p className="text-sm font-bold text-red-900 mb-3">O que fazer:</p>
                          <ol className="text-sm text-red-800 space-y-2 ml-4 list-decimal">
                            <li>Clique em "ENVIAR ASSINADO" → seu email será aberto automaticamente</li>
                            <li>O assunto e mensagem já estarão preenchidos</li>
                            <li><strong>Clique em "Anexar" ou "Attachment"</strong> e selecione o PDF assinado</li>
                            <li>Revise o conteúdo do email</li>
                            <li>Clique em "Enviar"</li>
                          </ol>
                        </div>
                        <ul className="text-sm text-red-700 space-y-2 ml-4">
                          <li className="flex gap-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>Certifique-se de que o PDF assinado está anexado ao email</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>Verifique se o arquivo não está corrompido antes de enviar</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>Guarde uma cópia do email enviado como comprovante</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* PASSO 4 */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border-2 border-green-300">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-12 h-12 bg-green-600 rounded-full">
                          <span className="text-white font-black text-lg">4</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-green-900 text-lg mb-2 uppercase tracking-wide">Email Receptor</h4>
                        <p className="text-sm text-green-800 mb-4 leading-relaxed">
                          Seu email será enviado para o endereço oficial de recebimento de planos de trabalho:
                        </p>
                        <div className="bg-white rounded-lg p-4 border-2 border-green-400 shadow-sm">
                          <p className="text-center text-lg font-black text-green-700 font-mono break-all">
                            gcf-emendasfederais@saude.sp.gov.br
                          </p>
                        </div>
                        <ul className="text-sm text-green-700 space-y-2 ml-4 mt-4">
                          <li className="flex gap-2">
                            <span className="text-green-600 font-bold">✓</span>
                            <span>Este é o email oficial para recebimento de planos de trabalho</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="text-green-600 font-bold">✓</span>
                            <span>Você receberá uma confirmação de recebimento em breve</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="text-green-600 font-bold">✓</span>
                            <span>Guarde o número da emenda ({formData.emenda.numero}) para consultas futuras</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 border-t border-gray-200 flex items-center justify-end gap-3 rounded-b-3xl">
                  <button
                    onClick={() => setShowHelpModal(false)}
                    className="px-8 py-3 font-bold text-white bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 rounded-lg transition-all shadow-md hover:shadow-lg"
                  >
                    Entendi, Fechar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* VIEW: LISTA DE PLANOS */}
          {currentView === 'list' && (
            <div className="space-y-6 animate-fadeIn">
              {/* Input oculto para upload inline de extrato bancário */}
              <input
                ref={inlineExtratoInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => { if (inlineExtratoUploadId) handleInlineExtratoUpload(inlineExtratoUploadId, e); }}
              />

              {/* AVISO INFORMATIVO FIXO para usuários */}
              {currentUser?.role === 'user' && emendasDisponiveisFiltradas.length === 0 && !isLoadingEmendasDisponiveis && (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4" role="status">
                  <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-amber-900 text-sm leading-relaxed">
                    <span className="font-black">Novos Planos de Trabalho</span> somente serão iniciados após a disponibilização de emenda pelo
                    <span className="font-bold"> Grupo de Controle Financeiro — Emendas Federais - SES-SP</span>.
                    Aguarde o contato da equipe.
                  </p>
                </div>
              )}

              {/* BANNER: Emenda(s) disponível(is) para usuário */}
              {currentUser?.role === 'user' && emendasDisponiveisFiltradas.length > 0 && (
                <div className="bg-[#0B3B8C] rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-md">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                      <Bell className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-black text-white text-base leading-tight">
                        {emendasDisponiveisFiltradas.length === 1
                          ? 'Você tem uma emenda disponível para cadastro'
                          : `Você tem ${emendasDisponiveisFiltradas.length} emendas disponíveis para cadastro`}
                      </p>
                      <p className="text-blue-100 text-sm mt-0.5">
                        {emendasDisponiveisFiltradas[0].parlamentar}
                        {emendasDisponiveisFiltradas[0].valor ? ` · R$ ${formatCurrencyDisplay(emendasDisponiveisFiltradas[0].valor)}` : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={iniciarNovoPlano}
                    className="flex-shrink-0 h-11 px-5 bg-white text-[#0B3B8C] rounded-xl font-bold text-sm hover:bg-blue-50 transition-colors shadow-sm flex items-center justify-center gap-1.5"
                  >
                    Iniciar Plano <ChevronRight className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Meus Planos de Trabalho</h1>
                  {planosList.length > 0 && (
                    <p className="text-sm text-slate-600 mt-1.5 flex flex-wrap items-center gap-x-2">
                      <span>{planosList.length} {planosList.length === 1 ? 'plano' : 'planos'}</span>
                      <span className="text-slate-300" aria-hidden="true">•</span>
                      <span>Valor total <span className="font-bold text-slate-900 font-mono">R$ {planosList.reduce((sum, p) => sum + (parseFloat(p.valor_total) || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                    </p>
                  )}
                </div>
                {isAdmin() && (
                  <button
                    onClick={exportToXLSX}
                    className="flex items-center justify-center gap-2 h-11 px-5 bg-green-700 text-white rounded-xl font-bold text-sm hover:bg-green-800 shadow-sm transition-colors"
                  >
                    <Download className="w-4 h-4" /> Relatório XLSX
                  </button>
                )}
              </div>

              {/* SELEÇÃO E BULK DELETE - Admin */}
              {isAdmin() && (
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedPlanos.size === planosList.length && planosList.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPlanos(new Set(planosList.map(p => p.id)));
                          } else {
                            setSelectedPlanos(new Set());
                          }
                        }}
                        className="w-5 h-5 accent-amber-600"
                      />
                      <span className="font-bold text-sm text-amber-900">Selecionar Todos ({planosList.length})</span>
                    </label>
                    {selectedPlanos.size > 0 && (
                      <button
                        onClick={() => setShowBulkDeleteModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-bold text-xs uppercase tracking-wider hover:bg-red-700 transition-all"
                      >
                        <Trash2 className="w-4 h-4" /> Deletar {selectedPlanos.size} Selecionado(s)
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* FILTROS - Compactos */}
              {isAdmin() && (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4">
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-widest flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" aria-hidden="true" /> Filtros de pesquisa</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                    <input
                      type="text"
                      placeholder="CNES"
                      value={filtros.cnes}
                      onChange={(e) => setFiltros({...filtros, cnes: e.target.value})}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600"
                    />
                    <input
                      type="text"
                      placeholder="Parlamentar"
                      value={filtros.parlamentar}
                      onChange={(e) => setFiltros({...filtros, parlamentar: e.target.value})}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600"
                    />
                    <input
                      type="text"
                      placeholder="Emenda"
                      value={filtros.emenda}
                      onChange={(e) => setFiltros({...filtros, emenda: e.target.value})}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600"
                    />
                    <input
                      type="text"
                      placeholder="CNPJ"
                      value={filtros.cnpj}
                      onChange={(e) => setFiltros({...filtros, cnpj: e.target.value})}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-600/20 focus:border-red-600"
                    />
                  </div>
                  {(filtros.cnes || filtros.parlamentar || filtros.emenda || filtros.cnpj) && (
                    <button
                      onClick={() => setFiltros({ cnes: '', parlamentar: '', emenda: '', cnpj: '' })}
                      className="text-xs font-bold text-red-600 hover:text-red-700 uppercase tracking-widest"
                    >
                      Limpar filtros
                    </button>
                  )}
                </div>
              )}

              {isLoadingPlanos ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-16 h-16 text-red-600 animate-spin" />
                </div>
              ) : planosList.length === 0 ? (
                <div className="text-center py-20 px-6 bg-white rounded-2xl border-2 border-dashed border-slate-300">
                  <ClipboardCheck className="w-14 h-14 mx-auto text-slate-300 mb-5" aria-hidden="true" />
                  <p className="text-lg text-slate-800 font-semibold">Nenhum plano cadastrado ainda</p>
                  <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
                    {currentUser?.role === 'user'
                      ? 'Quando a SES-SP disponibilizar uma emenda para o seu CNES, o botão “Iniciar Plano” aparece no topo desta página.'
                      : 'Os planos de trabalho cadastrados aparecerão aqui.'}
                  </p>
                  {faqDisponivel && (
                    <button onClick={abrirFaq} className="mt-6 h-11 px-5 rounded-xl border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50 inline-flex items-center gap-2">
                      <HelpCircle className="w-4 h-4" aria-hidden="true" /> Ver dúvidas frequentes
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {planosList
                    .filter(plano => {
                      if (filtros.cnes && !plano.cnes?.toLowerCase().includes(filtros.cnes.toLowerCase())) return false;
                      if (filtros.parlamentar && !plano.parlamentar?.toLowerCase().includes(filtros.parlamentar.toLowerCase())) return false;
                      if (filtros.emenda && !plano.numero_emenda?.toLowerCase().includes(filtros.emenda.toLowerCase())) return false;
                      if (filtros.cnpj && !plano.beneficiario_cnpj?.toLowerCase().includes(filtros.cnpj.toLowerCase())) return false;
                      return true;
                    })
                    .length === 0 ? (
                    <div className="text-center py-16 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                      <p className="text-sm text-gray-600 font-bold">Nenhum plano encontrado com os filtros aplicados</p>
                    </div>
                  ) : (
                    <>
                      {(() => {
                        const filteredPlanos = planosList.filter(plano => {
                          if (filtros.cnes && !plano.cnes?.toLowerCase().includes(filtros.cnes.toLowerCase())) return false;
                          if (filtros.parlamentar && !plano.parlamentar?.toLowerCase().includes(filtros.parlamentar.toLowerCase())) return false;
                          if (filtros.emenda && !plano.numero_emenda?.toLowerCase().includes(filtros.emenda.toLowerCase())) return false;
                          if (filtros.cnpj && !plano.beneficiario_cnpj?.toLowerCase().includes(filtros.cnpj.toLowerCase())) return false;
                          return true;
                        });
                        const totalValue = filteredPlanos.reduce((sum, p) => sum + (parseFloat(p.valor_total) || 0), 0);
                        return (
                          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 mb-4">
                            <p className="text-xs text-gray-700 font-bold mb-2">{filteredPlanos.length} {filteredPlanos.length === 1 ? 'plano encontrado' : 'planos encontrados'}</p>
                            <p className="text-sm font-black text-blue-600">Valor Total: R$ {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                        );
                      })()}
                      {/* ALERTA DE CONTAS BANCÁRIAS DUPLICADAS */}
                      {(() => {
                        const contaCount: Record<string, string[]> = {};
                        planosList.forEach(p => {
                          const conta = (p.conta_bancaria || '').trim();
                          if (conta) {
                            if (!contaCount[conta]) contaCount[conta] = [];
                            contaCount[conta].push(p.parlamentar || p.numero_emenda || p.id);
                          }
                        });
                        const duplicadas = Object.entries(contaCount).filter(([, ids]) => ids.length > 1);
                        if (duplicadas.length === 0) return null;
                        return (
                          <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 mb-4">
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-black text-amber-800 uppercase tracking-wider mb-2">Conta(s) Bancária(s) Utilizada(s) em Mais de 1 Plano</p>
                                {duplicadas.map(([conta, planos]) => (
                                  <div key={conta} className="mb-1">
                                    <p className="text-sm text-amber-900">
                                      <span className="font-mono font-bold bg-amber-200 px-2 py-0.5 rounded">{conta}</span>
                                      <span className="text-amber-700 ml-2">→ usada em {planos.length} planos: {planos.join(', ')}</span>
                                    </p>
                                  </div>
                                ))}
                                <p className="text-sm font-semibold text-red-700 mt-3 border-t border-amber-300 pt-2">Não serão efetuados pagamentos de emendas que utilizem conta bancária previamente utilizada.</p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {planosList
                        .filter(plano => {
                          if (filtros.cnes && !plano.cnes?.toLowerCase().includes(filtros.cnes.toLowerCase())) return false;
                          if (filtros.parlamentar && !plano.parlamentar?.toLowerCase().includes(filtros.parlamentar.toLowerCase())) return false;
                          if (filtros.emenda && !plano.numero_emenda?.toLowerCase().includes(filtros.emenda.toLowerCase())) return false;
                          if (filtros.cnpj && !plano.beneficiario_cnpj?.toLowerCase().includes(filtros.cnpj.toLowerCase())) return false;
                          return true;
                        })
                        .map((plano) => (
                        <div key={plano.id} className="bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:border-red-400 transition-all">
                          <div className="flex items-start gap-3">
                            {isAdmin() && (
                              <input
                                type="checkbox"
                                checked={selectedPlanos.has(plano.id)}
                                onChange={(e) => {
                                  const newSelected = new Set(selectedPlanos);
                                  if (e.target.checked) {
                                    newSelected.add(plano.id);
                                  } else {
                                    newSelected.delete(plano.id);
                                  }
                                  setSelectedPlanos(newSelected);
                                }}
                                className="w-5 h-5 accent-amber-600 mt-1 flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              {/* Linha única com todos os dados */}
                              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-x-4 gap-y-1">
                                <div className="col-span-1 lg:col-span-2 min-w-0">
                                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Parlamentar</p>
                                  <p className="text-sm font-bold text-gray-900">{plano.parlamentar}</p>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Emenda</p>
                                  <p className="text-sm font-bold text-gray-900 font-mono">{plano.numero_emenda}</p>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Valor</p>
                                  <p className="text-sm font-black text-red-600">R$ {parseFloat(plano.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">CNES</p>
                                  <p className="text-sm font-bold text-gray-900 font-mono">{plano.cnes || '—'}</p>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">CNPJ</p>
                                  <p className="text-sm font-bold text-gray-900 font-mono">{plano.beneficiario_cnpj || '—'}</p>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Conta Bancária</p>
                                  {inlineEditContaId === plano.id ? (
                                    <div className="flex items-center gap-2 mt-1 bg-blue-50 p-2 rounded border border-blue-200">
                                      <input
                                        type="text"
                                        value={inlineContaValue}
                                        onChange={(e) => setInlineContaValue(e.target.value)}
                                        maxLength={25}
                                        className="flex-1 px-2 py-1 text-xs border border-blue-400 rounded font-mono focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                                        placeholder="001/1234-5/12345-6"
                                        autoFocus
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveInlineConta(plano.id); if (e.key === 'Escape') setInlineEditContaId(null); }}
                                      />
                                      <button onClick={() => handleSaveInlineConta(plano.id)} className="px-2 py-1 bg-green-500 text-white rounded text-xs font-bold hover:bg-green-600 whitespace-nowrap">Salvar</button>
                                      <button onClick={() => setInlineEditContaId(null)} className="px-2 py-1 bg-gray-400 text-white rounded text-xs font-bold hover:bg-gray-500 whitespace-nowrap">Cancel</button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-between gap-2 mt-1">
                                      {plano.conta_bancaria && planosList.filter(p => p.conta_bancaria && p.conta_bancaria.trim() === plano.conta_bancaria.trim() && p.id !== plano.id).length > 0 ? (
                                        <p className="text-sm font-bold text-amber-700 font-mono flex-1">{plano.conta_bancaria} <AlertTriangle className="w-3 h-3 text-amber-500 inline" /></p>
                                      ) : (
                                        <p className="text-sm font-bold text-gray-900 font-mono flex-1">{plano.conta_bancaria || '—'}</p>
                                      )}
                                      {!((plano.validado ?? false)) && (
                                        <button
                                          onClick={() => { setInlineEditContaId(plano.id); setInlineContaValue(plano.conta_bancaria || ''); }}
                                          className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-bold hover:bg-blue-200 whitespace-nowrap"
                                          title="Editar conta bancária"
                                        >
                                          Editar
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Preenchido por</p>
                                  <p className="text-sm font-bold text-gray-900">{plano.created_by_name || '—'}</p>
                                  <p className="text-xs text-gray-500 truncate">{plano.created_by_email || ''}</p>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Data</p>
                                  <p className="text-sm font-bold text-gray-900">{new Date(plano.created_at).toLocaleDateString('pt-BR')}</p>
                                  {isAdmin() && plano.last_edited_at && (
                                    <p className="text-xs text-orange-600 font-semibold">Ed: {new Date(plano.last_edited_at).toLocaleDateString('pt-BR')}</p>
                                  )}
                                  {isAdmin() && plano.edit_count > 0 && (
                                    <span className="text-xs bg-orange-100 text-orange-700 font-bold px-1.5 py-0.5 rounded">{plano.edit_count}x</span>
                                  )}
                                  {((plano.validado ?? false)) && (
                                    <div className="mt-1">
                                      <span className="inline-flex items-center gap-1 text-[10px] bg-green-100 text-green-700 font-bold px-1.5 py-0.5 rounded-full border border-green-300" title={(plano.validado_em) ? `Validado em ${new Date(plano.validado_em).toLocaleDateString('pt-BR')}` : 'Plano validado'}>
                                        <CheckCircle2 className="w-2.5 h-2.5" /> Validado
                                      </span>
                                    </div>
                                  )}
                                  {plano.justificativa_alterada_em && (
                                    <div className="mt-1">
                                      <span className="inline-flex items-center gap-1 text-[10px] bg-purple-100 text-purple-700 font-bold px-1.5 py-0.5 rounded-full border border-purple-200" title={`Justificativa alterada em ${new Date(plano.justificativa_alterada_em).toLocaleDateString('pt-BR')} às ${new Date(plano.justificativa_alterada_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}>
                                        <Clock className="w-2.5 h-2.5" />
                                        Justif. alterada {new Date(plano.justificativa_alterada_em).toLocaleDateString('pt-BR')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                            </div>
                            {/* Menu Hambúrguer - Todas as ações */}
                            <div className="flex-shrink-0">
                              <div className="relative" ref={openMenuPlanoId === plano.id ? menuRef : undefined}>
                                <button
                                  onClick={() => setOpenMenuPlanoId(openMenuPlanoId === plano.id ? null : plano.id)}
                                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg font-bold text-xs uppercase hover:bg-gray-200 transition-all border border-gray-300 shadow-sm"
                                  title="Mais opções"
                                >
                                  <ChevronDown className={`w-4 h-4 transition-transform ${openMenuPlanoId === plano.id ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {/* Menu Dropdown */}
                                {openMenuPlanoId === plano.id && (
                                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 overflow-hidden">
                                    {/* PDF */}
                                    {canViewPlan(plano.created_by) && (
                                      <button
                                        onClick={() => { handlePrintFromList(plano.id); setOpenMenuPlanoId(null); }}
                                        className="flex items-center gap-2 w-full px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm font-semibold"
                                      >
                                        <Printer className="w-4 h-4" /> Gerar PDF
                                      </button>
                                    )}
                                    
                                    {/* Upload Extrato */}
                                    {!((plano.validado ?? false)) && (
                                      <button
                                        onClick={() => { setInlineExtratoUploadId(plano.id); setTimeout(() => inlineExtratoInputRef.current?.click(), 50); setOpenMenuPlanoId(null); }}
                                        disabled={isUploadingInlineExtrato && inlineExtratoUploadId === plano.id}
                                        className="flex items-center gap-2 w-full px-4 py-2.5 text-teal-600 hover:bg-teal-50 transition-colors border-b border-gray-100 text-sm font-semibold disabled:opacity-60"
                                      >
                                        {isUploadingInlineExtrato && inlineExtratoUploadId === plano.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <UploadCloud className="w-4 h-4" />
                                        )}
                                        {plano.extrato_url ? '✓ Substituir' : 'Enviar'} Extrato
                                      </button>
                                    )}
                                    
                                    {/* Ver Extrato */}
                                    {plano.extrato_url && (
                                      <button
                                        onClick={() => { handleViewExtrato(plano.extrato_url); setOpenMenuPlanoId(null); }}
                                        className="flex items-center gap-2 w-full px-4 py-2.5 text-blue-600 hover:bg-blue-50 transition-colors border-b border-gray-100 text-sm font-semibold"
                                      >
                                        <Eye className="w-4 h-4" /> Ver Extrato
                                      </button>
                                    )}
                                    
                                    {/* Download Extrato */}
                                    {plano.extrato_url && (
                                      <button
                                        onClick={() => { handleDownloadExtrato(plano.extrato_url, plano.extrato_filename); setOpenMenuPlanoId(null); }}
                                        className="flex items-center gap-2 w-full px-4 py-2.5 text-green-600 hover:bg-green-50 transition-colors border-b border-gray-100 text-sm font-semibold"
                                      >
                                        <Download className="w-4 h-4" /> Baixar Extrato
                                      </button>
                                    )}
                                    
                                    {/* Editar Plano */}
                                    {canEditPlan(plano.created_by, plano.validado ?? false) && (
                                      <button 
                                        onClick={() => { 
                                          setFormData(getInitialFormData());
                                          setLastSavedFormData(null);
                                          setPlanoSalvoId(null);
                                          setSelectedOfertaEmendaId(null);
                                          setFormHasChanges(false);
                                          setCurrentSelection({ categoria: '', item: '', metas: [''] });
                                          setCurrentMetaQualitativa({ meta: '', valor: '' });
                                          setCurrentNatureza({ codigo: '', valor: '' });
                                          setCurrentView('new'); 
                                          setActiveSection('info-emenda'); 
                                          setSentSuccess(false);
                                          loadingPlanIdRef.current = null;
                                          planLoadCompletedRef.current.delete(plano.id);
                                          setOpenMenuPlanoId(null);
                                          setTimeout(() => { setEditingPlanId(plano.id); }, 50);
                                        }}
                                        className="flex items-center gap-2 w-full px-4 py-2.5 text-orange-600 hover:bg-orange-50 transition-colors border-b border-gray-100 text-sm font-semibold"
                                      >
                                        <Settings2 className="w-4 h-4" /> Editar
                                      </button>
                                    )}
                                    
                                    {/* Validar (Admin) */}
                                    {isAdmin() && (
                                      <button
                                        onClick={() => { handleValidarPlano(plano.id, plano.validado ?? false); setOpenMenuPlanoId(null); }}
                                        disabled={validandoPlanoId === plano.id}
                                        className={`flex items-center gap-2 w-full px-4 py-2.5 hover:bg-opacity-50 transition-colors border-b border-gray-100 text-sm font-semibold disabled:opacity-60 ${
                                          (plano.validado ?? false)
                                            ? 'text-green-600 hover:bg-green-50'
                                            : 'text-purple-600 hover:bg-purple-50'
                                        }`}
                                      >
                                        {validandoPlanoId === plano.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <CheckCircle2 className="w-4 h-4" />
                                        )}
                                        {(plano.validado ?? false) ? '✓ Desvalidar' : 'Validar'}
                                      </button>
                                    )}
                                    
                                    {/* Deletar (Admin) */}
                                    {isAdmin() && (
                                      <button 
                                        onClick={() => { deletePlan(plano.id); setOpenMenuPlanoId(null); }}
                                        className="flex items-center gap-2 w-full px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors text-sm font-semibold"
                                      >
                                        <Trash2 className="w-4 h-4" /> Deletar
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* VIEW: DASHBOARD */}
          {currentView === 'dashboard' && (
            !(isAdmin() || currentUser?.role === 'intermediate') ? (
              <div className="text-center py-20 bg-red-50 rounded-2xl border-2 border-red-200 p-8">
                <Lock className="w-20 h-20 text-red-600 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-red-900 mb-4">Acesso negado</h2>
                <p className="text-lg text-red-700 mb-6">O Dashboard é exclusivo para administradores e usuários intermediários.</p>
                <p className="text-sm text-red-600 mb-8">Contacte um administrador se você acredita que deveria ter acesso.</p>
                <Button
                  label="Voltar para Meus Planos"
                  onClick={() => setCurrentView('list')}
                  variant="primary"
                />
              </div>
            ) : (() => {
              // Aplicar filtros de dashboard
              const dashPlanos = planosList.filter(p => {
                if (dashboardFiltros.programa && p.programa !== dashboardFiltros.programa) return false;
                if (dashboardFiltros.cnes && !(p.cnes || '').toLowerCase().includes(dashboardFiltros.cnes.toLowerCase())) return false;
                if (dashboardFiltros.periodo) {
                  const [year, month] = dashboardFiltros.periodo.split('-').map(Number);
                  const d = new Date(p.created_at);
                  if (d.getFullYear() !== year || (d.getMonth() + 1) !== month) return false;
                }
                return true;
              });

              const totalValor = dashPlanos.reduce((s, p) => s + (parseFloat(p.valor_total) || 0), 0);
              const avgValor = dashPlanos.length > 0 ? totalValor / dashPlanos.length : 0;
              const planosComJustifAlterada = dashPlanos.filter(p => p.justificativa_alterada_em);
              const planosEditados = dashPlanos.filter(p => p.edit_count > 0);
              const planosMac = dashPlanos.filter(p => p.programa === dynamicProgramas[0]);

              // Agrupar por programa com valor
              const programaStats = dynamicProgramas.map(prog => {
                const pts = dashPlanos.filter(p => p.programa === prog);
                return { programa: prog, count: pts.length, valor: pts.reduce((s, p) => s + (parseFloat(p.valor_total) || 0), 0) };
              }).filter(ps => ps.count > 0).sort((a, b) => b.valor - a.valor);

              // Top parlamentares
              const parlamentarMap: Record<string, { count: number; valor: number }> = {};
              dashPlanos.forEach(p => {
                const k = p.parlamentar || 'Sem nome';
                if (!parlamentarMap[k]) parlamentarMap[k] = { count: 0, valor: 0 };
                parlamentarMap[k].count++;
                parlamentarMap[k].valor += parseFloat(p.valor_total) || 0;
              });
              const topParlamentares = Object.entries(parlamentarMap)
                .sort(([, a], [, b]) => b.valor - a.valor).slice(0, 8);

              // Agrupar por CNES
              const cnesMap: Record<string, { count: number; valor: number }> = {};
              dashPlanos.forEach(p => {
                const k = p.cnes || '—';
                if (!cnesMap[k]) cnesMap[k] = { count: 0, valor: 0 };
                cnesMap[k].count++;
                cnesMap[k].valor += parseFloat(p.valor_total) || 0;
              });
              const topCnes = Object.entries(cnesMap)
                .sort(([, a], [, b]) => b.count - a.count).slice(0, 6);

              const hasFiltros = dashboardFiltros.programa || dashboardFiltros.cnes || dashboardFiltros.periodo;

              return (
                <div className="space-y-6 animate-fadeIn">
                  {/* TÍTULO + ACÕES */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-black text-gray-900 tracking-tight">Dashboard Analítico</h2>
                      <p className="text-sm text-gray-500 mt-0.5">Visão consolidada dos planos de trabalho da SES-SP</p>
                    </div>
                    <button onClick={() => loadPlanos()}
                      className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl font-bold text-xs hover:bg-gray-50 shadow-sm hover:shadow transition-all">
                      <RefreshCw className="w-3.5 h-3.5" /> Atualizar
                    </button>
                  </div>

                  {/* FILTROS */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Filter className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-black text-gray-700 uppercase tracking-wider">Filtros</span>
                      {hasFiltros && (
                        <button onClick={() => setDashboardFiltros({ programa: '', cnes: '', periodo: '' })}
                          className="ml-auto text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5" /> Limpar filtros
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-1.5">Programa</label>
                        <select
                          value={dashboardFiltros.programa}
                          onChange={e => setDashboardFiltros(f => ({ ...f, programa: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 bg-gray-50 font-semibold text-gray-700"
                        >
                          <option value="">Todos os programas</option>
                          {dynamicProgramas.map(p => <option key={p} value={p}>{p.length > 60 ? p.slice(0, 60) + '...' : p}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-1.5">CNES</label>
                        <input
                          type="text"
                          value={dashboardFiltros.cnes}
                          onChange={e => setDashboardFiltros(f => ({ ...f, cnes: e.target.value }))}
                          placeholder="Filtrar por CNES..."
                          className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-gray-600 uppercase tracking-wider block mb-1.5">Mês/Ano de Cadastro</label>
                        <input
                          type="month"
                          value={dashboardFiltros.periodo}
                          onChange={e => setDashboardFiltros(f => ({ ...f, periodo: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 bg-gray-50"
                        />
                      </div>
                    </div>
                    {hasFiltros && (
                      <p className="text-xs text-amber-600 font-bold mt-3 flex items-center gap-1.5">
                        <Bell className="w-3 h-3" /> Mostrando {dashPlanos.length} de {planosList.length} planos filtrados
                      </p>
                    )}
                  </div>

                  {isLoadingPlanos ? (
                    <div className="flex flex-col items-center justify-center py-24 gap-4">
                      <Loader2 className="w-14 h-14 text-red-600 animate-spin" />
                      <p className="text-gray-500 font-semibold">Carregando dados...</p>
                    </div>
                  ) : (
                    <>
                      {/* KPIs — Linha 1 */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Total Planos */}
                        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 shadow-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="p-2 bg-white/10 rounded-xl">
                              <FileCheck className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-3xl font-black text-white">{dashPlanos.length}</span>
                          </div>
                          <p className="text-xs font-bold text-gray-300 uppercase tracking-widest">Planos Cadastrados</p>
                          {hasFiltros && <p className="text-[10px] text-gray-500 mt-0.5">de {planosList.length} no total</p>}
                        </div>

                        {/* Valor Total */}
                        <div className="bg-gradient-to-br from-red-700 to-red-600 rounded-2xl p-5 shadow-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="p-2 bg-white/10 rounded-xl">
                              <DollarSign className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-lg font-black text-white leading-tight text-right">{totalValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}</span>
                          </div>
                          <p className="text-xs font-bold text-red-200 uppercase tracking-widest">Valor Total</p>
                        </div>

                        {/* Média por Plano */}
                        <div className="bg-gradient-to-br from-blue-700 to-blue-600 rounded-2xl p-5 shadow-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="p-2 bg-white/10 rounded-xl">
                              <BarChart3 className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-base font-black text-white leading-tight text-right">{avgValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}</span>
                          </div>
                          <p className="text-xs font-bold text-blue-200 uppercase tracking-widest">Valor Médio/Plano</p>
                        </div>

                        {/* % MAC */}
                        <div className="bg-gradient-to-br from-emerald-700 to-emerald-600 rounded-2xl p-5 shadow-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div className="p-2 bg-white/10 rounded-xl">
                              <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-3xl font-black text-white">{dashPlanos.length > 0 ? ((planosMac.length / dashPlanos.length) * 100).toFixed(0) : 0}%</span>
                          </div>
                          <p className="text-xs font-bold text-emerald-200 uppercase tracking-widest">Custeio MAC</p>
                          <p className="text-[10px] text-emerald-300 mt-0.5">{planosMac.length} plano(s)</p>
                        </div>
                      </div>

                      {/* KPIs — Linha 2 (detalhes de status) */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm flex items-center gap-4">
                          <div className="p-3 bg-orange-100 rounded-xl flex-shrink-0">
                            <Activity className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-black text-gray-900">{planosEditados.length}</p>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Com Edições</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm flex items-center gap-4">
                          <div className="p-3 bg-purple-100 rounded-xl flex-shrink-0">
                            <Clock className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-black text-gray-900">{planosComJustifAlterada.length}</p>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Justif. Alteradas</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm flex items-center gap-4">
                          <div className="p-3 bg-teal-100 rounded-xl flex-shrink-0">
                            <MapPin className="w-5 h-5 text-teal-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-black text-gray-900">{Object.keys(cnesMap).length}</p>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">CNES Únicos</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm flex items-center gap-4">
                          <div className="p-3 bg-amber-100 rounded-xl flex-shrink-0">
                            <Users className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-black text-gray-900">{Object.keys(parlamentarMap).length}</p>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Parlamentares</p>
                          </div>
                        </div>
                      </div>

                      {/* LINHA PRINCIPAL: Programas + Parlamentares */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Distribuição por Programa */}
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                              <Layers className="w-4 h-4 text-gray-600" />
                            </div>
                            <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">Distribuição por Programa</h3>
                          </div>
                          <div className="p-6 space-y-4">
                            {programaStats.length === 0 ? (
                              <p className="text-sm text-gray-400 text-center py-6">Nenhum programa encontrado</p>
                            ) : programaStats.map((ps, idx) => {
                              const pct = totalValor > 0 ? (ps.valor / totalValor * 100) : 0;
                              const colors = ['red', 'blue', 'emerald', 'amber'];
                              const c = colors[idx % colors.length];
                              const barColors: Record<string, string> = { red: 'bg-red-500', blue: 'bg-blue-500', emerald: 'bg-emerald-500', amber: 'bg-amber-500' };
                              const textColors: Record<string, string> = { red: 'text-red-600', blue: 'text-blue-600', emerald: 'text-emerald-600', amber: 'text-amber-600' };
                              const bgColors: Record<string, string> = { red: 'bg-red-50', blue: 'bg-blue-50', emerald: 'bg-emerald-50', amber: 'bg-amber-50' };
                              return (
                                <div key={ps.programa} className={`${bgColors[c]} rounded-xl p-4 border border-gray-100`}>
                                  <div className="flex items-start justify-between mb-2 gap-2">
                                    <p className="text-xs font-bold text-gray-800 leading-tight flex-1">{ps.programa.length > 55 ? ps.programa.slice(0, 55) + '...' : ps.programa}</p>
                                    <span className={`${textColors[c]} text-sm font-black flex-shrink-0`}>{pct.toFixed(1)}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                                    <div className={`${barColors[c]} h-1.5 rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                                  </div>
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <span>{ps.count} plano(s)</span>
                                    <span className="font-bold">{ps.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Top Parlamentares */}
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                              <Award className="w-4 h-4 text-gray-600" />
                            </div>
                            <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">Top Parlamentares</h3>
                          </div>
                          <div className="divide-y divide-gray-50">
                            {topParlamentares.length === 0 ? (
                              <p className="text-sm text-gray-400 text-center py-6">Nenhum dado</p>
                            ) : topParlamentares.map(([parl, stats], i) => (
                              <div key={parl} className="flex items-center gap-4 px-6 py-3.5 hover:bg-gray-50 transition-colors">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 ${i === 0 ? 'bg-amber-400 text-amber-900' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-orange-300 text-orange-800' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-bold text-gray-900 truncate">{parl}</p>
                                  <p className="text-xs text-gray-400">{stats.count} plano(s)</p>
                                </div>
                                <p className="text-sm font-black text-red-600 flex-shrink-0">{stats.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* LINHA: CNES + Justificativas Alteradas */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* CNES Breakdown */}
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                            <div className="p-2 bg-teal-50 rounded-lg">
                              <MapPin className="w-4 h-4 text-teal-600" />
                            </div>
                            <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">Distribuição por CNES</h3>
                          </div>
                          {topCnes.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-6">Nenhum dado</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-gray-50 border-b border-gray-100">
                                    <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">CNES</th>
                                    <th className="px-5 py-3 text-center text-xs font-black text-gray-600 uppercase tracking-wider">Planos</th>
                                    <th className="px-5 py-3 text-right text-xs font-black text-gray-600 uppercase tracking-wider">Valor Total</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                  {topCnes.map(([cnes, stats]) => (
                                    <tr key={cnes} className="hover:bg-teal-50/50 transition-colors">
                                      <td className="px-5 py-3 font-mono text-sm font-bold text-gray-900">{cnes}</td>
                                      <td className="px-5 py-3 text-center">
                                        <span className="inline-flex items-center justify-center w-7 h-7 bg-teal-100 text-teal-700 font-black text-xs rounded-full">{stats.count}</span>
                                      </td>
                                      <td className="px-5 py-3 text-right text-sm font-bold text-gray-800">{stats.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        {/* Justificativas Alteradas */}
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                            <div className="p-2 bg-purple-50 rounded-lg">
                              <Clock className="w-4 h-4 text-purple-600" />
                            </div>
                            <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">Justificativas Alteradas</h3>
                            <span className="ml-auto inline-flex items-center justify-center px-2.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-black rounded-full">{planosComJustifAlterada.length}</span>
                          </div>
                          {planosComJustifAlterada.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-400">
                              <CheckCircle2 className="w-10 h-10 text-green-300" />
                              <p className="text-sm font-semibold">Nenhuma justificativa alterada</p>
                            </div>
                          ) : (
                            <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
                              {planosComJustifAlterada
                                .sort((a, b) => new Date(b.justificativa_alterada_em).getTime() - new Date(a.justificativa_alterada_em).getTime())
                                .map(plano => (
                                  <div key={plano.id} className="flex items-start gap-3 px-5 py-3.5 hover:bg-purple-50/50 transition-colors">
                                    <div className="p-1.5 bg-purple-100 rounded-lg flex-shrink-0 mt-0.5">
                                      <Clock className="w-3.5 h-3.5 text-purple-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-bold text-gray-900 truncate">{plano.numero_emenda} — {plano.parlamentar}</p>
                                      <p className="text-xs text-gray-500">{plano.created_by_name || '—'}</p>
                                    </div>
                                    <p className="text-xs font-semibold text-purple-600 flex-shrink-0 text-right leading-tight">
                                      {new Date(plano.justificativa_alterada_em).toLocaleDateString('pt-BR')}<br/>
                                      <span className="text-[10px] text-gray-400">{new Date(plano.justificativa_alterada_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                                    </p>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* TABELA: Histórico de Edições */}
                      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                          <div className="p-2 bg-orange-50 rounded-lg">
                            <Activity className="w-4 h-4 text-orange-600" />
                          </div>
                          <h3 className="text-sm font-black text-gray-900 uppercase tracking-wider">Histórico de Edições Recentes</h3>
                          <span className="ml-auto inline-flex items-center justify-center px-2.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-black rounded-full">{planosEditados.length} editado(s)</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">Emenda</th>
                                <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">Parlamentar</th>
                                <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">CNES</th>
                                <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">Preenchedor</th>
                                <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">Cadastro</th>
                                <th className="px-5 py-3 text-center text-xs font-black text-gray-600 uppercase tracking-wider">Edições</th>
                                <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">Última Edição</th>
                                <th className="px-5 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider">Just. Alterada</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {dashPlanos
                                .filter(p => p.edit_count !== undefined)
                                .sort((a, b) => new Date(b.last_edited_at || b.created_at).getTime() - new Date(a.last_edited_at || a.created_at).getTime())
                                .slice(0, 15)
                                .map(plano => (
                                  <tr key={plano.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-5 py-3 font-mono font-bold text-gray-900 text-xs">{plano.numero_emenda}</td>
                                    <td className="px-5 py-3 text-xs text-gray-700 max-w-[140px] truncate">{plano.parlamentar}</td>
                                    <td className="px-5 py-3 font-mono text-xs text-gray-500">{plano.cnes || '—'}</td>
                                    <td className="px-5 py-3 text-xs text-gray-600 max-w-[120px] truncate">{plano.created_by_name || '—'}</td>
                                    <td className="px-5 py-3 text-xs text-gray-500">{new Date(plano.created_at).toLocaleDateString('pt-BR')}</td>
                                    <td className="px-5 py-3 text-center">
                                      {plano.edit_count > 0 ? (
                                        <span className="inline-flex items-center justify-center px-2 py-0.5 bg-orange-100 text-orange-700 font-black text-xs rounded-full">{plano.edit_count}</span>
                                      ) : (
                                        <span className="text-gray-300 text-xs">—</span>
                                      )}
                                    </td>
                                    <td className="px-5 py-3 text-xs text-gray-500">{plano.last_edited_at ? new Date(plano.last_edited_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                    <td className="px-5 py-3">
                                      {plano.justificativa_alterada_em ? (
                                        <span className="inline-flex items-center gap-1 text-[10px] bg-purple-100 text-purple-700 font-bold px-2 py-0.5 rounded-full border border-purple-200">
                                          <Clock className="w-2.5 h-2.5" />
                                          {new Date(plano.justificativa_alterada_em).toLocaleDateString('pt-BR')}
                                        </span>
                                      ) : (
                                        <span className="text-gray-300 text-xs">—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                          {dashPlanos.filter(p => p.edit_count !== undefined).length === 0 && (
                            <div className="text-center py-10 text-gray-400">
                              <p className="text-sm">Nenhum plano encontrado</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* FOOTER INFO */}
                      <div className="flex items-center justify-between text-xs text-gray-400 pb-2">
                        <span>Atualizado em {new Date().toLocaleString('pt-BR')}</span>
                        <span>{dashPlanos.length} plano(s) exibido(s) • Valor total: R$ {totalValor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })()
          )}
          {/* VIEW: NOVO PLANO - ONE PAGE DESIGN */}
          {currentView === 'new' && (
            <>
              {/* BLOQUEIO: usuário sem emenda disponível e não está editando */}
              {currentUser?.role === 'user' && !editingPlanId && !planoSalvoId && emendasDisponiveisFiltradas.length === 0 && !isLoadingEmendasDisponiveis ? (
                <div className="flex flex-col items-center justify-center py-16 px-4 text-center animate-fadeIn">
                  <div className="w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mb-6">
                    <Landmark className="w-10 h-10 text-[#0B3B8C]" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-black text-gray-800 mb-3">Aguardando disponibilização de emenda</h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl px-6 py-5 max-w-md mb-8 text-left">
                    <p className="text-amber-800 text-sm font-semibold mb-1">Informativo</p>
                    <p className="text-amber-700 text-sm leading-relaxed">
                      Novos Planos de Trabalho somente serão iniciados após a disponibilização de emenda pelo
                      <span className="font-black"> Grupo de Controle Financeiro — Emendas Federais</span>.
                    </p>
                    <p className="text-amber-600 text-xs mt-2">
                      Quando uma emenda for disponibilizada para o seu CNES, você receberá acesso automático ao formulário.
                    </p>
                  </div>
                  <button
                    onClick={() => setCurrentView('list')}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors"
                  >
                    Ver Meus Planos
                  </button>
                </div>
              ) : (
              <>
              <StepperProgress 
                steps={sections.map((s, i) => {
                  const shortLabels = ['Emenda', 'Beneficiário', 'Estratégia', 'Metas', 'Indicadores', 'Financeiro', 'Finalizar'];
                  return {
                    id: s.id,
                    label: shortLabels[i] || `Etapa ${i + 1}`,
                    title: s.title
                  };
                })}
                activeStep={activeSection}
                onStepClick={scrollToSection}
                completedSteps={Object.entries(sectionStatus)
                  .filter(([_, isComplete]) => isComplete)
                  .map(([id, _]) => id)}
              />
              
              <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 2rem' }}>
                <div className="space-y-8">
                {/* ──── CARD COPILOT: Nova Emenda Disponível ──── */}
                {currentUser?.role === 'user' && (
                  isLoadingEmendasDisponiveis ? (
                    <div className="flex items-center gap-3 text-sm text-gray-400 py-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Verificando emendas disponíveis...
                    </div>
                  ) : emendasDisponiveisFiltradas.length === 1 ? (() => {
                    const oferta = emendasDisponiveisFiltradas[0];
                    const isSel = selectedOfertaEmendaId === oferta.id;
                    return (
                      <div
                        className="bg-white rounded-2xl border border-[#E5E7EB] p-6 max-w-[900px] mx-auto"
                        style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', fontFamily: "Inter,'Segoe UI',sans-serif" }}
                        id="emendas-disponiveis"
                        role="region"
                        aria-label="Nova Emenda Disponível"
                      >
                        {/* Cabeçalho do card */}
                        <div className="flex items-start justify-between gap-4 mb-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                              <Bell className="w-5 h-5 text-[#0B3B8C]" />
                            </div>
                            <div>
                              <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider leading-none mb-1">Nova Emenda Disponível</p>
                              <p className="text-[17px] font-bold text-gray-900 leading-tight">{oferta.parlamentar}</p>
                            </div>
                          </div>
                          {isSel && (
                            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full flex-shrink-0">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Selecionada
                            </span>
                          )}
                        </div>

                        {/* Divisor */}
                        <div className="h-px bg-[#F3F4F6] mb-5" />

                        {/* Grid de informações */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4 mb-6">
                          {oferta.tipo_parlamentar && (
                            <div>
                              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Tipo</p>
                              <p className="text-[13px] font-semibold text-gray-900">{oferta.tipo_parlamentar}</p>
                            </div>
                          )}
                          {oferta.numero_emenda && (
                            <div>
                              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Nº da Emenda</p>
                              <p className="text-[13px] font-semibold text-gray-900">{oferta.numero_emenda}</p>
                            </div>
                          )}
                          {oferta.programa && (
                            <div className="col-span-2 sm:col-span-1">
                              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Programa</p>
                              <p className="text-[13px] font-semibold text-gray-900 leading-snug">{oferta.programa}</p>
                            </div>
                          )}
                          <div className="col-span-2">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Entidade Beneficiada</p>
                            <p className="text-[14px] font-bold text-gray-900">{oferta.entidade}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">CNES</p>
                            <p className="text-[13px] font-mono font-semibold text-gray-900">{oferta.cnes}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">CNPJ</p>
                            <p className="text-[13px] font-mono font-semibold text-gray-900">{oferta.cnpj}</p>
                          </div>
                        </div>

                        {/* Rodapé: valor + botão */}
                        <div className="flex items-center justify-between gap-4 flex-wrap pt-5 border-t border-[#F3F4F6]">
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Valor Disponível</p>
                            <p className="text-2xl font-black text-gray-900">R$ {formatCurrencyDisplay(oferta.valor)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => aplicarEmendaDisponibilizadaNoFormulario(oferta)}
                            className={`h-12 px-6 rounded-xl text-[14px] font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                              isSel
                                ? 'bg-green-600 hover:bg-green-700 text-white focus-visible:ring-green-500'
                                : 'bg-[#2563EB] hover:bg-[#1D4ED8] text-white focus-visible:ring-blue-500'
                            }`}
                            aria-label={isSel ? 'Alterar seleção de emenda' : 'Gerar Plano de Trabalho automaticamente com esta emenda'}
                          >
                            {isSel ? '✓ Selecionada — Alterar' : 'Iniciar Plano de Trabalho →'}
                          </button>
                        </div>
                      </div>
                    );
                  })() : emendasDisponiveisFiltradas.length > 1 ? (
                    <div className="max-w-[900px] mx-auto space-y-3" id="emendas-disponiveis" style={{ fontFamily: "Inter,'Segoe UI',sans-serif" }}>
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                        <Bell className="w-3.5 h-3.5" aria-hidden="true" />
                        {emendasDisponiveisFiltradas.length} emendas disponíveis para sua entidade
                      </p>
                      {emendasDisponiveisFiltradas.map((oferta: any) => {
                        const isSel = selectedOfertaEmendaId === oferta.id;
                        return (
                          <div
                            key={oferta.id}
                            className={`bg-white rounded-xl border p-4 flex items-center justify-between gap-4 transition-all ${
                              isSel ? 'border-green-400 ring-2 ring-green-400/20' : 'border-[#E5E7EB] hover:border-blue-300'
                            }`}
                            style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                {oferta.numero_emenda && (
                                  <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Nº {oferta.numero_emenda}</span>
                                )}
                                {oferta.tipo_parlamentar && (
                                  <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">{oferta.tipo_parlamentar}</span>
                                )}
                              </div>
                              <p className="text-[13px] font-bold text-gray-900 truncate">{oferta.parlamentar}</p>
                              <p className="text-[12px] text-gray-500 truncate">{oferta.entidade} · CNES {oferta.cnes}</p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <p className="text-[15px] font-black text-gray-900 hidden sm:block">R$ {formatCurrencyDisplay(oferta.valor)}</p>
                              <button
                                type="button"
                                onClick={() => aplicarEmendaDisponibilizadaNoFormulario(oferta)}
                                className={`h-9 px-4 rounded-lg text-xs font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                                  isSel
                                    ? 'bg-green-600 text-white focus-visible:ring-green-500'
                                    : 'bg-[#2563EB] text-white hover:bg-[#1D4ED8] focus-visible:ring-blue-500'
                                }`}
                                aria-label={`${isSel ? 'Selecionada' : 'Usar'}: emenda de ${oferta.parlamentar}`}
                              >
                                {isSel ? '✓ Selecionada' : 'Usar →'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null
                )}

                {/* SECTION 1: IDENTIFICAÇÃO DA EMENDA */}
                <Section
                  id="info-emenda"
                  title="Identificação da Emenda"
                  description="Dados básicos da emenda parlamentar"
                  icon={<FileText className="w-6 h-6" />}
                  step={1}
                  totalSteps={7}
                  isComplete={sectionStatus['info-emenda']}
                  onHelpClick={() => openHelpModal('info-emenda')}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <Select
                      label="Programa Orçamentário"
                      value={formData.emenda.programa}
                      onChange={(e) => updateFormData('emenda', { ...formData.emenda, programa: e.target.value })}
                      options={dynamicProgramas.map(p => ({ value: p, label: p }))}
                      required
                      disabled={!!selectedOfertaEmendaId && currentUser?.role === 'user'}
                    />
                    <InputField
                      label="Parlamentar Autor"
                      name="parlamentar"
                      value={formData.emenda.parlamentar}
                      onChange={(e) => updateFormData('emenda', { ...formData.emenda, parlamentar: e.target.value })}
                      placeholder="Ex: Deputado Federal João Silva"
                      required
                      readOnly={!!selectedOfertaEmendaId && currentUser?.role === 'user'}
                    />
                    <InputField
                      label="Nº da Emenda"
                      name="numero"
                      value={formData.emenda.numero}
                      onChange={(e) => updateFormData('emenda', { ...formData.emenda, numero: e.target.value })}
                      placeholder="Ex: 12340001"
                      required
                      readOnly={!!selectedOfertaEmendaId && currentUser?.role === 'user'}
                    />
                    <InputField
                      label="Valor do Recurso (R$)"
                      name="valor"
                      type="text"
                      value={formData.emenda.valor}
                      onChange={(e) => updateFormData('emenda', { ...formData.emenda, valor: maskCurrency(e.target.value) })}
                      mask={(val: string) => maskCurrency(val)}
                      placeholder="R$ 0,00"
                      required
                      readOnly={!!selectedOfertaEmendaId && currentUser?.role === 'user'}
                    />
                  </div>
                </Section>

                {/* SECTION 2: BENEFICIÁRIO */}
                <Section
                  id="beneficiario"
                  title="Dados do Beneficiário"
                  description="Informações da unidade beneficiária"
                  icon={<Building2 className="w-6 h-6" />}
                  step={2}
                  totalSteps={7}
                  isComplete={sectionStatus['beneficiario']}
                  onHelpClick={() => openHelpModal('beneficiario')}
                >
                  <div className="space-y-6">
                    <InputField
                      label="Razão Social da Unidade"
                      name="nome"
                      value={formData.beneficiario.nome}
                      onChange={(e) => updateFormData('beneficiario', { ...formData.beneficiario, nome: e.target.value })}
                      placeholder="Exemplo: Hospital XYZ"
                      required
                      readOnly={!!selectedOfertaEmendaId && currentUser?.role === 'user'}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      <InputField
                        label="CNPJ"
                        name="cnpj"
                        value={formData.beneficiario.cnpj}
                        onChange={(e) => updateFormData('beneficiario', { ...formData.beneficiario, cnpj: maskCNPJ(e.target.value) })}
                        mask={(val: string) => maskCNPJ(val)}
                        placeholder="XX.XXX.XXX/XXXX-XX"
                        required
                        readOnly={!!selectedOfertaEmendaId && currentUser?.role === 'user'}
                      />
                      {/* CNES - Dropdown para usuários com múltiplos CNES */}
                      {(() => {
                        // Detectar múltiplos CNES de qualquer fonte
                        const hasMultipleCnes = (currentUser?.cnes || '').includes(',') || userCnesList.length > 1;
                        const cnesOptions = userCnesList.length > 1 ? userCnesList : parseCnesList(currentUser?.cnes);
                        
                        if (currentUser?.role === 'user' && hasMultipleCnes && cnesOptions.length > 1) {
                          return (
                            <div className="flex flex-col">
                              <label className="text-sm font-semibold text-gray-700 mb-2">
                                CNES <span className="text-xs text-gray-500 font-normal">({cnesOptions.length} unidades - selecione)</span>
                              </label>
                              <select
                                value={selectedCnes || cnesOptions[0]}
                                onChange={(e) => {
                                  const newCnes = e.target.value;
                                  setSelectedCnes(newCnes);
                                  updateFormData('beneficiario', { ...formData.beneficiario, cnes: newCnes });
                                }}
                                className="w-full px-4 py-3 rounded-xl border-2 border-blue-300 bg-white text-gray-800 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-mono text-base"
                                title={`Seus CNES: ${cnesOptions.join(', ')}`}
                              >
                                {cnesOptions.map(cnes => (
                                  <option key={cnes} value={cnes}>{cnes}</option>
                                ))}
                              </select>
                            </div>
                          );
                        }
                        
                        return (
                          <InputField
                            label="CNES"
                            name="cnes"
                            value={formData.beneficiario.cnes}
                            onChange={(e) => {
                              if (currentUser?.role === 'admin') {
                                updateFormData('beneficiario', { ...formData.beneficiario, cnes: maskCNES(e.target.value) });
                              }
                            }}
                            mask={(val: string) => maskCNES(val)}
                            placeholder="Código CNES"
                            disabled={currentUser?.role === 'user' && !hasMultipleCnes}
                            title={`Seu CNES: ${selectedCnes || 'não preenchido'}`}
                          />
                        );
                      })()}
                      <InputField
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.beneficiario.email}
                        onChange={(e) => updateFormData('beneficiario', { ...formData.beneficiario, email: e.target.value })}
                        placeholder="email@example.com"
                      />
                    </div>
                    <InputField
                      label="Telefone"
                      name="telefone"
                      value={formData.beneficiario.telefone}
                      onChange={(e) => updateFormData('beneficiario', { ...formData.beneficiario, telefone: maskPhone(e.target.value) })}
                      mask={(val: string) => maskPhone(val)}
                      placeholder="(XX) 9XXXX-XXXX"
                    />

                    {/* CONTA BANCÁRIA E EXTRATO */}
                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <Landmark className="w-4 h-4 text-blue-600" />
                        Dados Bancários
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <InputField
                            label="Conta Bancária"
                            name="contaBancaria"
                            value={formData.beneficiario.contaBancaria}
                            onChange={(e) => updateFormData('beneficiario', { ...formData.beneficiario, contaBancaria: e.target.value })}
                            placeholder="Banco / Agência / Conta (Ex: 001 / 1234-5 / 12345-6)"
                            maxLength={25}
                            help="Informe somente números para banco, agência e conta."
                          />
                          {/* Alerta de conta bancária duplicada */}
                          {formData.beneficiario.contaBancaria?.trim() && planosList.some(p =>
                            p.conta_bancaria &&
                            p.conta_bancaria.trim() === formData.beneficiario.contaBancaria.trim() &&
                            p.id !== editingPlanId
                          ) && (
                            <div className="flex items-start gap-2 p-3 bg-amber-50 border-2 border-amber-400 rounded-xl -mt-8 mb-4">
                              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                              <p className="text-sm font-bold text-amber-800">
                                Não serão efetuados pagamentos de emendas que utilizem conta bancária previamente utilizada.
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-semibold text-gray-700 mb-2">
                            Extrato Bancário
                          </label>
                          {formData.beneficiario.extratoUrl ? (
                            <div className="flex items-center gap-3 p-3 bg-green-50 border-2 border-green-300 rounded-xl">
                              <Paperclip className="w-5 h-5 text-green-600 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-green-800 truncate">{formData.beneficiario.extratoFilename || 'Extrato enviado'}</p>
                                <p className="text-xs text-green-600">Arquivo enviado com sucesso</p>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleViewExtrato(formData.beneficiario.extratoUrl)}
                                  className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-all"
                                  title="Visualizar extrato"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={handleRemoveExtrato}
                                  className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                                  title="Remover extrato"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="relative">
                              <input
                                ref={extratoInputRef}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                onChange={handleExtratoUpload}
                                disabled={isUploadingExtrato}
                                className="hidden"
                                id="extrato-upload"
                              />
                              <label
                                htmlFor="extrato-upload"
                                className={`flex items-center justify-center gap-3 w-full px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                                  isUploadingExtrato
                                    ? 'border-gray-300 bg-gray-50 text-gray-400 cursor-not-allowed'
                                    : 'border-blue-300 bg-blue-50 text-blue-700 hover:border-blue-500 hover:bg-blue-100'
                                }`}
                              >
                                {isUploadingExtrato ? (
                                  <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span className="text-sm font-bold">Enviando...</span>
                                  </>
                                ) : (
                                  <>
                                    <UploadCloud className="w-5 h-5" />
                                    <span className="text-sm font-bold">Enviar Extrato</span>
                                    <span className="text-xs text-blue-500">(PDF, JPG, PNG - comprimido automaticamente)</span>
                                  </>
                                )}
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Section>

                {/* SECTION 3: ALINHAMENTO ESTRATÉGICO */}
                <Section
                  id="alinhamento"
                  title="Alinhamento Estratégico"
                  description="Escolha a diretriz, objetivo e metas do Plano Estadual de Saúde"
                  icon={<Target className="w-6 h-6" />}
                  step={3}
                  totalSteps={7}
                  isComplete={sectionStatus['alinhamento']}
                  onHelpClick={() => openHelpModal('alinhamento')}
                >
                  <div className="space-y-6">
                    {/* DIRETRIZES */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-4">
                        <span className="text-red-600">*</span> Diretriz Estratégica
                      </label>
                      <div className="grid gap-3">
                        {DIRETRIZES.map((d) => (
                          <button
                            key={d.id}
                            onClick={() => updateFormData('planejamento', { ...formData.planejamento, diretrizId: d.id, objetivoId: '', metaIds: [] })}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                              formData.planejamento.diretrizId === d.id
                                ? 'border-red-600 bg-red-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <span className="text-xs font-bold text-gray-600 block mb-1 uppercase tracking-widest">Diretriz {d.numero}</span>
                            <p className="text-sm text-gray-800 font-semibold">{d.titulo}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* OBJETIVOS */}
                    {selectedDiretriz && (
                      <div className="border-t-2 border-gray-200 pt-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-4">
                          <span className="text-red-600">*</span> Objetivo Específico
                        </label>
                        <div className="grid gap-3">
                          {selectedDiretriz.objetivos.map((obj) => (
                            <button
                              key={obj.id}
                              onClick={() => updateFormData('planejamento', { ...formData.planejamento, objetivoId: obj.id, metaIds: [] })}
                              className={`p-3 rounded-lg border-2 text-left transition-all ${
                                formData.planejamento.objetivoId === obj.id
                                  ? 'border-blue-600 bg-blue-100 shadow-md ring-2 ring-blue-300'
                                  : 'border-blue-200 bg-blue-50 hover:border-blue-400 hover:bg-blue-100'
                              }`}
                            >
                              <p className="text-sm text-gray-800 font-semibold">{obj.titulo}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* METAS */}
                    {selectedObjetivo && selectedObjetivo.metas.length > 0 && (
                      <div className="border-t-2 border-gray-200 pt-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-4">
                          <span className="text-red-600">*</span> Metas (Selecione uma ou mais)
                        </label>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3">
                          {selectedObjetivo.metas.map((meta) => (
                            <label
                              key={meta.id}
                              className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 cursor-pointer transition-all"
                            >
                              <input
                                type="checkbox"
                                checked={formData.planejamento.metaIds.includes(meta.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    updateFormData('planejamento', {
                                      ...formData.planejamento,
                                      metaIds: [...formData.planejamento.metaIds, meta.id]
                                    });
                                  } else {
                                    updateFormData('planejamento', {
                                      ...formData.planejamento,
                                      metaIds: formData.planejamento.metaIds.filter(id => id !== meta.id)
                                    });
                                  }
                                }}
                                className="w-5 h-5 text-blue-600 rounded cursor-pointer mt-1 flex-shrink-0"
                              />
                              <span className="text-sm text-gray-800 leading-relaxed">{meta.descricao}</span>
                            </label>
                          ))}
                        </div>
                        {formData.planejamento.metaIds.length > 0 && (
                          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-semibold text-blue-900">
                              ✓ {formData.planejamento.metaIds.length} meta(s) selecionada(s)
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Section>

                {/* SECTION 4: METAS QUANTITATIVAS */}
                <Section
                  id="metas-quantitativas"
                  title="Metas Quantitativas"
                  description="Defina as ações de serviço e metas quantitativas"
                  icon={<Settings2 className="w-6 h-6" />}
                  step={4}
                  totalSteps={7}
                  isComplete={sectionStatus['metas-quantitativas']}
                  onHelpClick={() => openHelpModal('metas-quantitativas')}
                >
                  <div className="space-y-6">
                    {/* AVISO 50% GRUPO VI/PORTARIA 10.169 */}
                    {formData.emenda.programa && formData.emenda.programa.includes('PORTARIA 10.169') &&
                      currentSelection.categoria && currentSelection.categoria.includes('VI - CUSTEIO DE OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE') && (
                        <div className="p-4 mb-2 rounded-lg bg-yellow-100 border-l-4 border-yellow-400 text-yellow-900 font-semibold">
                          Atenção: Para ações do Grupo VI - CUSTEIO DE OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE, você só poderá apontar até <b>50% do valor do recurso</b>.
                        </div>
                    )}
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select
                          label="Grupo de Ação"
                          value={currentSelection.categoria}
                          onChange={(e) => setCurrentSelection({ ...currentSelection, categoria: e.target.value, item: '', metas: [''] })}
                          options={availableMetasQuantitativas.map(cat => ({ value: cat.categoria, label: cat.categoria }))}
                        />
                        <Select
                          label="Ação Específica"
                          value={currentSelection.item}
                          onChange={(e) => setCurrentSelection({ ...currentSelection, item: e.target.value })}
                          options={availableMetasQuantitativas.find(c => c.categoria === currentSelection.categoria)?.itens.map(item => ({ value: item, label: item })) || []}
                          disabled={!currentSelection.categoria}
                        />
                      </div>

                      {currentSelection.categoria && currentSelection.item && (
                        <div className="border-t pt-4 btn-fade-in">
                          <Button
                            label="+ Adicionar Meta"
                            onClick={confirmAddAcao}
                            variant="primary"
                            size="md"
                          />
                        </div>
                      )}
                    </div>

                    {formData.acoesServicos.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600" /> Metas Adicionadas ({formData.acoesServicos.length})
                        </h4>
                        {formData.acoesServicos.map((acao, idx) => (
                          <div key={idx} className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">Grupo de Ação</p>
                              <p className="font-semibold text-gray-900 mb-2">{acao.categoria}</p>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">Ação Específica</p>
                              <p className="text-sm text-gray-700">{acao.item}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <InputField
                                label="Valor"
                                name={`valor-${idx}`}
                                type="text"
                                value={acao.valor}
                                onChange={(e) => {
                                  const recursoValue = parseCurrency(formData.emenda.valor);
                                  const isPortaria10169 = formData.emenda.programa && formData.emenda.programa.includes('PORTARIA 10.169');
                                  const isPortaria10352Custeio = formData.emenda.programa === PROGRAMA_EMENDA_COLETIVA_10352_CUSTEIO;
                                  const isGrupoVI = acao.categoria && acao.categoria.includes('VI - CUSTEIO DE OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE');
                                  const isOutrasAcoesMAC = acao.categoria === 'OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE';
                                  const newValorDigitado = parseCurrency(maskCurrency(e.target.value));
                                  // Calcula o novo total considerando o valor editado
                                  const totalMetas = formData.acoesServicos.reduce((sum, item, i) => {
                                    if (i === idx) return sum + newValorDigitado;
                                    return sum + parseCurrency(item.valor || '0');
                                  }, 0);
                                  const totalGrupoVI = formData.acoesServicos.reduce((sum, item, i) => {
                                    if (item.categoria && item.categoria.includes('VI - CUSTEIO DE OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE')) {
                                      if (i === idx) return sum + newValorDigitado;
                                      return sum + parseCurrency(item.valor || '0');
                                    }
                                    return sum;
                                  }, 0);
                                  const totalOutrasAcoes = formData.acoesServicos.reduce((sum, item, i) => {
                                    if (item.categoria === 'OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE') {
                                      if (i === idx) return sum + newValorDigitado;
                                      return sum + parseCurrency(item.valor || '0');
                                    }
                                    return sum;
                                  }, 0);
                                  // Validação Portaria 10.352 CUSTEIO + OUTRAS AÇÕES: máximo 70%
                                  if (isPortaria10352Custeio && isOutrasAcoesMAC) {
                                    if (totalOutrasAcoes > recursoValue * 0.7) {
                                      avisar(`⚠️ Atenção!\n\nPara OUTRAS AÇÕES DA MÉDIA E ALTA COMPLEXIDADE na Portaria 10.352, o valor total não pode ultrapassar 70% do valor do recurso (R$ ${(recursoValue * 0.7).toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`);
                                      return;
                                    }
                                  }
                                  // Validação Portaria 10.169 + Grupo VI: máximo 50%
                                  if (isPortaria10169 && isGrupoVI) {
                                    if (totalGrupoVI > recursoValue * 0.5) {
                                      avisar(`⚠️ Atenção!\n\nPara ações do Grupo VI, o valor total não pode ultrapassar 50% do valor do recurso (R$ ${(recursoValue * 0.5).toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`);
                                      return;
                                    }
                                  }
                                  // Validação geral: não ultrapassar valor do recurso
                                  if (totalMetas > recursoValue) {
                                    avisar(`⚠️ Atenção!\n\nO valor total das Metas Quantitativas não pode ultrapassar o Valor do Recurso (R$ ${recursoValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`);
                                    return;
                                  }
                                  const newAcoes = [...formData.acoesServicos];
                                  newAcoes[idx].valor = maskCurrency(e.target.value);
                                  updateFormData('acoesServicos', newAcoes);
                                }}
                                mask={(val: string) => maskCurrency(val)}
                                placeholder="R$ 0,00"
                                className="w-40"
                              />
                              <button
                                onClick={() => removeAcao(idx)}
                                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                        
                        {/* Sumário Total Metas Quantitativas */}
                        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-6">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-xs text-green-600 font-bold uppercase tracking-widest mb-2">Total Metas Quantitativas</p>
                              <p className="text-2xl font-bold text-gray-900">
                                {new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL'
                                }).format(
                                  formData.acoesServicos.reduce((sum, item) => {
                                    const value = parseFloat(item.valor.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
                                    return sum + value;
                                  }, 0)
                                )}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500 font-semibold uppercase tracking-widest mb-2">Valor do Recurso</p>
                              <p className="text-2xl font-bold text-blue-600">
                                {new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL'
                                }).format(parseFloat(formData.emenda.valor.replace(/[^\d,-]/g, '').replace(',', '.')) || 0)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Section>

                {/* SECTION 5: INDICADORES QUALITATIVOS */}
                <Section
                  id="metas-qualitativas"
                  title="Indicadores Qualitativos (Opcional)"
                  description="Adicione indicadores qualitativos complementares (opcional)"
                  icon={<BarChart3 className="w-6 h-6" />}
                  step={5}
                  totalSteps={7}
                  isComplete={sectionStatus['metas-qualitativas']}
                  onHelpClick={() => openHelpModal('metas-qualitativas')}
                >
                  <div className="space-y-6">
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                        <div className="md:col-span-8">
                          <Select
                            label="Indicador"
                            value={currentMetaQualitativa.meta}
                            onChange={(e) => setCurrentMetaQualitativa({ ...currentMetaQualitativa, meta: e.target.value })}
                            options={METAS_QUALITATIVAS_OPTIONS.map(o => ({ value: o, label: o }))}
                            hideBottomMargin={true}
                          />
                        </div>
                        <div className="md:col-span-4">
                          <div className="relative">
                            <InputField
                              label="Valor"
                              name="qual-temp-valor"
                              type="text"
                              value={currentMetaQualitativa.valor}
                              onChange={(e) => setCurrentMetaQualitativa({ ...currentMetaQualitativa, valor: maskPercentage(e.target.value) })}
                              placeholder="0"
                              hideBottomMargin={true}
                            />
                            <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium pointer-events-none" style={{ marginTop: '1.5rem' }}>%</span>
                          </div>
                        </div>
                        <div className="md:col-span-1"></div>
                      </div>

                      {currentMetaQualitativa.meta && currentMetaQualitativa.valor && (
                        <div className="border-t pt-4 btn-fade-in">
                          <Button
                            label="+ Adicionar Indicador"
                            onClick={confirmAddMetaQualitativa}
                            variant="primary"
                            size="md"
                          />
                        </div>
                      )}
                    </div>

                    {formData.metasQualitativas.length > 0 ? (
                      <div className="space-y-4">
                        {formData.metasQualitativas.map((item, idx) => (
                          <div 
                            key={idx} 
                            className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end bg-gradient-to-br from-gray-50 to-white p-5 rounded-xl border border-gray-200 hover:border-red-200 transition-colors duration-200"
                          >
                            <div className="md:col-span-8">
                              <Select
                                label="Indicador"
                                value={item.meta}
                                onChange={(e) => {
                                  const newMetas = [...formData.metasQualitativas];
                                  newMetas[idx].meta = e.target.value;
                                  updateFormData('metasQualitativas', newMetas);
                                }}
                                options={METAS_QUALITATIVAS_OPTIONS.map(o => ({ value: o, label: o }))}
                                hideBottomMargin={true}
                              />
                            </div>
                            <div className="md:col-span-3">
                              <div className="relative">
                                <InputField
                                  label="Valor"
                                  name={`qual-valor-${idx}`}
                                  type="text"
                                  value={item.valor}
                                  onChange={(e) => {
                                    const newMetas = [...formData.metasQualitativas];
                                    newMetas[idx].valor = maskPercentage(e.target.value);
                                    updateFormData('metasQualitativas', newMetas);
                                  }}
                                  placeholder="0"
                                  hideBottomMargin={true}
                                />
                                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium pointer-events-none" style={{ marginTop: '1.5rem' }}>%</span>
                              </div>
                            </div>
                            <div className="md:col-span-1 flex justify-center">
                              <button
                                onClick={() => removeMetaQualitativa(idx)}
                                className="p-3 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                title="Remover indicador"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
                        <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-400 font-medium">Nenhum indicador adicionado</p>
                        <p className="text-xs text-gray-300">Clique no botão acima para começar</p>
                      </div>
                    )}
                  </div>
                </Section>

                {/* SECTION 6: EXECUÇÃO FINANCEIRA */}
                <Section
                  id="execucao-financeira"
                  title="Execução Financeira"
                  description="Classifique os gastos por natureza de despesa"
                  icon={<DollarSign className="w-6 h-6" />}
                  step={6}
                  totalSteps={7}
                  isComplete={sectionStatus['execucao-financeira']}
                  onHelpClick={() => openHelpModal('execucao-financeira')}
                >
                  <div className="space-y-6">
                    {currentUser?.role === 'admin' && (
                      <div className="text-xs rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <p className="text-gray-500">
                          {treeDataError
                            ? 'Falha ao consultar o banco (usando lista padrão do sistema):'
                            : dynamicProgramaNaturezas[formData.emenda.programa]?.length
                              ? `Fonte: configuração do banco para este programa (${availableNaturezas.length} itens)`
                              : treeDataLoaded
                                ? `Fonte: banco — sem restrição configurada para este programa (${availableNaturezas.length} itens)`
                                : `Carregando configuração do banco… (${availableNaturezas.length} itens da lista padrão)`}
                        </p>
                        {treeDataError && treeDataErrorDetail && (
                          <pre className="mt-1 whitespace-pre-wrap break-all text-red-600 select-text">{treeDataErrorDetail}</pre>
                        )}
                      </div>
                    )}
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                        <div className="md:col-span-8">
                          <Select
                            label="Natureza de Despesa"
                            value={currentNatureza.codigo}
                            onChange={(e) => setCurrentNatureza({ ...currentNatureza, codigo: e.target.value })}
                            options={availableNaturezas.map((o: NaturezaDespesa) => ({ value: o.codigo, label: `${o.codigo} - ${o.descricao}` }))}
                            hideBottomMargin={true}
                          />
                        </div>
                        <div className="md:col-span-4">
                          <InputField
                            label="Valor"
                            name="nat-temp-valor"
                            type="text"
                            value={currentNatureza.valor}
                            onChange={(e) => setCurrentNatureza({ ...currentNatureza, valor: maskCurrency(e.target.value) })}
                            mask={(val: string) => maskCurrency(val)}
                            placeholder="R$ 0,00"
                            hideBottomMargin={true}
                          />
                        </div>
                        <div className="md:col-span-1"></div>
                      </div>

                      {currentUser?.role === 'admin' && (
                        <p className="text-xs text-gray-400">
                          {treeDataError
                            ? `Falha ao consultar o banco (usando lista padrão do sistema): ${treeDataErrorDetail || ''}`
                            : dynamicProgramaNaturezas[formData.emenda.programa]?.length
                              ? `Fonte: configuração do banco para este programa (${availableNaturezas.length} itens)`
                              : treeDataLoaded
                                ? `Fonte: banco — sem restrição configurada para este programa (${availableNaturezas.length} itens)`
                                : `Carregando configuração do banco… (${availableNaturezas.length} itens da lista padrão)`}
                        </p>
                      )}

                      {currentNatureza.codigo && currentNatureza.valor && (
                        <div className="border-t pt-4 btn-fade-in">
                          <Button
                            label="+ Adicionar Natureza de Despesa"
                            onClick={confirmAddNatureza}
                            variant="primary"
                            size="md"
                          />
                        </div>
                      )}
                    </div>

                    {formData.naturezasDespesa.length > 0 ? (
                      <div className="space-y-4">
                        {formData.naturezasDespesa.map((item, idx) => (
                          <div 
                            key={idx} 
                            className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end bg-gradient-to-br from-gray-50 to-white p-5 rounded-xl border border-gray-200 hover:border-red-200 transition-colors duration-200"
                          >
                            <div className="md:col-span-8">
                              <Select
                                label="Natureza de Despesa"
                                value={item.codigo}
                                onChange={(e) => {
                                  const newNaturezas = [...formData.naturezasDespesa];
                                  newNaturezas[idx].codigo = e.target.value;
                                  updateFormData('naturezasDespesa', newNaturezas);
                                }}
                                options={availableNaturezas.map((o: NaturezaDespesa) => ({ value: o.codigo, label: `${o.codigo} - ${o.descricao}` }))}
                                hideBottomMargin={true}
                              />
                            </div>
                            <div className="md:col-span-3">
                              <InputField
                                label="Valor"
                                name={`nat-valor-${idx}`}
                                type="text"
                                value={item.valor}
                                onChange={(e) => {
                                  const recursoValue = parseFloat(formData.emenda.valor.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
                                  const totalDespesas = formData.naturezasDespesa.reduce((sum, nat, i) => {
                                    if (i === idx) return sum + parseFloat(e.target.value.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
                                    const value = parseFloat(nat.valor.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
                                    return sum + value;
                                  }, 0);
                                  
                                  if (totalDespesas > recursoValue) {
                                    avisar(`⚠️ Atenção!\n\nO valor total da Execução Financeira não pode ultrapassar o Valor do Recurso (R$ ${recursoValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`);
                                    return;
                                  }
                                  
                                  const newNaturezas = [...formData.naturezasDespesa];
                                  newNaturezas[idx].valor = maskCurrency(e.target.value);
                                  updateFormData('naturezasDespesa', newNaturezas);
                                }}
                                mask={(val: string) => maskCurrency(val)}
                                placeholder="R$ 0,00"
                                hideBottomMargin={true}
                              />
                            </div>
                            <div className="md:col-span-1 flex justify-center">
                              <button
                                onClick={() => removeNatureza(idx)}
                                className="p-3 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                title="Remover natureza de despesa"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        ))}

                        {/* Sumário de totais com validação */}
                        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6">
                          {(() => {
                            const totalMetas = formData.acoesServicos.reduce((sum, item) => {
                              const value = parseFloat(item.valor.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
                              return sum + value;
                            }, 0);
                            const totalDespesas = formData.naturezasDespesa.reduce((sum, item) => {
                              const value = parseFloat(item.valor.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
                              return sum + value;
                            }, 0);
                            const diferenca = totalMetas - totalDespesas;
                            const isPending = diferenca !== 0;
                            
                            return (
                              <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <p className="text-xs text-red-600 font-bold uppercase tracking-widest mb-2">Total Metas Quantitativas</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                      {new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL'
                                      }).format(totalMetas)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-red-600 font-bold uppercase tracking-widest mb-2">Total Planejado</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                      {new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL'
                                      }).format(totalDespesas)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-red-600 font-bold uppercase tracking-widest mb-2">Diferença</p>
                                    <p className={`text-2xl font-bold ${
                                      diferenca === 0 ? 'text-green-600' : 'text-orange-600'
                                    }`}>
                                      {new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL'
                                      }).format(Math.abs(diferenca))}
                                    </p>
                                  </div>
                                </div>
                                {isPending && (
                                  <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-orange-700 font-medium">
                                      {diferenca > 0 
                                        ? `Falta distribuir R$ ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(diferenca)} nas naturezas de despesa`
                                        : `Você distribuiu R$ ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Math.abs(diferenca))} a mais que o total de metas`
                                      }
                                    </p>
                                  </div>
                                )}
                                {diferenca === 0 && formData.naturezasDespesa.length > 0 && (
                                  <div className="bg-green-100 border border-green-300 rounded-lg p-3 flex items-start gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-green-700 font-medium">
                                      Distribuição de despesas balanceada! ✓
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
                        <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-400 font-medium">Nenhuma natureza de despesa adicionada</p>
                        <p className="text-xs text-gray-300">Clique no botão acima para começar</p>
                      </div>
                    )}
                  </div>
                </Section>

                {/* SECTION 7: JUSTIFICATIVA */}
                <Section
                  id="finalizacao"
                  title="Justificativa"
                  icon={<FileCheck className="w-6 h-6" />}
                  step={7}
                  totalSteps={7}
                  isComplete={sectionStatus['finalizacao']}
                  onHelpClick={() => openHelpModal('finalizacao')}
                >
                  {!sentSuccess ? (
                    <div className="space-y-6">
                      {/* ORIENTAÇÃO TÉCNICA SAES — texto simples */}
                      <div>
                        <p className="text-sm font-black text-gray-800 uppercase tracking-wide">Orientação Técnica SAES — Como a justificativa será analisada</p>
                        <p className="text-xs text-gray-500 mt-1">Sua justificativa deve contemplar os 5 elementos abaixo. Use-os como guia ao redigir o texto.</p>
                      </div>

                      {/* CAMPOS: 5 critérios SAES com mínimo de 100 caracteres cada */}
                      <div className="space-y-4">
                        {CRITERIOS_SAES.map(({ num, label, desc, badge, border }, index) => {
                          const val = justCriterios[index] || '';
                          const charCount = val.trim().length;
                          const minChars = 100;
                          const isValid = charCount >= minChars;
                          const hasContent = charCount > 0;
                          return (
                            <div key={num} className={`border-2 rounded-xl overflow-hidden ${isValid ? 'border-green-300' : hasContent ? 'border-amber-300' : 'border-red-200'}`}>
                              <div className={`flex items-start gap-3 px-4 py-3 ${isValid ? 'bg-green-50' : hasContent ? 'bg-amber-50' : 'bg-red-50'}`}>
                                <span className={`inline-flex items-center text-[10px] font-black px-2 py-0.5 rounded flex-shrink-0 mt-0.5 ${badge}`}>
                                  {num}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-black text-gray-800">{label} <span className="text-red-500">*</span></p>
                                  <p className="text-[10px] text-gray-500 mt-0.5">{desc}</p>
                                </div>
                                <div className={`flex items-center gap-1 text-[10px] font-bold flex-shrink-0 ${isValid ? 'text-green-600' : hasContent ? 'text-amber-600' : 'text-red-400'}`}>
                                  {isValid
                                    ? <><CheckCircle2 className="w-3 h-3" /> {charCount}</>
                                    : <><AlertCircle className="w-3 h-3" /> {charCount}/{minChars}</>
                                  }
                                </div>
                              </div>
                              <div className="px-4 pb-4 pt-2 bg-white">
                                <TextArea
                                  label=""
                                  value={val}
                                  onChange={(e) => updateJustCriterio(index, e.target.value)}
                                  placeholder={`Descreva ${label.toLowerCase()}...`}
                                  rows={4}
                                  required
                                />
                                {!isValid && hasContent && (
                                  <p className="mt-1 text-[10px] text-amber-600 font-semibold">
                                    Faltam {minChars - charCount} caracteres para atingir o mínimo obrigatório.
                                  </p>
                                )}
                                {!isValid && !hasContent && (
                                  <p className="mt-1 text-[10px] text-red-500 font-semibold">
                                    Campo obrigatório — mínimo {minChars} caracteres.
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {/* Resumo geral */}
                        {(() => {
                          const validCount = justCriterios.filter(f => f.trim().length >= 100).length;
                          return (
                            <div className={`flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg ${validCount === 5 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {validCount === 5
                                ? <><CheckCircle2 className="w-4 h-4" /> Todos os 5 critérios preenchidos — justificativa completa</>
                                : <><AlertCircle className="w-4 h-4" /> {validCount}/5 critérios com mínimo de 100 caracteres</>
                              }
                            </div>
                          );
                        })()}
                      </div>

                      <InputField
                        label="Responsável pela Assinatura"
                        value={formData.responsavelAssinatura}
                        onChange={(e) => updateFormData('responsavelAssinatura', e.target.value)}
                        placeholder="Nome completo da pessoa responsável..."
                        required
                      />

                      {/* INSTRUÇÕES DE ENVIO - COM BOTÃO DE AJUDA */}
                      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6 rounded-2xl border-2 border-yellow-300 shadow-md flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-black text-amber-900 flex items-center gap-3 mb-2">
                            <div className="flex items-center justify-center w-10 h-10 bg-yellow-400 rounded-full">
                              <AlertCircle className="w-6 h-6 text-amber-900" />
                            </div>
                            Próximos Passos para Envio
                          </h3>
                          <p className="text-sm text-amber-800 leading-relaxed">
                            Siga os 4 passos: 1️⃣ Gere o PDF • 2️⃣ Assine o documento • 3️⃣ Envie para SES-SP • 4️⃣ Confirme o envio
                          </p>
                        </div>
                        <button
                          onClick={() => { setHelpSectionId(null); setShowHelpModal(true); }}
                          className="ml-4 flex-shrink-0 p-3 bg-amber-400 hover:bg-amber-500 text-amber-900 font-black rounded-full transition-colors shadow-md hover:shadow-lg transform hover:scale-110 duration-200"
                          title="Ver instruções detalhadas"
                        >
                          <HelpCircle className="w-6 h-6" />
                        </button>
                      </div>

                      {/* BOTÕES DE AÇÃO - MELHORADOS */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                        {/* BOTÃO GERAR PDF */}
                        <div>
                          <div className="h-full bg-white p-7 rounded-2xl shadow-sm border border-slate-200 border-t-4 border-t-[#0B3B8C]">
                            <div className="flex items-center gap-3 mb-5">
                              <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#0B3B8C] text-white font-bold flex items-center justify-center" aria-hidden="true">1</span>
                              <div>
                                <h4 className="font-bold text-slate-900 leading-tight">Gerar PDF</h4>
                                <p className="text-xs text-slate-500 mt-0.5">Salva o plano e abre o documento</p>
                              </div>
                              <Download className="w-6 h-6 text-[#0B3B8C] ml-auto" aria-hidden="true" />
                            </div>
                            <Button
                              label={isSending ? "Gerando..." : "Visualizar e baixar PDF"}
                              onClick={() => {
                                if (isSending) return;
                                
                                const validation = validateRequiredFields();
                                if (!validation.isValid) {
                                  const missingList = validation.missingFields.map((field, idx) => `${idx + 1}. ${field}`).join('\n');
                                  avisar(
                                    `⚠️ FORMULÁRIO INCOMPLETO!\n\n` +
                                    `Os campos obrigatórios abaixo devem ser preenchidos:\n\n${missingList}\n\n` +
                                    `Por favor, complete todos os campos indicados antes de gerar o PDF.`
                                  );
                                  return;
                                }
                                
                                setShowDocument(true);
                                setTimeout(() => handleGeneratePDF(), 500);
                              }}
                              variant="primary"
                              disabled={isSending}
                              fullWidth
                            />
                            <ul className="text-sm text-slate-600 space-y-1 mt-4">
                              <li className="flex gap-2">
                                <span>•</span>
                                <span>Salva automaticamente seu plano</span>
                              </li>
                              <li className="flex gap-2">
                                <span>•</span>
                                <span>Gera em PDF pronto para imprimir</span>
                              </li>
                              <li className="flex gap-2">
                                <span>•</span>
                                <span>Abre visualização antes de baixar</span>
                              </li>
                            </ul>
                          </div>
                        </div>

                        {/* BOTÃO ENVIAR ASSINADO */}
                        <div>
                          <div className="h-full bg-white p-7 rounded-2xl shadow-sm border border-slate-200 border-t-4 border-t-[#D92B2B]">
                            <div className="flex items-center gap-3 mb-5">
                              <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#D92B2B] text-white font-bold flex items-center justify-center" aria-hidden="true">2</span>
                              <div>
                                <h4 className="font-bold text-slate-900 leading-tight">Enviar para SES-SP</h4>
                                <p className="text-xs text-slate-500 mt-0.5">Depois de assinar o PDF</p>
                              </div>
                              <Send className="w-6 h-6 text-[#D92B2B] ml-auto" aria-hidden="true" />
                            </div>
                            <Button
                              label="Enviar assinado"
                              onClick={handleSendToSES}
                              variant="primary"
                              fullWidth
                            />
                            <ul className="text-sm text-slate-600 space-y-1 mt-4">
                              <li className="flex gap-2">
                                <span>•</span>
                                <span>Abre seu cliente de email</span>
                              </li>
                              <li className="flex gap-2">
                                <span>•</span>
                                <span>Mensagem pré-preenchida</span>
                              </li>
                              <li className="flex gap-2">
                                <span>•</span>
                                <span>Anexe o PDF assinado</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* AUTO SAVE STATUS */}
                      <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                        <div className="flex items-center justify-center gap-2 text-sm">
                          {autoSaveStatus === 'saving' && (
                            <>
                              <Loader2 className="w-4 h-4 text-green-600 animate-spin" />
                              <span className="text-green-700 font-semibold">Salvando automaticamente...</span>
                            </>
                          )}
                          {autoSaveStatus === 'saved' && (
                            <>
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                              <span className="text-green-700 font-semibold">✓ Salvo com sucesso</span>
                              {lastAutoSaveTime && (
                                <span className="text-xs text-green-600">às {lastAutoSaveTime.toLocaleTimeString('pt-BR')}</span>
                              )}
                            </>
                          )}
                          {autoSaveStatus === 'idle' && lastAutoSaveTime && (
                            <>
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                              <span className="text-green-700 font-semibold">Seu plano é salvo automaticamente</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Texto final de orientação */}
                      <p className="text-center text-sm text-gray-500 font-medium pt-2">
                        Revise, gere PDF, assine e envie para a SES-SP
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-green-50 rounded-lg border-2 border-green-200 space-y-4">
                      <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto">
                        <MailCheck className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-green-900">Plano salvo com sucesso</h3>
                      <p className="text-sm text-green-700">Seu plano foi registrado no banco de dados da SES-SP.</p>
                      <div className="bg-white p-4 rounded-lg border border-green-300 space-y-2">
                        <p className="text-xs text-gray-600"><strong>Próximo Passo:</strong></p>
                        <p className="text-sm font-semibold text-gray-900">Envie o PDF assinado para:</p>
                        <p className="font-mono text-red-600 font-bold">gcf-emendasfederais@saude.sp.gov.br</p>
                      </div>
                      <Button
                        label="Abrir e-mail"
                        onClick={handleSendToSES}
                        variant="primary"
                      />
                      <Button
                        label="Novo Cadastro"
                        onClick={() => window.location.reload()}
                        variant="secondary"
                      />
                    </div>
                  )}
                </Section>

                {/* STICKY BACK TO TOP BUTTON */}
                {activeSection !== 'info-emenda' && (
                  <button
                    onClick={() => scrollToSection('info-emenda')}
                    className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 w-12 h-12 flex items-center justify-center bg-[#08162D] text-white rounded-full shadow-lg hover:bg-[#13284D] transition-colors print:hidden"
                    title="Voltar ao topo"
                    aria-label="Voltar ao topo do formulário"
                  >
                    <ArrowUp className="w-6 h-6" />
                  </button>
                )}
              </div>
            </div>
            </>
          )}
          </>
        )}
        </div>
      </main>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
};

export default App;
